import React, { useEffect, useRef, useImperativeHandle, forwardRef, useMemo } from 'react';
import Util from './Util.js';
import { isIntersecting, canClose, isClosed, windingDirection, drawText, resize, resizeC, drawImage, drawResize, rotatePath,rPathToSvg, rPathToSvgMap, svgTextToSvgPath, textToSvgPath,drawTextPathOnCtx, scaleSvgPathString, sanitizePath, getBoundingBoxFromPoints} from './Funcs.js';
import {drawPLine, drawControls, pageToScreen, pageToScreenN, screenToPage, drawImageClip, getPathBoundingBox, calculateCurveBoundingBox} from './Funcs.js';
import {bezierPHit, bezierCHit, applySegmentOptions, applySegmentOptionsB, rotateCurvePath, rPathToCurveSvg, rPathToCurveSvgMap, svgPathBoundingBox, svgPathBoundingBoxMap} from './Funcs.js';
import {pHit, jHit, drawFreehand,drawSelectedSegments,drawSelectedSegmentsB, getSegmentAtPoint, breakSegment, smoothBezierControls, straight, getSegmentsRect, isSameLine} from './Funcs.js';
import {exploadFunc, removeStray, hexToRgb, drawMirrorLine,mirror,rotateFunc, resizeFunc, drawFaintBezier, drawFaintPLine, addFilteredPoint} from './Funcs.js';
import {drawSvgCircle, drawSvgSquare, drawSvgRect, drawSvgEllipse, getUnique, drawRotatedRect, drawGroup,convertToBezier, aiToBezier, convertToImage, getCachedImage, invalidateImageCache, getImage} from './Funcs.js';
import {multiplyMatrices,createTranslationMatrix,createRotationMatrix, applyMatrixToCtx, createScreenTransformationMatrix, transformPoint, drawPath, drawCurvePath, resizeAnimate} from './Funcs.js';
import {insertMPath, insertMObj, insertMObjText, translateMObj, offsetPath, offsetView, doResize, offsetText, flipVertical, flipHorizontal, applyEffects, translateBezier, translatePRect, getOpacity} from './Funcs.js';
import {drawClipBoard, addToClipBoard,clipBoardHit, clearClipBoard, getClipIndex, createLoop, getClipRect, clipBoardToPage, restoreClipBoard, saveClipBoard} from './ClipBoard.js';
import {straighten, svgFileToBezier, loadNewSvg, loadLocalSvg, drawPlaceholder, isCompletelyOccluded} from './Funcs.js';
import { faL } from '@fortawesome/free-solid-svg-icons';

let gridCacheCanvas = null;
let gridCacheCtx = null;

const Canvas = forwardRef(({auth, 
                            operation, 
                            segmentAction,
                            onCoordUpdate, 
                            onRectSelect, 
                            onAnimateSelect,
                            setOperation, 
                            segmentOpRef,
                            pASegmentRef,
                            animateOpRef,
                            setCanUndo, 
                            setCanRedo, 
                            setCanSaveFunc,
                            setRefreshKey, 
                            filename, 
                            pageWidth, 
                            pageHeight,
                            gridWidth,
                            gridHeight,
                            stroke,
                            strokeColor,
                            fillColor,
                            allowStroke,
                            allowFill,                            
                            fillMode,
                            gradientColor1,
                            gradientColor2,
                            gradientAngle,                      
                            alpha,
                            snapToGrid,
                            showControls,
                            showEndPoints,
                            clipBoard,
                            pageNum,
                            demo,                  
                            pagesRef}, ref) => {

  const canvasRef = useRef();
  const rectsRef = useRef([]);
  const hitRef = useRef([]);  // Currently not used. This is a flattened structure for hit testing
  const parentsRef = useRef(null); // Currently not used. This allows a leaf to reference a parent if needed.
  const renderRef = useRef([]); // flattened rectsRef structure to maintain depth within groups
  const resizeViewRef = useRef(false);
  
  
  const pageInfoRef = useRef(null);
  //   const pagesRef = useRef([]);
  const pointsRef = useRef([]);
  const controlRef = useRef([]);
  const segmentsRef = useRef([]);
  const tabSelectRef = useRef(0);
  const rectTabSelectRef = useRef(0);
  const aSegmentRef = useRef(null);

  const keepPointsRef = useRef(false);

  const undoLineRef = useRef([]);
  const undoControlRef = useRef([]);
  const undoSegmentsRef = useRef([]);

  const historyRef = useRef(new Map());
  const redoRef = useRef(new Map());
  const selectedRef = useRef([]);
  const draggingRef = useRef(null);
  const pointDragRef = useRef(null);
  const pointDownRef = useRef(null);
  const controlDragRef = useRef(null);
  const tempRectRef = useRef(null);

  const offsetRef = useRef({ x: 0, y: 0 });
  const dragStartRef = useRef({ x: 0, y: 0 });
  const mouseDownPageRef = useRef({ x: 0, y: 0 });
  const mouseDownScreenRef = useRef({ x: 0, y: 0 });
  const pageSizeRef = useRef({ width: pageWidth, height: pageHeight });
  const onClipRef = useRef(false);
  const clipIndexRef = useRef(-1);
  const plineRef = useRef({width:gridWidth,height:gridHeight, stroke:stroke, snapToGrid:snapToGrid, showControls:showControls, showEndPoints:showEndPoints});
  const clipBoardRef = useRef(clipBoard);
  const filenameRef = useRef(filename);
  const lastMoveTimeRef = useRef(0);
  const isDrawingRef = useRef(false);
  const mirrorPoint1Ref=useRef (null);
  const mirrorPoint2Ref=useRef (null);
  const rotatePointRef=useRef(null);

  const mirrorLineRef = useRef([]);
  const mirrorControlRef = useRef([]);
  const rotateLineRef = useRef([]);
  const rotateControlRef = useRef([]);

  const operationRef = useRef(operation);
  // const segmentOpRef = useRef("none");
  const segmentActionRef = useRef(segmentAction);
  
  const lineBeginRef = useRef(true);

  const fontTypeRef = useRef("Roboto-Regular.ttf");
  const fontTextRef = useRef("Thanks");

  const viewPortRef = useRef({
    offsetX: 50,
    offsetY: 50,
    zoom: 0.90,
    originalWidth: 30,
    originalHeight: 30,
    originalRect: null,
    originalPath: null,
    originalControl: null,
    originalSvgPath: null,
    originalGroup: null,
    rZoomX: 1,
    rZoomY: 1,
    isResizing: false
  });


  const historyStack = useMemo(() => ({
    stacks: historyRef, // key: page index, value: array of states
    maxSize: 100,
  
    ensureStack(page) {
      const map = this.stacks.current;
      if (!map.has(page)) {
        map.set(page, []);
      }
      return map.get(page);
    },
  
    push(page, state) {
      const stack = this.ensureStack(page);
      stack.push(JSON.parse(JSON.stringify(state)));
  
      if (stack.length > this.maxSize) {
        stack.shift();
      }
  
      setCanUndo(true); // optional: update per-page if needed
      setCanSaveFunc(true);
    },
  
    pop(page) {
      const map = this.stacks.current;
      const stack = map.get(page);
      if (!stack || stack.length === 0) return null;
      return stack.pop();
    },
  
    getLength(page) {
      const map = this.stacks.current;
      const stack = map.get(page);
      return stack ? stack.length : 0;
    },
  
    clear(page) {
      const map = this.stacks.current;
      map.set(page, []);
      setCanUndo(false); // optional: update per-page if needed
    },
  
    clearAll() {
      const map = this.stacks.current;
      setCanSaveFunc(false);
      map.clear();
    },

    isEmpty()
    {
      const isEmpty = historyRef.current.size === 0;
      return isEmpty;
    },

    isPageEmpty(page)
    {
      const map = this.stacks.current;
      const stack = map.get(page);
      if (!stack || stack.length === 0) 
      {
        return true;
      }
      return false;
    }  
  }), []);
  

  const undoSegmentsStack = useMemo(() => ({
    stack: undoSegmentsRef,
    maxSize: 100,

    push(state) {
      const cloned = JSON.parse(JSON.stringify(state));
      this.stack.current.push(cloned);

      if (this.stack.current.length > this.maxSize) {
        this.stack.current.shift();
      }
    },

    pop() {
      const popped = this.stack.current.pop();
      return popped;
    },

    getLength() {
      return this.stack.current.length;
    },

    clear() {
      this.stack.current = [];
    }
  }), []); // 👈 Safe dependency


  const undoPlineStack = useMemo(() => ({
    stack: undoLineRef,
    maxSize: 100,

    push(state) {
      const cloned = JSON.parse(JSON.stringify(state));
      this.stack.current.push(cloned);

      if (this.stack.current.length > this.maxSize) {
        this.stack.current.shift();
      }
    },

    pop() {
      const popped = this.stack.current.pop();
      return popped;
    },

    getLength() {
      return this.stack.current.length;
    },

    clear() {
      this.stack.current = [];
    }
  }), []); // 👈 Safe dependency


  const undoControlStack = useMemo(() => ({
    stack: undoControlRef,
    maxSize: 100,

    push(state) {
      const cloned = JSON.parse(JSON.stringify(state));
      this.stack.current.push(cloned);

      if (this.stack.current.length > this.maxSize) {
        this.stack.current.shift();
      }
    },

    pop() {
      const popped = this.stack.current.pop();
      return popped;
    },

    getLength() {
      return this.stack.current.length;
    },

    clear() {
      this.stack.current = [];
    }
  }), []); // 👈 Safe dependency


  const redoStack = useMemo(() => ({
    stacks: redoRef, // key: page index, value: array of states
    maxSize: 100,
  
    ensureStack(page) {
      const map = this.stacks.current;
      if (!map.has(page)) {
        map.set(page, []);
      }
      return map.get(page);
    },
  
    push(page, state) {      
      const stack = this.ensureStack(page);
      stack.push(JSON.parse(JSON.stringify(state)));
  
      if (stack.length > this.maxSize) {
        stack.shift();
      }
  
      setCanRedo(true); // optional: update per-page if needed
    },
  
    pop(page) {
      const map = this.stacks.current;
      const stack = map.get(page);
      if (!stack || stack.length === 0) return null;
      return stack.pop();
    },
  
    getLength(page) {
      const map = this.stacks.current;
      const stack = map.get(page);
      return stack ? stack.length : 0;
    },
  
    clear(page) {
      const map = this.stacks.current;
      map.set(page, []);
      setCanRedo(false); // optional: update per-page if needed
    },
  
    clearAll() {
      const map = this.stacks.current;
      map.clear();
    },

    isEmpty()
    {
      const isEmpty = historyRef.current.size === 0;
      return isEmpty;
    },

    isPageEmpty(page)
    {
      const map = this.stacks.current;
      const stack = map.get(page);
      if (!stack || stack.length === 0) 
      {
        return true;
      }
      return false;
    }

  }), []);
  
/*
  const redoStack = useMemo(() => ({
    stack: redoRef,
    maxSize: 100,

    push(state) {    
      this.stack.current.push(JSON.parse(JSON.stringify(state)));

      if(this.stack.current.length > this.maxSize)
      {
        this.stack.current.shift();
      }
      setCanRedo(true);
    },
    pop(){
      return this.stack.current.pop();
    },

    getLength() {
      return this.stack.current.length;
    },

    clear()
    {
      this.stack.current = [];
      setCanRedo(false);
    }

  }),[]);
*/
  

  useImperativeHandle(ref, () => ({
    getRectangles: () => rectsRef.current,
    setRectangles: (data) => {
      // historyStack.push(pageNum, rectsRef.current);
      // historyRef.current.push(JSON.parse(JSON.stringify(rectsRef.current)));
      rectsRef.current = data;
      selectedRef.current = [];
      draw();
    },
    setPageInfo: (data) => {
      pageInfoRef.current = data;
      draw();
    },
/*    
    getPages: () => pagesRef.current,
    setPages: (data)=> {
      pagesRef.current = data;
      selectedRef.current = [];
      draw();
    },
*/
    redraw: draw,
    deleteSelectedRect: () => {
      if (selectedRef.current) {
        historyStack.push(pageNum, rectsRef.current);
        // historyRef.current.push(JSON.parse(JSON.stringify(rectsRef.current)))
        rectsRef.current = rectsRef.current.filter(r => !selectedRef.current.includes(r));

        const currentPageLayout = pagesRef.current.pages[pageNum - 1].layout;
        pagesRef.current.pages[pageNum - 1].layout = currentPageLayout.filter(r => !selectedRef.current.includes(r));
 
        // rectsRef.current = rectsRef.current.filter(r => r !== selectedRef.current);

        if(selectedRef.current.length===1 && selectedRef.current[0].type === 'Menubar')
        {
          console.log("Deleting Rect");
          pagesRef.current.navigation.method="scroll";
          pagesRef.current.navigation.menubar.visible = false;
          pagesRef.current.navigation.menubar.rectId = "";
        }

        selectedRef.current = [];

        draw();
      }
    },
    duplicatePage: (from) =>
    {

      // make copied rect the selected ref also
      selectedRef.current = [];


      historyStack.push(pageNum, rectsRef.current);
      from.forEach (r => {

        let n = JSON.parse(JSON.stringify(r));
        rectsRef.current.push(n);
        selectedRef.current.push(n);
      });
    },
    duplicateRect: () =>
    {

      if(selectedRef.current)
      {
        historyStack.push(pageNum, rectsRef.current);
        // historyRef.current.push(JSON.parse(JSON.stringify(rectsRef.current)))
                
        if(selectedRef.current.length >= 1)
        {
          selectedRef.current.forEach(r => {

            let offsetX = 10;
            let offsetY = 10;

            let rect = JSON.parse(JSON.stringify(r));

            if(rect.locked)
            {
              rect.locked= false;
            }

            if(rect.type === "Group")
            {
              rect.x += offsetX;
              rect.y += offsetY;        
              // moveGroup(rect,offsetX,offsetY);
            }
            else
            {
              rect.x += offsetX;
              rect.y += offsetY;
            }

            if(r.subType === 'Animate')
            {
              if(r.mPath)
              {
                rect.mPath.x = rect.x - rect.offsetViewX;
                rect.mPath.y = rect.y - rect.offsetViewY;
 
              }
              if(r.mObj)
              {
                  rect.mObj.x = rect.x - rect.offsetViewX; // += offsetX;
                  rect.mObj.y = rect.y - rect.offsetViewY; //+= offsetY;
              }
            }


            rectsRef.current.push(rect);
            // make copied rect the selected ref also
            selectedRef.current = [];
            selectedRef.current.push(rect);

          });


          
          pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;

        }

        draw();
      }

    },
    alignTop: () => {
      historyStack.push(pageNum, rectsRef.current);
      alignTop();
      draw();
    },
    alignLeft: () => {
      historyStack.push(pageNum, rectsRef.current);
      alignLeft();
      draw();
    },
    alignRight: () => {
      historyStack.push(pageNum, rectsRef.current);
      alignRight();
      draw();
    },
    alignBottom: () => {
      historyStack.push(pageNum, rectsRef.current);
      alignBottom();
      draw();
    },
    groupTogether: () => {
      historyStack.push(pageNum, rectsRef.current);
      groupTogether();
      draw();
    },
    ungroup: () => {
      historyStack.push(pageNum, rectsRef.current);
      ungroup();
      draw();
    },    
    convertToImage: (rects) => {
      if(rects.length === 1)
      {
        historyStack.push(pageNum, rectsRef.current);
        let webId = auth?.user?.webId;
        invalidateImageCache(rects[0].imageName,webId, demo);
        convertToImage(rectsRef.current, rects[0], parentsRef.current);
        pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
        draw();
      }

    },
    
    addNewAiCode: (rects, msg, w,h) =>{

      if(rects.length === 1)
      {
        historyStack.push(pageNum, rectsRef.current);

        let newRect = JSON.parse(JSON.stringify(rects[0]));

        newRect.type = "Rectangle";
        newRect.subType = 'Custom';
        newRect.imageName = "";
        newRect.aiCode = msg;
        newRect.w = w;
        newRect.h = h;
       
        rectsRef.current.push(newRect);
        pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
        draw();
      }


    },

    addNewImage:(rects, imageName) =>
    {

      if(rects.length === 1)
      {
        historyStack.push(pageNum, rectsRef.current);

        let newRect = JSON.parse(JSON.stringify(rects[0]));

        newRect.type = "Rectangle";
        newRect.subType = 'Image';
        newRect.imageName = imageName;
       
        rectsRef.current.push(newRect);
        pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
        draw();
      }

    },
    addNewBezier: (rects, width, height, msg) =>
    {
      if(rects.length === 1)
      {
        historyStack.push(pageNum, rectsRef.current);

        let newRect = JSON.parse(JSON.stringify(rects[0]));
        newRect.w = width;
        newRect.h = height;          
        newRect.aiSvg = msg;

        let converted = aiToBezier(newRect);

        rectsRef.current.push(converted);
        pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
        draw();


      }
    },    
    convertToBezier: (rects) => {

      if(rects.length === 1)
      {
        historyStack.push(pageNum, rectsRef.current);
        let webId = auth?.user?.webId;

        convertToBezier(rectsRef.current, rects[0], parentsRef.current, webId, draw);
        pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
        draw();
      }
    },
    expandLeft: () => {
      historyStack.push(pageNum, rectsRef.current);
      expandLeft();
      draw();
    },
    expandRight: () => {
      historyStack.push(pageNum, rectsRef.current);
      expandRight();
      draw();
    },
    expandUp: () => {
      historyStack.push(pageNum, rectsRef.current);
      expandUp();
      draw();
    },
    expandDown: () => {
      historyStack.push(pageNum, rectsRef.current);
      expandDown();
      draw();
    },
    copyTo: () => {
      addToClipBoard(selectedRef.current);
      saveClipBoard();
      draw();
    },
    rotate: (angle, rect) => {
      historyStack.push(pageNum, rectsRef.current);      
      rect.rotation = angle;      
      pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
      draw();
      
    },
    lock: (rects) => {
      if(rects.length === 1)
      {
        if(rects[0].locked === true)
        {
          rects[0].locked = false;
        }
        else
        {
          rects[0].locked = true;
        }
      }
      draw();
    },
    lockAll: (rects) => {
        rects.forEach(r => r.locked = true);        
        draw();
    },
    unLockAll: (rects) => {
      rects.forEach(r => r.locked = false);        
      draw();
    },

    flipV: (rect) => {
      historyStack.push(pageNum, rectsRef.current);      
      

      let {rPath, rControls, flippedRotation} = flipVertical(rect.w,rect.h,rect.rotation, rect.path, rect.control);
      rect.path = rPath;
      rect.control = rControls;
      // rect.rotation = flippedRotation;      

      pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
      draw();
    },
    flipH: (rect) => {
      historyStack.push(pageNum, rectsRef.current);      

      let {rPath, rControls, flippedRotation} = flipHorizontal(rect.w,rect.h,rect.rotation, rect.path, rect.control);
      rect.path = rPath;
      rect.control = rControls;
      // rect.rotation = flippedRotation;      

      pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
      console.log("Flipping Horizonatally");
      draw();

    },
    updateBezier: async (rect, glyph, inTextForm=false) =>{
        historyStack.push(pageNum, rectsRef.current); 

        if(inTextForm)
        {

          await loadLocalSvg(rect, glyph);
          pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;

        }
        else
        {
          let svgUrl = `https://myaiweb.ca/openmoji/color/svg/${glyph}`;

          await loadNewSvg(rect,svgUrl);

          pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
        } 
        draw();
      
    },

    clearClip: () => {
      clearClipBoard(selectedRef.current);
      saveClipBoard();
      draw();
    },
    paste: () => {
      onClipRef.current=false;
      historyStack.push(pageNum, rectsRef.current);
      setOperation("paste");
    },

    pushToFront: () => {
      pushRectToFront();
    },
    pushToBack: () => {        
        historyStack.push(pageNum, rectsRef.current);
        const rects = rectsRef.current;
        const selected = selectedRef.current;

        if (selected.length==0) return;

        // Find the index using reference comparison
        const index = rects.findIndex(r => r === selected[0]);

        if (index === -1) return;

        // Remove and re-insert at index 0
        const [removed] = rects.splice(index, 1);
        rects.unshift(removed);
        labelZ();
        draw();
        setRefreshKey();
        selectedRef.current[0] = removed;

        onRectSelect(removed, null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);

    },
    clear: () => {
      historyStack.push(pageNum, rectsRef.current);
      rectsRef.current = [];
      if(pageNum > 0)
      {
        pagesRef.current.pages[pageNum - 1].layout=[];
      }
      draw();
    },
    clearHistory: () => {
      historyStack.clear(pageNum);
      redoStack.clear(pageNum);
    },
    undo: () => {      
      undo();
    },
    redo: () => {
      redo();
    },
    setCanUndo: (u) => {
      setCanUndo(u);
    },
    /*
    setCanRedo: (u) => {
      setCanRedo(u);
    },
    setCanSave: (u) =>
    {
      setCanSave(u);
    },
    */
    canUndo: (p) => {
      return !historyStack.isPageEmpty(p);
    },
    canRedo: (p) => {
      return !redoStack.isPageEmpty(p);
    },
    canSave: () => {
      return !historyStack.isEmpty();
    },
    savePageHistory: (p) =>
    {
      historyStack.push(p, rectsRef.current);      
    },

    setOperation: (op) => {
      setOperation(op); // <- safely updates parent's operation
    },
    setSegmentAction: (action) => {      
      segmentActionRef.current = action;

      if(segmentActionRef.current === 'deleteSegment')
      {
        if(aSegmentRef.current!= null)
        {
          console.log("Breaking Segment");
          undoPlineStack.push(pointsRef.current);    

          if(operationRef.current !== "bezier")
          {
            const {newLines} = breakSegment(pointsRef.current, aSegmentRef.current, false, null);
            
            pointsRef.current = newLines;
          }
          else
          {          
            undoControlStack.push(controlRef.current);
            const {newLines, newControls} = breakSegment(pointsRef.current, aSegmentRef.current, true, controlRef.current);
            pointsRef.current = newLines;
            controlRef.current = newControls; 
          }

          let li=aSegmentRef.current.lineIndex;
          pointsRef.current.splice(li,1);
          if(operationRef.current === 'bezier')
          {
            controlRef.current.splice(li,1);
          }
  
          segmentsRef.current=[];
        
          draw();
          return;
        }
      }

      if(segmentActionRef.current === 'deleteSelected')
      {

        console.log("Delete selected");
        const lines = pointsRef?.current;
        const selectedLineIndexes = new Set();

        if(segmentsRef.current.length > 0)
        {
          undoPlineStack.push(pointsRef.current);    
    
          if(operationRef.current === "bezier")
          {
            undoControlStack.push(controlRef.current);            
          }

        }

        for (const segment of segmentsRef.current)
        {
            if (segment.lineIndex !== undefined)
            {
                selectedLineIndexes.add(segment.lineIndex);
            }
        }

        pointsRef.current = pointsRef.current.filter(
          (_, index) => !selectedLineIndexes.has(index)
        );
      
        if(operationRef.current ===  "bezier")
        {
          controlRef.current = controlRef.current.filter(
            (_, index) => !selectedLineIndexes.has(index)
          );
        }

        tabSelectRef.current=0;
        segmentsRef.current = [];
        segmentOpRef.current = 'none';
        lineBeginRef.current = true; 
        draw();
      }

      if(segmentActionRef.current === 'breakSegment')
      {
          if(aSegmentRef.current!= null)
          {
            console.log("Breaking Segment");
            undoPlineStack.push(pointsRef.current);    

            if(operationRef.current !== "bezier")
            {
              const {newLines} = breakSegment(pointsRef.current, aSegmentRef.current, false, null);
              
              pointsRef.current = newLines;
            }
            else
            {          
              undoControlStack.push(controlRef.current);
              const {newLines, newControls} = breakSegment(pointsRef.current, aSegmentRef.current, true, controlRef.current);
              pointsRef.current = newLines;
              controlRef.current = newControls; 
            }

            segmentsRef.current=[];
            segmentsRef.current.push(aSegmentRef.current);
            
          
            draw();
            return;
          }
      }

      if(segmentActionRef.current === 'smoothBezier')
      {
           
            if(operationRef.current !== "bezier")
            {
              // don't do anything for a pline
            }
            else
            {        
              undoPlineStack.push(pointsRef.current);    
                          // Current selected line index
              let li = segmentsRef.current[0].lineIndex;

              undoControlStack.push(controlRef.current);              
              const {newControl} =  smoothBezierControls(pointsRef.current[li], controlRef.current[li]);              
              controlRef.current[li]= newControl; 
            }

            segmentsRef.current=[];
            // segmentsRef.current.push(aSegmentRef.current);
            draw();
            return;

      }


      if(segmentActionRef.current === 'lineToBack')
      {
        let numLines = pointsRef.current.length;

        if(numLines > 1)
        {
            // Save undo state
            undoPlineStack.push(JSON.parse(JSON.stringify(pointsRef.current)));

            if(operationRef.current === "bezier")
            {
                undoControlStack.push(JSON.parse(JSON.stringify(controlRef.current)));
            }

            // Current selected line index
            let li = segmentsRef.current[0].lineIndex;

            // Remove selected line
            let movedLine = pointsRef.current.splice(li, 1)[0];

            // Move to front (beginning of array)
            pointsRef.current.unshift(movedLine);

            // Do same for bezier controls
            if(operationRef.current === "bezier")
            {
                let movedControl = controlRef.current.splice(li, 1)[0];
                controlRef.current.unshift(movedControl);
            }

            // Update selection to new location
            tabSelectRef.current = 0;
            segmentsRef.current = selectedSegmentLine(tabSelectRef.current);
        }

        draw();
        console.log("Line moved to back");
      } 

      if(segmentActionRef.current === 'straight')
      {
        segmentActionRef.current = action;

        if(aSegmentRef.current!= null)
        {
          console.log("Straigtehning Segment");
         
  
          if(operationRef.current === "bezier")
          {
            undoPlineStack.push(pointsRef.current);    
            // Current selected line index
            let li = segmentsRef.current[0].lineIndex;

            undoControlStack.push(controlRef.current);              
            const {newControl} =  straighten(pointsRef.current[li], controlRef.current[li],aSegmentRef.current,segmentsRef.current);              
            controlRef.current[li]= newControl; 
          }
  
          // segmentsRef.current=[];
          // segmentsRef.current.push(aSegmentRef.current);
              
          draw();
          return;
        }
      }
      if(segmentActionRef.current === 'lineToFront')
      {
          let numLines = pointsRef.current.length;

          if(numLines > 1)
          {
              // Save undo state
              undoPlineStack.push(JSON.parse(JSON.stringify(pointsRef.current)));

              if(operationRef.current === "bezier")
              {
                  undoControlStack.push(JSON.parse(JSON.stringify(controlRef.current)));
              }

              // Current selected line index
              let li = segmentsRef.current[0].lineIndex;

              // Remove selected line
              let movedLine = pointsRef.current.splice(li, 1)[0];

              // Move to end of array
              pointsRef.current.push(movedLine);

              // Do same for bezier controls
              if(operationRef.current === "bezier")
              {
                  let movedControl = controlRef.current.splice(li, 1)[0];
                  controlRef.current.push(movedControl);
              }

              // Update selection to new location (last line)
              tabSelectRef.current = pointsRef.current.length - 1;
              segmentsRef.current = selectedSegmentLine(tabSelectRef.current);
          }

          draw();
          console.log("Line moved to front");
      }

      if(segmentActionRef.current === 'deleteLine')
      {
        let numLines = pointsRef.current.length;
        if(numLines > 0)
        {
          undoPlineStack.push(pointsRef.current);    

          if(operationRef.current === "bezier")
          {

            undoControlStack.push(controlRef.current);
            
          }
            
          let li=segmentsRef.current[0].lineIndex;
          pointsRef.current.splice(li,1);
          controlRef.current.splice(li,1);
          numLines--;

          if(numLines > 0)
          {

            tabSelectRef.current=0;
            segmentsRef.current = selectedSegmentLine(tabSelectRef.current);            
          }            
          else
          {
            tabSelectRef.current=0;
            segmentsRef.current = [];
            segmentOpRef.current = 'none';
            lineBeginRef.current = true; 
          }            
        }

        draw();
        console.log("Delete line");
      }

      if(segmentActionRef.current === 'copySelected')
      {

        console.log("Copy selected");
        const lines = pointsRef?.current;
        const selectedLineIndexes = new Set();

        if(segmentsRef.current.length > 0)
        {
          undoPlineStack.push(pointsRef.current);    
    
          if(operationRef.current === "bezier")
          {
            undoControlStack.push(controlRef.current);            
          }

        }

        for (const segment of segmentsRef.current)
        {
            if (segment.lineIndex !== undefined)
            {
                selectedLineIndexes.add(segment.lineIndex);
            }
        }        
        for (const li of selectedLineIndexes)
        {
          const {points:p, controls:c}=copyLine(li, gridWidth);

          let lo = pointsRef.current[li];
          let loCopy = Object.assign({}, lo);
          loCopy.points = p;  
          pointsRef.current.push(loCopy);

          if(operationRef.current === "bezier")
          {
            let co = controlRef.current[li];
            let coCopy = Object.assign({}, co);              
            coCopy.controls = c;
            controlRef.current.push(coCopy);
          }
        
        }

        draw();
      }



      if(segmentActionRef.current === 'copyLine')
      {
        undoPlineStack.push(pointsRef.current);    

        if(operationRef.current === "bezier")
        {
          undoControlStack.push(controlRef.current);            
        }
            
        let li=segmentsRef.current[0].lineIndex;
        const {points:p, controls:c}=copyLine(li, gridWidth);

        let lo = pointsRef.current[li];
        let loCopy = Object.assign({}, lo);
        loCopy.points = p;
        pointsRef.current.push(loCopy);

        if(operationRef.current === "bezier")
        {
          let co = controlRef.current[li];
          let coCopy = Object.assign({}, co);              
          coCopy.controls = c;
          controlRef.current.push(coCopy);
        }
        

        draw();
        console.log("copy Line");        
      }

      if(segmentActionRef.current === 'joinLine')
      {

        undoPlineStack.push(pointsRef.current);    

        if(operationRef.current === "bezier")
        {
          undoControlStack.push(controlRef.current);            
        }
            
        let li=segmentsRef.current[0].lineIndex;

        const hitInfo = jHit(li, pointsRef.current);

        console.log(JSON.stringify(hitInfo));

        
        const {lines:l, controls:c}=joinLine(li, hitInfo);

        pointsRef.current = l;
        if(operationRef.current === "bezier")
        {
          controlRef.current = c;
        }

        segmentsRef.current = [];
        segmentOpRef.current = 'none';

        draw();
        console.log("join line");
      }
      if(segmentActionRef.current==="mirrorLine")
      {
        if(mirrorPoint1Ref.current === null)
        {
          // let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%207.168656%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%2015.439830%207.683889%20L%2015.439830%2015.413969%20L%207.168656%2015.413969%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2023.627456%208.074297%20L%2023.711003%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
          let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2010.000000%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2018.271174%2010.000000%20L%2018.271174%2017.730080%20L%2010.000000%2017.730080%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2026.458800%2010.390408%20L%2026.542347%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
          canvasRef.current.style.cursor=`url('${dataUrl}') 16 16, auto`;  
        }

        undoPlineStack.push(pointsRef.current);    

        if(operationRef.current === "bezier")
        {
          undoControlStack.push(controlRef.current);            
        }            
      }
      if(segmentActionRef.current==="rotateLine")
      {

        if(mirrorPoint1Ref.current === null)
        {
          // let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%207.168656%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%2015.439830%207.683889%20L%2015.439830%2015.413969%20L%207.168656%2015.413969%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2023.627456%208.074297%20L%2023.711003%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
          let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2010.000000%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2018.271174%2010.000000%20L%2018.271174%2017.730080%20L%2010.000000%2017.730080%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2026.458800%2010.390408%20L%2026.542347%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
          canvasRef.current.style.cursor=`url('${dataUrl}') 16 16, auto`;  
        }

        undoPlineStack.push(pointsRef.current);    

        if(operationRef.current === "bezier")
        {
          undoControlStack.push(controlRef.current);            
        }
      }
      if(segmentActionRef.current==="resizeLine")
      {

        if(mirrorPoint1Ref.current === null)
        {
          // let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%207.168656%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%2015.439830%207.683889%20L%2015.439830%2015.413969%20L%207.168656%2015.413969%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2023.627456%208.074297%20L%2023.711003%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
          let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2010.000000%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2018.271174%2010.000000%20L%2018.271174%2017.730080%20L%2010.000000%2017.730080%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2026.458800%2010.390408%20L%2026.542347%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
          canvasRef.current.style.cursor=`url('${dataUrl}') 16 16, auto`;  
        }

        undoPlineStack.push(pointsRef.current);    

        if(operationRef.current === "bezier")
        {
          undoControlStack.push(controlRef.current);            
        }
      }

    },
    updateLineSettings: (settings) =>
    {

      
      if(segmentOpRef.current === "lineop")
      {

        for(let l=0; l<segmentsRef.current.length; l++)
        {
          let line = segmentsRef.current[l];
          let currentLine = pointsRef.current[line.lineIndex];

          // console.log(JSON.stringify(settings));

          currentLine.strokeColor = settings.strokeColor;
          currentLine.stroke = parseInt(settings.strokeWidth);
          currentLine.strokeWidth = parseInt(settings.strokeWidth);
          currentLine.fillColor = settings.fillColor;
          currentLine.allowStroke = settings.allowStroke;
          currentLine.allowFill = settings.allowFill;
          currentLine.fillMode =  settings.fillMode;
          currentLine.gradientColor1 = settings.gradientColor1;
          currentLine.gradientColor2 = settings.gradientColor2;
          currentLine.gradientAngle = settings.gradientAngle;
          currentLine.alpha = parseFloat(settings.alpha);  
        }                        
        segmentsRef.current = [];
        segmentOpRef.current = 'none';
        draw();
      }
    },
    rectTabPressed: (shiftKey, key) =>
    {
      if(operationRef.current === 'bezier ' || operationRef.current === 'pline')
      {
        return;
      }
      const rects = rectsRef.current;
      let length = rects?.length;

      if(rectTabSelectRef.current < 0 || rectTabSelectRef.current > length -1)
      {
        if(rectTabSelectRef.current < 0)
        {
          rectTabSelectRef.current = 0;
        }
        else
        {
          rectTabSelectRef.current = length - 1;
        }
      }

      if(selectedRef.current.length === 1)
      {
          if(key === "Tab" || key === "ArrowRight")
          {
            rectTabSelectRef.current++;
            if(rectTabSelectRef.current > length-1)
            {
              rectTabSelectRef.current  = 0;
            }
          }
          else
          {
            if(key === "ArrowLeft")
            {
              rectTabSelectRef.current--;
              if(rectTabSelectRef.current < 0 )
              {
                rectTabSelectRef.current  = length-1;
              }  
            }
          }

          let r = rects[rectTabSelectRef.current];
          selectedRef.current = [r];  

      }
      else
      {
        if(selectedRef.current.length === 0)
        {
          let r = rects[rectTabSelectRef.current];
          selectedRef.current = [r];
        }
      }

      
      
      onRectSelect(selectedRef.current, null, false, segmentsRef.current, aSegmentRef.current,pointsRef.current);
      draw();

    },
    tabPressed: (shiftKey, key)=>
    {

      if(operationRef.current !== 'bezier' && operationRef.current != 'pline')
      {
        return;
      }

      console.log("tab pressed")

      segmentOpRef.current = 'lineop';

      let numLines = pointsRef.current.length;
      let overFlow = false;

      if(numLines <= 0)
      {
        return;
      }


      if(shiftKey && key === "ArrowLeft")
      {
          // This is the line we would remove if we moved left.
          let lastIndex = tabSelectRef.current;
      
          if(lastIndex < 0)
          {
              lastIndex = numLines - 1;
          }
      
          // Actually, because tabSelectRef.current has not moved yet,
          // the line being removed is the current line.
          const hasSegments = segmentsRef.current.some(
              segment => segment.lineIndex === lastIndex
          );
      
          if(!hasSegments)
          {
              // Nothing to undo, so don't change tabSelectRef.current.
              return;
          }
      
          // Remove all segments belonging to this line.
          segmentsRef.current = segmentsRef.current.filter(
              segment => segment.lineIndex !== lastIndex
          );
      
          // Now move selection backwards.
          tabSelectRef.current--;
      
          if(tabSelectRef.current < 0)
          {
              tabSelectRef.current = numLines - 1;
          }
      }
      else
      {
        if(key === "Tab" || key === "ArrowRight")
        {
            tabSelectRef.current++;
        }
        else
        {
          tabSelectRef.current--;
        }


        if(tabSelectRef.current > numLines-1 || tabSelectRef.current < 0)
        {
          if(tabSelectRef.current > numLines -1)
          {          
              tabSelectRef.current = 0;
          }
          if(tabSelectRef.current < 0)
          {
            tabSelectRef.current = numLines -1;
          }
        }
      }

      const {nLines:l, nControls:c} = removeStray(pointsRef.current, controlRef.current, operationRef.current, false);
      pointsRef.current = l;
      controlRef.current = c;

      undoPlineStack.push(pointsRef.current);   
      if(operationRef.current === 'bezier') 
      {
        undoControlStack.push(controlRef.current);
      }        
      undoSegmentsStack.push(segmentsRef.current);
  
      // segmentsRef.current = [];
      aSegmentRef.current = null;


      let currSegment = selectedSegmentLine(tabSelectRef.current);

      if (!shiftKey)
          {
              segmentsRef.current = [...currSegment];
          }
          else
          {
              if (key === 'ArrowRight')
              {
                  const segmentExists = (currSegment) =>
                  {
                      return currSegment.every(curr =>
                          segmentsRef.current.some(existing =>
                              existing.lineIndex === curr.lineIndex &&
                              existing.segmentIndex === curr.segmentIndex
                          )
                      );
                  };

                  if (!segmentExists(currSegment))
                  {
                      segmentsRef.current.push(...currSegment);
                  }
              }
              if (key === 'ArrowLeft')
              {
                  let lastIndex = tabSelectRef.current + 1;

                  if (lastIndex > numLines - 1)
                  {
                      lastIndex = 0;
                  }

                  segmentsRef.current = segmentsRef.current.filter(
                      segment => segment.lineIndex !== lastIndex
                  );
              }

      }
     

      /*
      if(key === "Tab" || key === "ArrowRight")
      {
        tabSelectRef.current++;
      }
      else
      {
        tabSelectRef.current--;
      }
      */
      onRectSelect([], null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      draw();
    },
    newPolyline: () =>
    {
      console.log("New polyline");
      lineBeginRef.current = true;
    },
    escapeLineOp: () =>
    {      
      if(segmentActionRef.current === "mirrorLine" || segmentActionRef.current === "rotateLine" || segmentActionRef.current === 'resizeLine')
      {
        mirrorPoint1Ref.current = null;
        mirrorPoint2Ref.current = null;        
        mirrorLineRef.current = null;
        mirrorControlRef.current = null;
        rotatePointRef.current = null;
        rotateLineRef.current = null;
        rotateControlRef.current = null;
        
        segmentsRef.current = [];
        segmentOpRef.current = 'none';
        onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
        canvasRef.current.style.cursor = 'crosshair';
        draw();
        return;
      }
    },
    regenIds: (p) =>
    {
      regenUnique(p);
      draw();
    },
    finalizePolyline: () => {            
      if(operationRef.current === 'bezier')
      {
      
        localStorage.setItem('bezier',JSON.stringify(pointsRef.current));
        localStorage.setItem('controls',JSON.stringify(controlRef.current));
      }
      if(operationRef.current === 'pline')
      {
        console.log("pline");
        localStorage.setItem('pline', JSON.stringify(pointsRef.current));
      }

      doFinalizePolyline();      
      console.log("Finalizing polyline");
    },
    finalizeBezier: () => {
      if(operationRef.current === 'bezier')
      {
      
        localStorage.setItem('bezier',JSON.stringify(pointsRef.current));
        localStorage.setItem('controls',JSON.stringify(controlRef.current));
      }
      if(operationRef.current === 'pline')
      {
        console.log("pline");
        localStorage.setItem('pline', JSON.stringify(pointsRef.current));
      }

      doFinalizeBezier();
      console.log("Finalizing bezier");
    },
    saveLine: (op) =>
    {
      
      if(op === 'bezier')
      {
      
        localStorage.setItem('bezier',JSON.stringify(pointsRef.current));
        localStorage.setItem('controls',JSON.stringify(controlRef.current));
      }
      if(op === 'pline')
      {
        console.log("pline");
        localStorage.setItem('pline', JSON.stringify(pointsRef.current));
      }
    },
    restoreLine: (op) =>
    {
      if(op === 'bezier')
      {
        let p = localStorage.getItem('bezier');
        let c = localStorage.getItem('controls');

        if(p != "")
        {
          pointsRef.current = JSON.parse(p);
          controlRef.current = JSON.parse(c);
        }
      }

      if(op === 'pline')
      {
        let p = localStorage.getItem('pline');

        if(p != "")
        {
          pointsRef.current = JSON.parse(p);
        }
      }



    },
    undoPline: () =>
    {
      if(segmentOpRef.current === "lineop")
      {
        let undoSegment = undoSegmentsStack.pop();
        if(undoSegment != undefined && undoSegment.length !== 0)
        {

          segmentsRef.current = undoSegment;
          aSegmentRef.current = null; 
          if(undoSegmentsStack.getLength() === 0)
          {         
            segmentOpRef.current = 'none';           
          }

          undoPlineOperation();        
        }
      }
      else
      {          
        undoPlineOperation();
      }
      draw();
    },
    undoBezier: () =>
    {
      if(segmentOpRef.current === "lineop")
      {
        let undoSegment = undoSegmentsStack.pop();
        if(undoSegment != undefined && undoSegment.length !== 0)
        {

          segmentsRef.current = undoSegment;
          aSegmentRef.current = null; 
          if(undoSegmentsStack.getLength() === 0)
          {         
            segmentOpRef.current = 'none';           
          }

          undoBezierOperation();        
        }
      }
      else
      {          
        undoBezierOperation();
      }
      draw();
    },
    undoFreeHand: () =>
    {
      undoFreeHandOperation();
      draw();
    },
    explodeBezier: () =>
    {        
        pointsRef.current = [];
        controlRef.current = [];

        if(exploadFunc(pointsRef.current, controlRef.current, selectedRef.current))
        {              
          keepPointsRef.current = true;
          setOperation('bezier');
          setRefreshKey();
        }

        draw();
    }, 
    explodePline: () =>
    {        
        pointsRef.current = [];
        controlRef.current = [];

        if(exploadFunc(pointsRef.current, controlRef.current, selectedRef.current))
        {          

          keepPointsRef.current = true;
          setOperation('pline');
          setRefreshKey();
        }

        draw();
    }, 
    adjustImage: ()=>{
      if(selectedRef.current)
      {
          if(selectedRef.current.length ===1)
          {
            let r = selectedRef.current[0];

            if(r.imageName != "")
            {
              setOperation("adjimage");
            }
          }
          
      }
    },
    clearPoints: () =>
    {

      undoPlineStack.push(pointsRef.current);    
      if(operationRef.current === 'bezier')
      {
        undoControlStack.push(controlRef.current);
      }
      pointsRef.current = [];  
      if(operationRef.current ==='bezier')
      {
        controlRef.current = [];
      }

      lineBeginRef.current= true;
      draw();
    },
    resetLineHistory: () =>
    {
      undoPlineStack.clear();
      pointsRef.current = [];  
      lineBeginRef.current= true;

      undoControlStack.clear();
      controlRef.current = [];
      segmentsRef.current=[];
    },

    setRefreshKey: () => {
      setRefreshKey((prevKey) => prevKey + 1);
      
    },
    freeTempRect: () =>
    {      
      tempRectRef.current = null;
      draw();
    },

    applySvgTextOptions: (rect) =>
    {      
      // fontTypeRef.current = rect.fontUrl;
      fontTextRef.current = rect.fontText;


      /*
      console.log("fontTypeRef: ",fontTypeRef.current);      

      selectedRef.current[0].strokeWidth = options.strokeWidth;
      selectedRef.current[0].fillColor = options.fillColor;
      selectedRef.current[0].strokeColor = options.strokeColor;
      selectedRef.current[0].fillAlpha = options.alpha;
      selectedRef.current[0].fontUrl = options.font;
      */

      // setFillColor(options.fillColor);
      // setStrokeColor(options.strokeColor);

    
      adjustText(selectedRef.current[0], fontTextRef.current, true);


      setOperation('select');

      draw();
    },    
    setCursor: (s)=>
    {
      canvasRef.current.style.cursor = s;
    },
    resizeView: (flag) =>
    {
      if(flag === true)
      {
        resizeViewRef.current = true;
        setOperation('resize');
      }
      else
      {
        resizeViewRef.current = false;
        setOperation('select');
      }
    }
  }));

  useEffect(() => {    
    pageSizeRef.current = { width: pageWidth, height: pageHeight };
    filenameRef.current = filename;
    plineRef.current = {
      width:gridWidth, 
      height:gridHeight, 
      stroke:stroke, 
      snapToGrid:snapToGrid, 
      showControls:showControls,
      showEndPoints:showEndPoints,
      stroke, 
      strokeColor, 
      fillColor,
      allowStroke,
      allowFill, 
      fillMode,
      gradientColor1,
      gradientColor2,
      gradientAngle,      
      alpha};  
    clipBoardRef.current = clipBoard;
    
    draw();
  }, [pageWidth, pageHeight, filename, gridWidth, gridHeight, stroke, strokeColor, fillColor, allowStroke, allowFill, alpha, snapToGrid, clipBoard,fillMode, gradientAngle, gradientColor1, gradientColor2, showControls, showEndPoints]);

  useEffect(() => {
    

    const canvas = canvasRef.current;
    if (!canvas) return;
  
    draw();
    const handleContextMenu = (e) => {
      e.preventDefault(); // This stops the browser's context menu from showing
    };


    
    const handleZoom = (e) => {
      e.preventDefault();
    
      const zoomFactor = 1.1;
      const scale = e.deltaY < 0 ? zoomFactor : 1 / zoomFactor;
    
      if(operationRef.current === 'adjimage')
      {
        let r = selectedRef.current[0];

        if(r)
        {
          r.imageZoom = r.imageZoom*scale;
          draw();
          return;
        }
      }

      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
    
      const view = viewPortRef.current;
      const { offsetX, offsetY, zoom } = view;
    
      /*
      // Translate the canvas so that the zoom center (mouse) becomes the new origin
      const dx = mouseX - offsetX;
      const dy = mouseY - offsetY;
    
      // Scale the zoom
      const newZoom = zoom * scale;
      view.zoom = newZoom;
    
      // Translate back to the original position
      view.offsetX = mouseX - dx * scale;
      view.offsetY = mouseY - dy * scale;
      // console.log("operation: ", operation);
      */
    
      const oldZoom = view.zoom;
    
    
      // Convert mouse from screen space to world space
      const worldX = mouseX / oldZoom - view.offsetX;
      const worldY = mouseY / oldZoom - view.offsetY;


      // Apply new zoom
      const newZoom = oldZoom * scale;

      // CHANGED:
      // Solve:
      // mouseX = (worldX + offsetX) * newZoom
      // mouseY = (worldY + offsetY) * newZoom

      view.offsetX = mouseX / newZoom - worldX;
      view.offsetY = mouseY / newZoom - worldY;

      view.zoom = newZoom;

      draw();
    };
    
    const handleMouseUpGlobal = () => {
      draggingRef.current = false;
      if (operationRef.current === 'move' || operationRef.current === 'resize') {        
        setOperation('select');
      }
    };
  
    window.addEventListener('mouseup', handleMouseUpGlobal);

    canvas.addEventListener('contextmenu', handleContextMenu);
    canvas.addEventListener('wheel', handleZoom);

    canvas.addEventListener('mouseleave', () => {
      
      // Optional: pause dragging or show visual cue
    });
    
    canvas.addEventListener('mouseenter', e => {
      if (operationRef.current === 'move' && selectedRef.current.length > 0 )
      {
        const rect = canvas.getBoundingClientRect();
        const xP = e.clientX - rect.left;
        const yP = e.clientY - rect.top;
    
        // Reset drag origin to current mouse location
        // mouseDownPageRef.current = { x: xP, y: yP };
      }
      // You don't need to change state here unless you paused dragging
    });
    

  
    return () => {
      canvas.removeEventListener('contextmenu', handleContextMenu);
      canvas.removeEventListener('wheel', handleZoom);
      window.removeEventListener('mouseup', handleMouseUpGlobal);
    };
  }, []);

  useEffect(() => {

    if(operation === 'pline' && operationRef.current != 'pline')
    {
      selectedRef.current = [];
      onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      if(keepPointsRef.current === false)
      {
        pointsRef.current = [];
        lineBeginRef.current = true; 
      }
      else
      {
        lineBeginRef.current = false;
        keepPointsRef.current = false;
      }
      draw();  
    }

    if(operation === 'freehand' && operationRef.current != 'freehand')
    {
      selectedRef.current = [];      
      onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      pointsRef.current = [];
      controlRef.current = [];
      lineBeginRef.current = true;   
      draw();
    }

    if(operation === 'bezier' && operationRef.current != 'bezier')
    {
      selectedRef.current = [];
      onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      if(keepPointsRef.current === false)
      {

        pointsRef.current = [];
        controlRef.current = [];      
        lineBeginRef.current = true;   
      }
      else
      {
        lineBeginRef.current = false;
        keepPointsRef.current = false;
      }

      draw();
    }

    operationRef.current = operation;
    if (operation === 'delete') {
      draw();
    }

    if (!canvasRef.current) return;
               
    let u = Util.getInstance();
    let serverName = u.getServerName();
    
    switch (operation) {
      case 'move':
              
        // let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%207.168656%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%2015.439830%207.683889%20L%2015.439830%2015.413969%20L%207.168656%2015.413969%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2023.627456%208.074297%20L%2023.711003%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
        // canvasRef.current.style.cursor=`url('${dataUrl}') 16 16, auto`;  
        canvasRef.current.style.cursor = 'move';
        break;
      case 'adjimage':
        canvasRef.current.style.cursor = 'crosshair';
        break;
      case 'resize':
        canvasRef.current.style.cursor = 'nwse-resize';
        break;
      case 'rect':
        // canvasRef.current.style.cursor = 'crosshair';
        canvasRef.current.style.cursor =`url('${serverName}/humanhtml.svg') 2 2, auto`;
        break;
      case 'image':
          // canvasRef.current.style.cursor = 'crosshair';
          canvasRef.current.style.cursor =`url('${serverName}/imageicon.svg') 22 18, auto`;
          break;  
      case 'aiart':
            // canvasRef.current.style.cursor = 'crosshair';
            canvasRef.current.style.cursor =`url('${serverName}/aiart.svg') 5 17, auto`;
            break;      
      case 'airect':
          // canvasRef.current.style.cursor = 'crosshair';
          canvasRef.current.style.cursor =`url('${serverName}/aihtml.svg') 2 2, auto`;
          break;    
      case 'animate':
          canvasRef.current.style.cursor =`url('${serverName}/animate.svg') 2 2, auto`;            
            break;      
      case 'svgcircle':
            canvasRef.current.style.cursor =`url('${serverName}/circle.svg') 2 2, auto`;                        
            break;      
      case 'svgsquare':
          canvasRef.current.style.cursor =`url('${serverName}/square.svg') 2 2, auto`;                        
          break;        
      case 'svgrect':
          canvasRef.current.style.cursor =`url('${serverName}/rectangle.svg') 2 2, auto`;                        
          break;          
      case 'svgellipse':
          canvasRef.current.style.cursor =`url('${serverName}/ellipse.svg') 2 2, auto`;                        
            break;        
      case 'svgtext':
        // canvasRef.current.style.cursor = 'crosshair';
        canvasRef.current.style.cursor =`url('${serverName}/text.svg') 2 2, auto`;            
        break;
      case 'svgemoji':
          // canvasRef.current.style.cursor = 'crosshair';
          canvasRef.current.style.cursor =`url('${serverName}/smiley.svg') 0 0, auto`;            
          break;  
      case 'menubar':
          canvasRef.current.style.cursor = 'crosshair';
          break;  
      case 'paste':
          canvasRef.current.style.cursor = 'crosshair';
          draw();
          break;  
  
      case 'select':
        pointsRef.current = [];
        lineBeginRef.current = true;
        canvasRef.current.style.cursor = 'pointer';
        draw();
        break;
      case 'pline':
        canvasRef.current.style.cursor = 'crosshair';
        break;  
      case 'freehand':
          canvasRef.current.style.cursor = 'crosshair';
          break;  
      case 'bezier':
          canvasRef.current.style.cursor = 'crosshair';
          break;      
      default:
        canvasRef.current.style.cursor = 'default';
    }    
  }, [operation]);

  /*
  useEffect(() => {
    segmentActionRef.current = segmentAction;
    console.log("Segment action: " + segmentActionRef.current);
  }, [segmentAction])
  */


  const getTransform = (parent) =>
  {
    if (!parent) return {matrix: null, opacity:1};
  
    let matrix = { a:1, b:0, c:0, d:1, e:0, f:0 };
    let opacity = 1;
  
    // collect hierarchy
    let hierarchy = [];
    let p = parent;
  
    while (p != null)
    {
      hierarchy.push(p);
      p = parentsRef.current.get(p);
    }
  
    // apply from root → child
    hierarchy.reverse();
  
    for (let obj of hierarchy)
    {
      let pX = obj.x;
      let pY = obj.y;
  
      let cX = obj.w / 2;
      let cY = obj.h / 2;
  
      let t1 = createTranslationMatrix(pX, pY);
      let t2 = createTranslationMatrix(cX, cY);
      let r1 = createRotationMatrix((obj.rotation * Math.PI) / 180);
      let t3 = createTranslationMatrix(-cX, -cY);
  
      matrix = multiplyMatrices(matrix, t1);
      matrix = multiplyMatrices(matrix, t2);
      matrix = multiplyMatrices(matrix, r1);
      matrix = multiplyMatrices(matrix, t3);

      opacity=opacity*obj.fillAlpha;
      
    }
  
    const {offsetX, offsetY, zoom} = viewPortRef.current;
  
    let scr = createScreenTransformationMatrix(zoom, zoom, offsetX, offsetY);
  
    matrix = multiplyMatrices(scr, matrix);
  
    return {matrix, opacity};
  }

  /*
  const getTransform = (parent) =>
  {
    if(parent != undefined && parent != null)      
    {
        let matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }; // Identity matrix
        
        // ctx.save();
        // Traverse the parent hierarchy to get the top parent
        
        let topParent = parent;                         
        let topParentPosition = { x: topParent.x, y: topParent.y, rotation: topParent.rotation};
            
         let pX = topParent.x;
        let pY = topParent.y;
        // let { x: pX, y: pY } = pageToScreen(topParent.x, topParent.y, viewPortRef);

        // ctx.translate(pX, pY);
        let t1 = createTranslationMatrix(pX,pY);

        let cX = topParent.w/2; // *zoom;
        let cY = topParent.h/2; // *zoom;
        // Rotate around rectangle center
        // ctx.translate(zoomW / 2, zoomH / 2);        
        // ctx.rotate((-topParent.rotation * Math.PI) / 180);
        // ctx.translate(-zoomW / 2, -zoomH / 2);

              
        let t2 = createTranslationMatrix(cX, cY);
        let r1 = createRotationMatrix((topParent.rotation * Math.PI) / 180);

        let t3 = createTranslationMatrix(-cX, -cY);
                    
        matrix = multiplyMatrices(matrix, t1);                
        matrix = multiplyMatrices(matrix, t2);        
        matrix = multiplyMatrices(matrix, r1);        
        matrix = multiplyMatrices(matrix, t3);      
                
    
        while (topParent != null) 
        {
            topParent = parentsRef.current.get(topParent); 
            if(topParent != null)
            {
              topParentPosition.x += topParent.x; // Accumulate parent's offset
              topParentPosition.y += topParent.y; // Accumulate parent's offset           
              topParentPosition.rotation += topParent.rotation;


              // let { x: pX, y: pY } = pageToScreen(topParent.x, topParent.y, viewPortRef);
              pX = topParent.x;
              pY = topParent.y;
              cX = topParent.w/2; // *zoom;
              cY = topParent.h/2; // *zoom;

              // ctx.translate(pX, pY);
      
              // Rotate around rectangle center
              // ctx.translate(zoomW / 2, zoomH / 2);
              //ctx.rotate((-topParent.rotation * Math.PI) / 180);
              // ctx.translate(-zoomW / 2, -zoomH / 2);      


              t1 = createTranslationMatrix(pX,pY);              
              t2 = createTranslationMatrix(cX, cY);
              r1 = createRotationMatrix((topParent.rotation * Math.PI) / 180);
              t3 = createTranslationMatrix(-cX, -cY);

              matrix = multiplyMatrices(matrix, t1);
              matrix = multiplyMatrices(matrix, t2);
              matrix = multiplyMatrices(matrix, r1);
              matrix = multiplyMatrices(matrix, t3);
            }              
        }


          // Apply the transformation matrix to the context        
          const {offsetX, offsetY, zoom } = viewPortRef.current;
          let scr=createScreenTransformationMatrix(zoom,zoom,offsetX, offsetY);        
          matrix = multiplyMatrices(scr, matrix);
        

        return matrix;
        
      }
      else
      { 
        return null;
      }

  }
  */

  function throttle(fn ,limit)
  {
    let lastCall = 0;

    return function (...args)
    {
      const now = Date.now();
      if (now - lastCall >= limit)
      {
        lastCall = now;
        fn.apply(this,args);

      }
    }
  }

  function debounce(fn,delay)
  {
    let timeout;
    return function(...args)
    {
      clearTimeout(timeout);
      timeout = setTimeout(()=>{
        fn.apply(this,args);
      }, delay);
    }
  }

  const draw = (stunt=false) =>
  {

    if(stunt === true)
    {
      const throttleDraw = throttle(drawT, 50);

      const debouncedDraw = debounce(drawT, 1000);

      throttleDraw(true);
      debouncedDraw(false);
    }
    else
    {
      drawT(false);
    }

  }
  

  const drawT = (fast) => {

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
  
    const { offsetX, offsetY, zoom } = viewPortRef.current;
    const { width, height } = pageSizeRef.current;
    const {width:gridWidth, height:gridHeight, stroke: gridStroke}=plineRef.current;

    const pageInfo = pageInfoRef.current;

    let backgroundColor = "#ddd";
    let pageColor = 'white';

    let fillMode = "solid";
    let gradientColor1 = "#ffffff";
    let gradientColor2 = "#000000";
    let gradientAngle = 0;
    // const marginPadding = 40; // Margin width outside the page, adjust this to make it bigger or smaller
    let marginTop = 0;
    let marginBottom = 0;
    
    labelZ();

    if(pageInfo != null)
    {
      let firstPage =pagesRef?.current?.pages?.[0]?.pageInfo;

      if(firstPage != null)
      {
        backgroundColor = firstPage.browserBackground; // You can change this dynamically
      }
      pageColor = pageInfo.pageBackground;

      fillMode = pageInfo.fillMode || "solid";
      gradientColor1 = pageInfo.gradientColor1 || pageColor;
      gradientColor2 = pageInfo.gradientColor2 || pageColor;
      gradientAngle = parseFloat(pageInfo.gradientAngle || 0);  
      marginTop = parseInt(pageInfo.marginTop);
      marginBottom = parseInt(pageInfo.marginBottom);
    }
            
    // Create a semi-transparent margin color based on the background color
    const marginAlpha = 1; // Adjust this alpha for better visibility
    const factor=0.85;
    let marginColor = `rgba(${hexToRgb(backgroundColor, factor)},${marginAlpha})`;
  

    // Clear canvas
    ctx.fillStyle = backgroundColor; // '#ddd';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    
    if(clipBoardRef.current === true)
    {
        drawClipBoard(ctx, viewPortRef, pageSizeRef, onClipRef.current, clipIndexRef.current, auth, demo);
    }
  
  
    if (filenameRef.current != '') {
      const { x: xR, y: yR } = pageToScreen(0, 0, viewPortRef);
      const pageW = width * zoom;
      const pageH = height * zoom;

       // Draw the margin (rectangle around the page)
      ctx.fillStyle = marginColor;
      // ctx.fillRect(xR - marginPadding, yR - marginPadding, pageW + marginPadding * 2, pageH + marginPadding * 2);  // Surrounding the page
      
      ctx.fillRect(xR, yR - marginTop, pageW, pageH + marginTop+marginBottom);  // Surrounding the page
  
      ctx.save();
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 12;
      ctx.shadowOffsetX = 6;
      ctx.shadowOffsetY = 6;
  
      if(animateOpRef.current === 'selectMPath' ||
        animateOpRef.current === 'selectMObj' ||
        animateOpRef.current === 'handleOffset' ||
        animateOpRef.current === 'handleOffsetPath' ||
        animateOpRef.current === 'resizeView')
      {
        // user has to select an mpath or an mobj
        ctx.fillStyle = "#000"; // '#ddd';
      }
      else
      {
         // --- SOLID OR GRADIENT PAGE BACKGROUND ---
         if (fillMode === "gradient")
         {
             const radians = (gradientAngle * Math.PI) / 180;
 
             const cx = xR + pageW / 2;
             const cy = yR + pageH / 2;
 
             const halfDiag = Math.sqrt(pageW * pageW + pageH * pageH) / 2;
 
             const x1 = cx - Math.cos(radians) * halfDiag;
             const y1 = cy - Math.sin(radians) * halfDiag;
 
             const x2 = cx + Math.cos(radians) * halfDiag;
             const y2 = cy + Math.sin(radians) * halfDiag;
 
             const gradient = ctx.createLinearGradient(x1, y1, x2, y2);
             gradient.addColorStop(0, gradientColor1);
             gradient.addColorStop(1, gradientColor2);
 
             ctx.fillStyle = gradient;
         }
         else
         {
             ctx.fillStyle = pageColor;
         }
        // ctx.fillStyle = pageColor;      
      }

      ctx.fillRect(xR, yR, pageW, pageH);
      
      ctx.restore();
  
      ctx.strokeStyle = '#888';
      ctx.lineWidth = 1;
      ctx.strokeRect(xR, yR, pageW, pageH);
    }

    if(tempRectRef.current != null)
    {      
      const { x, y } = pageToScreen(tempRectRef.current.x, tempRectRef.current.y, viewPortRef);
      const w = tempRectRef.current.w * zoom;
      const h = tempRectRef.current.h * zoom;

      // ctx.fillStyle = 'rgba(0,0,255,0.3)';
      ctx.fillStyle = "rgba(0, 0, 0, 0.05)"; 
      ctx.fillRect(x, y, w, h);


      ctx.strokeStyle ='black';
      ctx.lineWidth = 3;

      ctx.strokeRect(x,y,w,h);        
    }
    
    // const queue = [...rectsRef.current];  // Start with all rectangles (including groups)


    // let firstGroup=true;
    for(let i=0; i<renderRef.current.length; i++)
    {
        const rect = renderRef.current[i];        
        if(rect.imageName !== "")
        { 

          let webId = auth?.user?.webId
          getCachedImage(rect.imageName, webId, demo, ()=>{
            draw();
          });
        }
    }
    
    /*
    getCachedImage("aiart.webp", "", demo, ()=> {
      draw();
    });

    getCachedImage("human.webp", "", demo, ()=> {
      draw();
    });

    getCachedImage("animate.webp", "", demo, ()=> {
      draw();
    });

    getCachedImage("aicode.webp", "", demo, ()=> {
      draw();
    });
    */


    


    // let firstGroup=true;
    for(let i=0; i<renderRef.current.length; i++)
    {
        const rect = renderRef.current[i];


    // while (queue.length > 0) {
    //   const rect = queue.shift(); // Get the next rectangle/group from the queue
    // Draw existing rectangles
    // for (const rect of rectsRef.current) {


      let parent = parentsRef.current.get(rect);
      let {matrix, opacity}= getTransform(parent);


      let {x,y} = pageToScreen(rect.x,rect.y, viewPortRef);

      let w = 0;
      let h = 0;
      
      if(matrix != null)
      {
        
        /*
         const {x:xPrime, y:yPrime} = transformPoint({x:rect.x, y:rect.y}, matrix);

         x = xPrime;
         y = yPrime;
         */
         
        
        x = rect.x;
        y = rect.y;
        w = rect.w;
        h = rect.h;

        rect.hasParent=true;
        rect.parentAlpha = opacity;

        ctx.save();
        applyMatrixToCtx(ctx,matrix);
        
      }
      else
      {
        
        matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }; // Identity matrix

        x = rect.x;
        y = rect.y;
        w = rect.w;
        h = rect.h;
        rect.hasParent = false;
        rect.parentAlpha = 1;

        if(rect.subType === 'Animate')
        {
          if(rect.rotation != 0)
          {
            let cX = rect.x+rect.w / 2;
            let cY = rect.y+rect.h / 2;

            let t2 = createTranslationMatrix(cX, cY);
            let r1 = createRotationMatrix((rect.rotation * Math.PI) / 180);
            let t3 = createTranslationMatrix(-cX, -cY);

            matrix = multiplyMatrices(matrix, t2);
            matrix = multiplyMatrices(matrix, r1);
            matrix = multiplyMatrices(matrix, t3);
      
          }
      
        }

        // Apply the transformation matrix to the context        
        const {offsetX, offsetY, zoom } = viewPortRef.current;
        let scr=createScreenTransformationMatrix(zoom,zoom,offsetX, offsetY);        
        
        matrix = multiplyMatrices(scr, matrix);
        
        ctx.save();
        applyMatrixToCtx(ctx,matrix);
        

        // w = rect.w * zoom;
        // h = rect.h * zoom;
  
      }
      
      
      
      if(rect.type === "Group")
      {
        // drawRotatedRect(ctx, x,y,w,h, -1*rect.rotation*Math.PI/180);        
        // drawSvgRect(ctx,x,y,w,h, rect);
        ctx.save();
        drawGroup(ctx,x,y,w,h, rect);

        if (rect.imageName !== "")
        {          
          let a = rect.rotation;

          let webId = auth?.user?.webId;            
          let iCache = getImage(rect.imageName, webId, demo);                                      
          drawImageClip(ctx,x,y, w, h, a, webId, rect.imageName, iCache, demo, rect, viewPortRef);         
        }  
        ctx.restore();

      }
            
      // ctx.fillStyle = 'rgba(0,0,255,0.3)';

      if(parent === null || parent === undefined)
      {
        if(highlightState(rect))
        {
          ctx.fillStyle = getHighlightFill(rect);
          //ctx.fillStyle = "rgba(0, 0, 0, 0.05)";       
          ctx.fillRect(x, y, w, h);
        }
      }
      
      if(rect.type === "Group")
      {
        if(selectedRef.current.includes(rect) /*&& firstGroup*/)
        {
          // firstGroup = false;
          if(rect.locked === true)
          {
            ctx.strokeStyle ='blue';
            ctx.lineWidth = 5;
          }
          else
          {
            ctx.strokeStyle ='red';
            ctx.lineWidth = 5;
          }
        }
        else
        {
          ctx.strokeStyle ='black';
          ctx.lineWidth = 1;
        }
      }
      else
      {
        if(rect.subType !== "Animate")
        {          
          if(selectedRef.current.includes(rect))
          {
            if(rect.locked === true)
            {
              ctx.strokeStyle = 'blue';
              ctx.lineWidth = 5;

            }
            else
            {
              ctx.strokeStyle = 'red';
              ctx.lineWidth = 5;
  
            }

          }
          else
          {
            ctx.strokeStyle = 'black';
            ctx.lineWidth = 1;
          }
          // ctx.strokeStyle = (selectedRef.current.includes(rect)) ? 'red' : 'black';
          // ctx.lineWidth = (selectedRef.current.includes(rect))? 5 : 1;
        }
        else
        {

          if(selectedRef.current.includes(rect))
          {
            if(rect.locked === true)
            {
              ctx.strokeStyle = 'blue';
              ctx.lineWidth = 5;

            }
            else
            {
              ctx.strokeStyle = 'red';
              ctx.lineWidth = 5;
  
            }

          }
          else
          {
            ctx.strokeStyle = 'black';
            ctx.lineWidth = 1;
          }

         
            // ctx.strokeStyle = (selectedRef.current.includes(rect)) ? 'red' : 'black';
            // ctx.lineWidth = (selectedRef.current.includes(rect))? 5 : 1;

            if(ctx.lineWidth > 1)
            {
              drawRotatedRect(ctx, x,y,w,h, 1*rect.rotation*Math.PI/180, true);
            }
            else
            {
              drawRotatedRect(ctx, x,y,w,h, 1*rect.rotation*Math.PI/180, false);
            }
        }        
      }


      /*
      if(rect.type !=='Prect' && rect.type !== 'SvgText' && rect.type !=='Bezier' )
      {
        ctx.strokeRect(x, y, w, h);

        drawResize(ctx,x,y,w,h);        
      }
      */


      if(rect.type === 'SvgText')
      {     

        let highlight = highlightState(rect);

        if(highlight)
        {

          ctx.save();
          // ctx.strokeRect(x, y, w, h);                               
          // drawTextPathOnCtx(ctx, rect.svgText, x,y, w,h, rect.rotation ,zoom, zoom, rect.rZoomX, rect.rZoomY, rect.fillColor, rect.strokeColor, rect.strokeWidth); 
          drawTextPathOnCtx(ctx, rect.svgText, x,y, w,h, rect.rotation ,rect,zoom, zoom); 
          let b = getPathBoundingBox(rect.svgText);
          rect.bound = b;
          ctx.restore();
        }

      }
  
      if(rect.type === 'Rectangle')
      {
        
        if(rect.subType === 'Image')
        {
          let highlight = highlightState(rect);

          if(highlight)
          {
            ctx.save();
            let a = rect.rotation;

            let selected = false;
            if(selectedRef.current.includes(rect))
            {
              selected = true;
            }
            drawImage(ctx,x,y, w, h, a, auth?.user?.webId, rect.imageName, demo, selected, rect.keepAspect, rect, () =>
            {            
              
              draw();
      
            });        
            ctx.restore();  
          }
        }

        if(rect.subType === 'Text')
        {
          let highlight = highlightState(rect);
          if(highlight)
          {
            if(rect.plainText != "")
            {            
              drawText(ctx,x+10,y+10,w-20,h-20,rect.plainText /*rect.richText*/, rect.rZoomX,rect.rZoomY);
            }
            else
            {
              ctx.save();
              let a = rect.rotation;
  
              let selected = false;
              if(selectedRef.current.includes(rect))
              {
                selected = true;
              }

              drawPlaceholder(ctx,x,y,w,h,a, "EDITOR", selected);
              
              // drawText(ctx,x+10,y+10,w-20,h-20,"Generate New AI Vector or AI Image" /*rect.richText*/, rect.rZoomX,rect.rZoomY);
              /*
              drawImage(ctx,x,y, w, h, a, "", "human.webp", demo, selected, rect.keepAspect, rect, () =>
              {                            
                draw();        
              });        
              */
              ctx.restore();  
   
            }
          }
        }

        if(rect.subType === 'Custom')
        {
          let highlight = highlightState(rect);
          if(highlight)
          {
            if(rect?.aiCode != undefined && rect.aiCode != "")
            {
                drawText(ctx,x+10,y+10,w-20,h-20,rect.aiCode /*rect.richText*/, rect.rZoomX,rect.rZoomY);
            }
            else
            {
              ctx.save();
              let a = rect.rotation;
  
              let selected = false;
              if(selectedRef.current.includes(rect))
              {
                selected = true;
              }

              drawPlaceholder(ctx,x,y,w,h,a, "AI HTML", selected);
              // drawText(ctx,x+10,y+10,w-20,h-20,"Generate New AI Vector or AI Image" /*rect.richText*/, rect.rZoomX,rect.rZoomY);
              /*
              drawImage(ctx,x,y, w, h, a, "", "aicode.webp", demo, selected, rect.keepAspect, rect, () =>
              {            
                
                draw();
        
              });        
              */
              ctx.restore();  
  
            }
          }
        }
        if(rect.subType === 'AiArt')
        {

          let highlight = highlightState(rect);

          if(highlight)
          {
            ctx.save();
            let a = rect.rotation;

            let selected = false;
            if(selectedRef.current.includes(rect))
            {
              selected = true;
            }

            drawPlaceholder(ctx,x,y,w,h,a, "AI Art", selected);
            
            /*
            // drawText(ctx,x+10,y+10,w-20,h-20,"Generate New AI Vector or AI Image", rect.rZoomX,rect.rZoomY);
            drawImage(ctx,x,y, w, h, a, "", "aiart.webp", demo, selected, rect.keepAspect, rect, () =>
            {            
              
              draw();
      
            });        
            */
            ctx.restore();  
          }
        }



        if(rect.subType === 'Animate')
        {
          let highlight = highlightState(rect)
          if(highlight)
          {
            // drawText(ctx,x+10,y+10,w-20,h-20,rect.description, rect.rZoomX,rect.rZoomY);

            if(rect.mPath === undefined && rect.mObj === undefined)
            {
              ctx.save();
              let a = rect.rotation;

              let selected = false;
              if(selectedRef.current.includes(rect))
              {
                selected = true;
              }

              drawPlaceholder(ctx,x,y,w,h,a, "ANIMATION", selected);
              
              // drawText(ctx,x+10,y+10,w-20,h-20,"Generate New AI Vector or AI Image" /*rect.richText*/, rect.rZoomX,rect.rZoomY);
              /*
              drawImage(ctx,x,y, w, h, a, "", "animate.webp", demo, selected, rect.keepAspect, rect, () =>
              {                            
                draw();        
              });    
              */    
              ctx.restore();  
            }
          }
        }

        if(rect.subType === 'SvgCircle')
        {

          let highlight = highlightState(rect)
          if(highlight)
          {

            ctx.save();      
            drawSvgCircle(ctx,x,y,w,h, rect);

            if (rect.imageName !== "")
            {          
              let a = rect.rotation;

              let webId = auth?.user?.webId;            
              let iCache = getImage(rect.imageName, webId, demo);                                             
              drawImageClip(ctx,x,y, w, h, a, webId, rect.imageName,  iCache, demo, rect, viewPortRef)          
            }  
            ctx.restore();
          }
        }

        if(rect.subType === 'SvgSquare')
        {
          let highlight = highlightState(rect)
          if(highlight)
          {

            drawSvgSquare(ctx,x,y,w,h, rect);

            if (rect.imageName !== "")
            {          
              let a = rect.rotation;
              let webId = auth?.user?.webId;            
              let iCache = getImage(rect.imageName, webId, demo);                                             
              drawImageClip(ctx,x,y, w, h, a, webId, rect.imageName,  iCache, demo, rect, viewPortRef)              
            }  
          }
        }

        if(rect.subType === 'SvgRect')
        {
          let highlight = highlightState(rect)    
          if(highlight)
          {
            drawSvgRect(ctx,x,y,w,h, rect);

            if (rect.imageName !== "")
            {          
              let a = rect.rotation;

              let webId = auth?.user?.webId;            
              let iCache = getImage(rect.imageName, webId, demo);                                             
              drawImageClip(ctx,x,y, w, h, a, webId, rect.imageName,  iCache, demo, rect, viewPortRef)              
            }
          }  
        }

        if(rect.subType === 'SvgEllipse')
        {
          let highlight = highlightState(rect)
          if(highlight)
          {

            drawSvgEllipse(ctx,x,y,w,h, rect);

            if (rect.imageName !== "")
            {          
              let a = rect.rotation;

              let webId = auth?.user?.webId;            
              let iCache = getImage(rect.imageName, webId, demo);
              drawImageClip(ctx,x,y, w, h, a, webId, rect.imageName,  iCache, demo, rect, viewPortRef);          
            }
          }
        }

        if(rect.subType === 'Animate')
        {
            let highlight = highlightState(rect)
            if(highlight)
            {
              ctx.save();

              let m = rect.mPath;
              if(m === undefined)
              {
                m = rect.mObj;
              }

              if(m != undefined && m != null)
              {

                if(animateOpRef.current === 'handleOffsetPath' ||
                  animateOpRef.current === 'resizeView')
                {
                  ctx.save();
                  // highligh white background
                  ctx.fillStyle = "#FFFFFF";
                  let mr = m;
                  ctx.fillRect(mr.x, mr.y, mr.w, mr.h);
                  ctx.restore();
                  
                }
                                
                if(m.type === 'Bezier')
                {
                  drawCurvePath(ctx, m, viewPortRef);
                }
                if(m.type ==='Prect')
                {
                  drawPath(ctx, m, viewPortRef);
                }

                if(m.subType === 'SvgRect' || m.subType === 'SvgSquare')
                {
                  let r = m;
                  drawSvgRect(ctx,r.x,r.y,r.w,r.h, r);
                }
                if(m.subType === 'SvgEllipse' || m.subType === 'SvgCircle')
                {
                  let r = m;
                  drawSvgEllipse(ctx,r.x,r.y,r.w,r.h, r);
                }
                              
              }
              if(rect.mObj != undefined)
              {
                if(animateOpRef.current === 'handleOffset')
                {
                  // highligh white background
                  ctx.save();
                  ctx.fillStyle = "#FFFFFF";
                  let mr = rect.mObj;
                  ctx.fillRect(mr.x, mr.y, mr.w, mr.h);
                  ctx.restore();
                  
                }
                if(rect.mObj.type === 'Bezier')
                {
                  drawCurvePath(ctx, rect.mObj, viewPortRef);
                  if (rect.mObj.imageName !== "")
                  {       
                    let m = rect.mObj;
                    let a = m.rotation;

                    let webId = auth?.user?.webId;            
                    let iCache = getImage(m.imageName, webId, demo);                                              
                    drawImageClip(ctx,m.x,m.y, m.w, m.h, a, webId, m.imageName, iCache,  demo, m, viewPortRef);          
                  }                      
                }

                if(rect.mObj.type ==='Prect')
                {
                  drawPath(ctx, rect.mObj, viewPortRef);
                  if (rect.mObj.imageName !== "")
                  {       
                    let m = rect.mObj;
                    let a = m.rotation;

                    let webId = auth?.user?.webId;            
                    let iCache = getImage(m.imageName, webId, demo);                          
                    drawImageClip(ctx,m.x,m.y, m.w, m.h, a, webId, m.imageName, iCache,  demo, m, viewPortRef);          
                  }                      
                }

                
                if(rect.mObj.type === 'SvgText')
                {
                  let m = rect.mObj;
                  ctx.save();
                  // ctx.strokeRect(x, y, w, h);                               
                  // drawTextPathOnCtx(ctx, rect.svgText, x,y, w,h, rect.rotation ,zoom, zoom, rect.rZoomX, rect.rZoomY, rect.fillColor, rect.strokeColor, rect.strokeWidth); 
                  drawTextPathOnCtx(ctx, m.svgText, m.x,m.y, m.w,m.h, m.rotation ,m,zoom, zoom); 
                  let b = getPathBoundingBox(m.svgText);
                  rect.bound = b;
                  ctx.restore();    
                }
              }

              // drawCurvePath(ctx,rect);
              ctx.restore();
            }
        }
      }

      if(rect.type === 'Prect')
      {                 
        let highlight = highlightState(rect)
        if(highlight)
        {
          ctx.save();     
          drawPath(ctx,rect, viewPortRef);
              
          if (rect.imageName !== "")
          {          
            let a = rect.rotation;

            let webId = auth?.user?.webId;            
            let iCache = getImage(rect.imageName, webId, demo);                                      
            drawImageClip(ctx,x,y, w, h, a, webId, rect.imageName, iCache, demo, rect, viewPortRef);         
          }
          ctx.restore();      
        }
      }

      
      if(rect.type === 'Bezier')
      {                         
        let highlight = highlightState(rect);              
        if(highlight)
        {
            ctx.save();     
            
            drawCurvePath(ctx,rect, viewPortRef);
                 
            if (rect.imageName !== "")
            {     
              ctx.save();               
              let a = rect.rotation;

              let webId = auth?.user?.webId;            
              let iCache = getImage(rect.imageName, webId, demo);                                      
              drawImageClip(ctx,x,y, w, h, a, webId, rect.imageName, iCache, demo, rect, viewPortRef);         
  

              ctx.restore();  
            }
                    
            ctx.restore();        
          }
                
      }
      
      /*
      if(rect.type === 'Group')
      {
         // If the current rect is a group, add its children to the queue
        if (rect.children && rect.children.length > 0) 
        {
            rect.children.forEach(child => {
            queue.push(child); // Add each child to the queue for later drawing
            });
          }
      }
      */

      if((rect.type === 'Prect' || rect.type === 'SvgText'  || rect.type === 'Bezier' || rect.type === 'Rectangle' || rect.type==='Group') && 
        selectedRef.current.includes(rect))
      {

        if(rect.subType != 'Animate')
        {
          ctx.strokeRect(x,y,w,h);  

          //resize handle
          drawResize(ctx,x,y,w,h);
        }
        else
        {
          drawResize(ctx,x,y,w,h, 1*rect.rotation*Math.PI/180);
        }
  
      }


      if(selectedRef.current.includes(rect))
      {
          // drawInfo(ctx, rect); // Draw the info in the center

      }


      ctx.restore();

      // ctx.restore();
    }
  
    drawOcclusions(ctx);
    const {snapToGrid} = plineRef.current;

    if(snapToGrid === true)
    {      
      drawGrid(ctx,gridWidth, gridHeight, width, height, zoom, offsetX, offsetY)
    }

    if(operationRef.current === 'pline')
    {            
      if(segmentOpRef.current === 'lineop')
      {
        drawPLine(ctx, pointsRef.current, viewPortRef, false);
        drawSelectedSegments(ctx, segmentsRef.current, viewPortRef, aSegmentRef.current)

        if(segmentActionRef.current === 'mirrorLine')
        {
          drawMirrorLine(ctx, mirrorPoint1Ref.current, mirrorPoint2Ref.current, viewPortRef);                  
          drawFaintPLine(ctx, mirrorLineRef.current,viewPortRef);
        }

        if(segmentActionRef.current === 'rotateLine')
        {
          if(mirrorPoint1Ref.current == null || mirrorPoint2Ref.current == null)
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,mirrorPoint2Ref.current, viewPortRef);
          }
          else
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,rotatePointRef.current, viewPortRef);
            drawFaintPLine(ctx, rotateLineRef.current,viewPortRef);
          }
        }
        if(segmentActionRef.current === 'resizeLine')
        {
          // fix for resizeLine
          if(mirrorPoint1Ref.current == null || mirrorPoint2Ref.current == null)
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,mirrorPoint2Ref.current, viewPortRef);            
          }
          else
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,rotatePointRef.current, viewPortRef);
            drawFaintPLine(ctx, rotateLineRef.current,viewPortRef);
          }
        }

      }
      else
      {
        const {showEndPoints} = plineRef.current;
        drawPLine(ctx, pointsRef.current, viewPortRef, showEndPoints);
      }
    }

    if(operationRef.current === 'freehand')
    {
      drawFreehand(ctx, pointsRef.current, viewPortRef);
    }


    if(operationRef.current === 'bezier')
    {
      
      if(segmentOpRef.current === 'lineop')
      {
        drawControls(ctx, pointsRef.current, controlRef.current, viewPortRef, false, false);
        drawSelectedSegmentsB(ctx, segmentsRef.current, viewPortRef, aSegmentRef.current)

        if(segmentActionRef.current === 'mirrorLine')
        {
          drawMirrorLine(ctx, mirrorPoint1Ref.current, mirrorPoint2Ref.current, viewPortRef);          
          drawFaintBezier(ctx, mirrorLineRef.current,mirrorControlRef.current,viewPortRef);
        }
        if(segmentActionRef.current === 'rotateLine')
        {
          if(mirrorPoint1Ref.current == null || mirrorPoint2Ref.current == null)
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,mirrorPoint2Ref.current, viewPortRef);
          }
          else
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,rotatePointRef.current, viewPortRef);
            drawFaintBezier(ctx, rotateLineRef.current, rotateControlRef.current,viewPortRef);
          }
        }
        if(segmentActionRef.current === 'resizeLine')
        {
          if(mirrorPoint1Ref.current == null || mirrorPoint2Ref.current == null)
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,mirrorPoint2Ref.current, viewPortRef);
          }
          else
          {
            drawMirrorLine(ctx,mirrorPoint1Ref.current,rotatePointRef.current, viewPortRef);
            drawFaintBezier(ctx, rotateLineRef.current, rotateControlRef.current,viewPortRef);
          }
        }


      }
      else
      {
        const {showControls, showEndPoints} = plineRef.current;

        drawControls(ctx, pointsRef.current, controlRef.current, viewPortRef, showControls,showEndPoints);
      }
    }

    //indicates special draw modes
    if (operationRef.current === 'bezier' ||
        operationRef.current === 'freehand' ||
        operationRef.current === 'pline') {

      ctx.save();
    
      // Clip to entire canvas
      ctx.beginPath();
      ctx.rect(0, 0, canvas.width, canvas.height);
      ctx.clip();
    
      // Very subtle diagonal hatch
      ctx.strokeStyle = 'rgba(80, 120, 180, 0.06)';
      ctx.lineWidth = 1;
    
      const spacing = 12;
    
      // Start above the canvas so the entire area is covered
      for (
        let x = -canvas.height;
        x < canvas.width;
        x += spacing
      ) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + canvas.height, canvas.height);
        ctx.stroke();
      }
    
      ctx.restore();
    }    
  }

  function drawOcclusions(ctx)
  {

    for(let i=0; i<selectedRef.current.length; i++)
    {
        const rect = selectedRef.current[i];
        

        if(isCompletelyOccluded(rect, renderRef.current))
        {

          let parent = parentsRef.current.get(rect);
          let {matrix, opacity}= getTransform(parent);
    
    
          let {x,y} = pageToScreen(rect.x,rect.y, viewPortRef);
    
          let w = 0;
          let h = 0;
          
          if(matrix != null)
          {
            
            /*
             const {x:xPrime, y:yPrime} = transformPoint({x:rect.x, y:rect.y}, matrix);
    
             x = xPrime;
             y = yPrime;
             */
             
            
            x = rect.x;
            y = rect.y;
            w = rect.w;
            h = rect.h;
    
            rect.hasParent=true;
            rect.parentAlpha = opacity;
    
            ctx.save();
            applyMatrixToCtx(ctx,matrix);
            
          }
          else
          {
            
            matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 }; // Identity matrix
    
            x = rect.x;
            y = rect.y;
            w = rect.w;
            h = rect.h;
            rect.hasParent = false;
            rect.parentAlpha = 1;
    
            if(rect.subType === 'Animate')
            {
              if(rect.rotation != 0)
              {
                let cX = rect.x+rect.w / 2;
                let cY = rect.y+rect.h / 2;
    
                let t2 = createTranslationMatrix(cX, cY);
                let r1 = createRotationMatrix((rect.rotation * Math.PI) / 180);
                let t3 = createTranslationMatrix(-cX, -cY);
    
                matrix = multiplyMatrices(matrix, t2);
                matrix = multiplyMatrices(matrix, r1);
                matrix = multiplyMatrices(matrix, t3);
          
              }
          
            }
    
            // Apply the transformation matrix to the context        
            const {offsetX, offsetY, zoom } = viewPortRef.current;
            let scr=createScreenTransformationMatrix(zoom,zoom,offsetX, offsetY);        
            
            matrix = multiplyMatrices(scr, matrix);
            
            ctx.save();
            applyMatrixToCtx(ctx,matrix);
            
    

            // w = rect.w * zoom;
            // h = rect.h * zoom;
      
          }

          ctx.setLineDash([4, 4]);
          ctx.strokeStyle = "red"; // "rgba(0, 120, 255, 0.8)";
          ctx.lineWidth = 3;

          drawRotatedRect(ctx, x,y,w,h, 1*rect.rotation*Math.PI/180, true);

          
          ctx.restore();

        }

    }

  }

  function getHighlightFill(rect)
  {
    if(animateOpRef.current === 'none')
    {
      return "rgba(0, 0, 0, 0.03)";       
    }    
    else
    {

      if(rect.subType === 'Animate')
      {
        return "rgba(255, 255, 255, 0.5)";
      }

      return "rgba(255, 255, 255, 1)";       
    }
  }
  

  function highlightState(rect)
  {

    if(animateOpRef.current === 'none')
    {
      return true;
    }

    if(animateOpRef.current === 'handleOffset')
    {
      if(rect.subType === 'Animate')
      {
        return true;
      }      
    }
 
    if(animateOpRef.current === 'handleOffsetPath' || animateOpRef.current === 'resizeView')
    {
      if(selectedRef.current.includes(rect))
      {
        if(rect.subType === 'Animate')
        {
          return true;
        }
      }
    }
    
    if(animateOpRef.current === 'selectMPath')
    {
      if(rect.type === 'Bezier' && rect.path.length <= 1)
      {
        return true;
      }

      if(rect.type === 'Prect' && rect.path.length <= 1)
      {
        return true;
      }

      if (rect.subType === 'SvgRect' ||
          rect.subType === 'SvgSquare' ||
          rect.subType === 'SvgCircle' ||
          rect.subType === 'SvgEllipse')
      {
        return true;
      }
    }

    if(animateOpRef.current === 'selectMObj')
    {
      if(rect.type === 'Bezier')
      {
        return true;
      }
      if(rect.type ==='Prect')
      {
        return true;
      }

      if(rect.type === 'SvgText')
      {
        return true;
      }
    }
    return false;
  }


  function adjustText(newRect, textVar, zoomRect)
  {      
        let  fontUrl = `https://myaiweb.ca/fonts/static/${newRect.fontUrl}`;

        if(demo === true)
        {
          fontUrl = `https://ait.myaiweb.ca/fonts/static/${newRect.fontUrl}`;
        }
  

        // newRect.fontUrl = fontUrl;
        newRect.fontText = textVar;


        let beforeWidth = newRect.w;
        let beforeHeight = newRect.h;

        let fontSize = 16;
        const opts = {x:0, y:0, fontSize:fontSize,rotation:0}

/*        
        const textRenderer = fontUrl.toLowerCase().endsWith(".svg")
        ? svgTextToSvgPath
        : textToSvgPath;


      textRenderer(
        newRect.fontText,
        fontUrl,
        opts,
        newRect,
        ({ svgPath, rotation, rect, descent, maxWidth, inRect}) => {
*/          
        textToSvgPath(newRect.fontText, fontUrl, opts, newRect, ({ path, svgPath, rotation, rect, descent, maxWidth, inRect}) => {
            inRect.svgText = svgPath;
            inRect.rotation = rotation;
            // newRect.y = newRect.y;     
            inRect.y = inRect.y;       
            inRect.w = rect.w +  maxWidth ;
            inRect.h = fontSize + descent + 5;
            inRect.originalSvgPath = svgPath;
            inRect.originalWidth = inRect.w;
            inRect.originalHeight = inRect.h;
            
            console.log("Height: ", rect.h);

            viewPortRef.current.originalWidth = inRect.w;
            viewPortRef.current.originalHeight = inRect.h;
            viewPortRef.current.originalSvgPath = svgPath;


            if(zoomRect === true)
            {
              if(beforeWidth > 30 && beforeHeight > 30)
              {
                console.log("Applying Zoom")
                // Calculate the zoom factors (relative change in width/height)
                // w and h have changed from within the adjustText function
                const rZoomX = beforeWidth/inRect.w;  // Zoom factor in the X direction
                const rZoomY = beforeHeight/inRect.h; // Zoom factor in the Y direction
                
                selectedRef.current[0].rZoomX = rZoomX;
                selectedRef.current[0].rZoomY = rZoomY;
                
                let scaledPathString = scaleSvgPathString(inRect.originalSvgPath, rZoomX, rZoomY);
                // console.log("scaled: ", scaledPathString);
                let sane = sanitizePath(scaledPathString);
                inRect.svgText = sane;
                inRect.w = beforeWidth;
                inRect.h = beforeHeight;

              }
        
            }

            // console.log("svgText: ", rect.svgText);
             draw(); 
        });
  }

  function snap(value, gridSize) {
    return Math.round(value / gridSize) * gridSize;
  }
  
  function createGridCache(gridWidth, gridHeight, pageWidth, pageHeight) {
    
    gridCacheCanvas = document.createElement('canvas');    
    gridCacheCanvas.width = pageWidth;
    gridCacheCanvas.height = pageHeight;

    if(gridCacheCanvas.width < 100)
    {
      gridCacheCanvas.width = 100;
    }

    if(gridCacheCanvas.height < 100)
    {
      gridCacheCanvas.height = 100;
    }

    // console.log("GridWidth: ", gridWidth);


    gridWidth = Number(gridWidth);
    gridHeight = Number(gridHeight);

    if(gridWidth > 50)
    {
      gridWidth = 50;
      
    }
    if(gridHeight > 50)
    {
      gridHeight = 50;
    }
    if(gridWidth < 10)
    {
      gridWidth = 10;
    }
    if(gridHeight < 10)
    {
      gridHeight = 10;
    }
    
    gridCacheCanvas.gridWidth = gridWidth;
    gridCacheCanvas.gridHeight = gridHeight;

    gridCacheCtx = gridCacheCanvas.getContext('2d');
  
    gridCacheCtx.fillStyle = '#aaa';
    
    for (let y = 0; y <= pageHeight; y += gridHeight) {
      for (let x = 0; x <= pageWidth; x += gridWidth) {
        gridCacheCtx.beginPath();
        gridCacheCtx.arc(x, y, 1.2, 0, Math.PI * 2);
        gridCacheCtx.fill();
      }
    }
  }

  function rotateLine()
  {
    let li=segmentsRef.current[0].lineIndex;
    
    let points = pointsRef.current[li].points;
    let controls = [];    

    if(operationRef.current === "bezier")
    {
      let control = controlRef.current[li];
      controls=control.controls;
    }

    let {rotatedPoints:mp, rotatedControls:mc} = rotateFunc(mirrorPoint1Ref.current, mirrorPoint2Ref.current, rotatePointRef.current, points, controls);

    let lo = pointsRef.current[li];
    let loCopy = Object.assign({}, lo);
    loCopy.points = mp;
    pointsRef.current.push(loCopy);

    if(operationRef.current === "bezier")
    {
      let co = controlRef.current[li];
      let coCopy = Object.assign({}, co);              
      coCopy.controls = mc;
      controlRef.current.push(coCopy);
    }
  }

  function resizeLine()
  {
    let li=segmentsRef.current[0].lineIndex;
    
    let points = pointsRef.current[li].points;
    let controls = [];    

    if(operationRef.current === "bezier")
    {
      let control = controlRef.current[li];
      controls=control.controls;
    }

    let {resizedPoints:mp, resizedControls:mc} = resizeFunc(mirrorPoint1Ref.current, mirrorPoint2Ref.current, rotatePointRef.current, points, controls);

    let lo = pointsRef.current[li];
    let loCopy = Object.assign({}, lo);
    loCopy.points = mp;
    pointsRef.current.push(loCopy);

    if(operationRef.current === "bezier")
    {
      let co = controlRef.current[li];
      let coCopy = Object.assign({}, co);              
      coCopy.controls = mc;
      controlRef.current.push(coCopy);
    }
  }



  function mirrorLine()
  {
    let li=segmentsRef.current[0].lineIndex;
    
    let points = pointsRef.current[li].points;
    let controls = [];    

    if(operationRef.current === "bezier")
    {
      let control = controlRef.current[li];
      controls=control.controls;
    }

    let {mirroredPoints:mp, mirroredControls:mc} = mirror(mirrorPoint1Ref.current, mirrorPoint2Ref.current, points, controls);

    let lo = pointsRef.current[li];
    let loCopy = Object.assign({}, lo);
    loCopy.points = mp;
    pointsRef.current.push(loCopy);

    if(operationRef.current === "bezier")
    {
      let co = controlRef.current[li];
      let coCopy = Object.assign({}, co);              
      coCopy.controls = mc;
      controlRef.current.push(coCopy);
    }
  }

  function joinLine(li, hi)
  {

    let pPoints =  [];
    let pcPoints = [];
    let cutPControl ={};

    let nPoints = [];
    let ncPoints = [];
    let cutNControl ={};

    const {points: pl, controls: cl} = copyLine(li,0);

    if(hi.previousIndex !==-1)
    {
      let {points: p, controls: c} = copyLine(hi.previousIndex,0);
      if(hi.prevOrientation !== "same")
      {
        if(p.length > 0)
        {
          p=p.reverse();
          c=c.reverse();

          for(let i=0; i<c.length; i++)
          {
            let tX=c[i].x1;
            let tY=c[i].y1;
      
            if(operationRef.current === "bezier")
            {
              c[i].x1 = c[i].x2;
              c[i].y1 = c[i].y2;
              c[i].x2 = tX;
              c[i].y2 = tY;
            }
          }    
    
          /*
          p.splice(p.length-1,1);          
          cutPControl = c[c.length-1];
          c.splice(c.length-1,1);
       
          cl[0].x1 = cutPControl.x1;
          cl[0].y1 = cutPControl.y1;

          pPoints = p;
          pcPoints = c;
          */
        }
      }
      if(p.length > 0)
      {
        p.splice(p.length-1,1);          
        pPoints = p;


        if(operationRef.current === "bezier")
        {
          cutPControl = c[c.length-1];
          c.splice(c.length-1,1);

          cl[0].x1 = cutPControl.x1;
          cl[0].y1 = cutPControl.y1;

          
          pcPoints = c;
        }
      }
    }

    if(hi.nextIndex !== -1)
    {
      let {points: p, controls: c} = copyLine(hi.nextIndex,0);
      if(hi.nextOrientation !== "same")
      {
        if(p.length > 0)
        {
          p=p.reverse();
          if(operationRef.current === "bezier")
          {
              c=c.reverse();
          }

          for(let i=0; i<c.length; i++)
          {
            let tX=c[i].x1;
            let tY=c[i].y1;
      
            if(operationRef.current === "bezier")
            {
              c[i].x1 = c[i].x2;
              c[i].y1 = c[i].y2;
              c[i].x2 = tX;
              c[i].y2 = tY;
            }
          }        
        }
      }
      if(p.length > 0)
      {
        p.splice(0,1);
        nPoints = p;

        if(operationRef.current === "bezier")
        {
          cutNControl = c[0];
          c.splice(0,1);

          cl[cl.length-1].x2 = cutNControl.x2;
          cl[cl.length-1].y2 = cutNControl.y2;

          // nPoints = p;
          ncPoints = c;
        }
      }
    }

    let cPoints = [];
    let ccPoints = [];
    
    if(pPoints.length > 0)
    {
      cPoints = cPoints.concat(pPoints);
      if(operationRef.current === "bezier")
      {
        ccPoints = ccPoints.concat(pcPoints);
      }
    }
    
    cPoints = cPoints.concat(pl);

    if(operationRef.current === "bezier")
    {
      ccPoints =ccPoints.concat(cl);
    }

    // console.log("First C");
    //console.log(JSON.stringify(cl[0]));
    
    if(nPoints.length > 0)
    {
      cPoints = cPoints.concat(nPoints);
      if(operationRef.current === "bezier")
      {
        ccPoints = ccPoints.concat(ncPoints);
      }
    }

    /*
    //delete after test
    cPoints = cPoints.reverse();
    ccPoints= ccPoints.reverse();
    for(let i=0; i<ccPoints.length; i++)
    {
      let tX=ccPoints[i].x1;
      let tY=ccPoints[i].y1;

      ccPoints[i].x1 = ccPoints[i].x2;
      ccPoints[i].y1 = ccPoints[i].y2;
      ccPoints[i].x2 = tX;
      ccPoints[i].y2 = tY;
    }    
    //delete above
    */

    let lo = pointsRef.current[li];
    let loCopy = Object.assign({}, lo);
    loCopy.points = cPoints;
    pointsRef.current.push(loCopy);

    if(operationRef.current === "bezier")
    {
      let co = controlRef.current[li];
      let coCopy = Object.assign({}, co);              
      coCopy.controls = ccPoints;
      controlRef.current.push(coCopy);
    }

    let lines = [];
    let controls = [];

    for(let i=0; i<pointsRef.current.length; i++)
    {
      if(i!== hi.previousIndex && 
          i !==hi.nextIndex && 
          i != li)
      {
        lines.push(pointsRef.current[i]);
        controls.push(controlRef.current[i]);
      }
    }
    return ({lines,controls});
  }

  function copyLine(li, offset)
  {

    let points = [];
    let controls = [];

    let line = pointsRef.current[li];

    let control = [];

    if(operationRef.current === "bezier")
    {
      control = controlRef.current[li];
    }

    for(let i=0; i< line.points.length; i++)
    {
      let offsetX = offset;
      let offsetY = offset;
      let xo = line.points[i].x+offsetX;
      let yo = line.points[i].y+offsetY;


      points.push({x:xo, y:yo});

      if(operationRef.current === "bezier")
      {
        let x1o = control.controls[i].x1+offsetX;
        let x2o = control.controls[i].x2+offsetX;
      
        let y1o = control.controls[i].y1+offsetY;
        let y2o = control.controls[i].y2+offsetY;

        controls.push({x1:x1o,y1:y1o, x2:x2o, y2:y2o});
      }
    }
    return {points,controls}
  }

  function selectedSegmentLine(index)
  {
    let s = [];
    try
    {
      let line = pointsRef.current[index].points;

      let controls = null;
      if(operationRef.current === 'bezier')
      {
        controls = controlRef.current[tabSelectRef.current].controls;
      }

      for(let p=0; p<line.length-1; p++)
      {
          const p1 = {x:line[p].x, y:line[p].y}
          const p2 = {x:line[p+1].x, y:line[p+1].y}

          let c1 = null;
          let c2 = null;

          if(operationRef.current === 'bezier')
          {
            c1 = {x1:controls[p].x1,y1:controls[p].y1, x2:controls[p].x2, y2:controls[p].y2};
            c2 = {x1:controls[p+1].x1, y1:controls[p+1].y1, x2:controls[p+1].x2,y2:controls[p+1].y2};
          }

          s.push({
            lineIndex: tabSelectRef.current,
            segmentIndex: p,
            p1,
            p2,
            c1,
            c2
          });      
      }
    }
    catch(err)
    {
      s=[];
    }

    return s;
  }

  function drawGrid(
    ctx,
    gridWidth,
    gridHeight,
    pageWidth,
    pageHeight,
    zoom = 1,
    offsetX = 0,
    offsetY = 0
  ) {


    if(isNaN(gridWidth) || isNaN(gridHeight))
    {
      return;
    }

    // Only recreate the cache if parameters change
    if (!gridCacheCanvas ||
        gridCacheCanvas.width !== pageWidth ||
        gridCacheCanvas.height !== pageHeight ||
        gridCacheCanvas.gridHeight !== gridHeight ||
        gridCacheCanvas.gridWidth !== gridWidth) {
      createGridCache(gridWidth, gridHeight, pageWidth, pageHeight);
      // console.log("Creating");
    }
  
    ctx.save();
    ctx.setTransform(zoom, 0, 0, zoom, offsetX * zoom, offsetY * zoom);
    ctx.drawImage(gridCacheCanvas, 0, 0);
    ctx.restore();
  }
    
  function drawArrow(ctx, r1, r2) {
    // Compute center of r1 and r2 in page coordinates

    const r1Center = {
      x: r1.x + r1.w / 2,
      y: r1.y + r1.h / 2
    };
  
    const r2Center = {
      x: r2.x + r2.w / 2,
      y: r2.y + r2.h / 2
    };
  
    // Convert to screen coordinates
    const p1 = pageToScreen(r1Center.x, r1Center.y, viewPortRef);
    const p2 = pageToScreen(r2Center.x, r2Center.y, viewPortRef);
      
    // Draw main line
    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
  
    // Draw arrowhead
    drawArrowhead(ctx, p1, p2);
  }

  
  function applyLineOp()
  {
    let options =  
    {
      operation: "none",
      fillColor: fillColor,
      strokeColor: strokeColor,
      strokeWidth: plineRef.current.stroke,
      allowStroke: plineRef.current.allowStroke,
      allowFill: plineRef.current.allowFill,
      filleMode: plineRef.current.fillMode,
      gradientColor1: plineRef.current.gradientColor1,
      gradientColor2: plineRef.current.gradientColor2,
      gradientAngle: plineRef.current.gradientAngle,
      alpha: plineRef.current.alpha
    }

    undoPlineStack.push(pointsRef.current);      
    applySegmentOptions(tempRectRef.current, options, pointsRef.current, segmentsRef);
    tempRectRef.current = null;
    tabSelectRef.current=0;

    undoSegmentsStack.clear();

    undoSegmentsStack.push(segmentsRef.current);
    segmentOpRef.current="lineop";
    setRefreshKey();
    draw();
  }

  function selectAll()
  {      
    selectedRef.current=[];
    for(let i=0; i<renderRef.current.length; i++)
    {
        const rect = renderRef.current[i];

        let {zoom} = viewPortRef.current;
        
        // let {x,y} = screenToPage(tempRectRef.current.x, tempRectRef.current.y, viewPortRef);
        let x = tempRectRef.current.x;
        let y = tempRectRef.current.y;
        let w = tempRectRef.current.w;
        let h = tempRectRef.current.h;

        if (
          rect.x >= x &&
          rect.y >= y &&
          rect.x + rect.w <= x + w &&
          rect.y + rect.h <= y + h
        ) 
        {

          let parent = parentsRef.current.get(rect);

          if(!parent)
          {
            selectedRef.current.push(rect);
          }
        }
    }

    onRectSelect(selectedRef.current, null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
  }

  function applyLineOpB()
  {

    let options =  
    {
      operation: "none",
      fillColor: fillColor,
      strokeColor: strokeColor,
      strokeWidth: plineRef.current.stroke,
      allowStroke: plineRef.current.allowStroke,
      allowFill: plineRef.current.allowFill,
      filleMode: plineRef.current.fillMode,
      gradientColor1: plineRef.current.gradientColor1,
      gradientColor2: plineRef.current.gradientColor2,
      gradientAngle: plineRef.current.gradientAngle,
      alpha: plineRef.current.alpha,
    }

    undoPlineStack.push(pointsRef.current);      
    undoControlStack.push(controlRef.current);
    applySegmentOptionsB(tempRectRef.current, options, pointsRef.current, segmentsRef, controlRef.current);
    tempRectRef.current = null;
    tabSelectRef.current=0;

    undoSegmentsStack.clear();    
    undoSegmentsStack.push(segmentsRef.current);

    segmentOpRef.current="lineop";
    setRefreshKey();
    draw();
  }




  function drawArrowhead(ctx, from, to) {
    const { zoom } = viewPortRef.current;
  
    const headLength = 20 * zoom; // more pronounced arrowhead
    const spread = Math.PI / 4; // wider angle (45 degrees)
    const angle = Math.atan2(to.y - from.y, to.x - from.x);
  
    ctx.beginPath();
    ctx.moveTo(to.x, to.y);
    ctx.lineTo(
      to.x - headLength * Math.cos(angle - spread),
      to.y - headLength * Math.sin(angle - spread)
    );
    ctx.lineTo(
      to.x - headLength * Math.cos(angle + spread),
      to.y - headLength * Math.sin(angle + spread)
    );
    ctx.lineTo(to.x, to.y);
    ctx.closePath();
    ctx.fill();
  }
  
  function drawInfo(ctx, rect) {
    const { zoom } = viewPortRef.current;
  
    // Get screen coords and size
    const { x, y } = pageToScreenN(rect.x, rect.y, viewPortRef);
    const w = rect.w; // * zoom;
    const h = rect.h; // * zoom;
  
    // Prepare text content
    let infoLines = [];

    let infoLinesR = [
        `Type: ${rect.type}`,
        `Z: ${rect.zIndex}`];

    if(rect.type==='Rectangle')
    {
      infoLinesR = [
        `SubType: ${rect.subType}`,
        `Z: ${rect.zIndex}`]

      if(rect.subType === "Image")        
      {        
        if(!rect?.imageName || rect?.imageName === "")
        {
          infoLinesR.push("**Select Image**");
        }
      }

      if(rect.subType === "Text")        
      {        
        if(!rect?.plainText || rect?.plainText === "")
        {
          infoLinesR.push("**Write Text**");
        }
      }

      if(rect.subType === "Custom")        
      {        
        if(!rect?.description || rect?.description === "")
        {
          infoLinesR.push("**Write Ai Prompt**");
        }
      }

      if(rect.subType === "Animate")        
      {        
        if(!rect?.description || rect?.description === "")
        {
          infoLinesR.push("**Pick motion path + object**");
        }
      }



    }
    

    infoLines = infoLinesR;

  
    // Set text style
    ctx.save();
    ctx.fillStyle = "black";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `${Math.max(10 * zoom, 6)}px Arial`; // scales with zoom, min 6px
  
    // Calculate line height (approx)
    const lineHeight = 12 * zoom;
  
    // Starting y position so text block is vertically centered
    const totalHeight = infoLines.length * lineHeight;
    let textY = y + h / 2 - totalHeight / 2 + lineHeight / 2;
  
    // Draw each line
    for (const line of infoLines) {
      ctx.fillText(line, x + w / 2, textY);
      textY += lineHeight;
    }
  
    ctx.restore();
  }
  

  const getMouse = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };



  function pushRectToFront() {
    historyStack.push(pageNum, rectsRef.current);
    const rects = rectsRef.current;
    const selected = selectedRef.current;

    if (selected.length === 0) return;

    // Find the index using reference comparison
    const index = rects.findIndex(r => r === selected[0]);

    if (index === -1) return;

    // Remove and re-insert at the last index
    const [removed] = rects.splice(index, 1);
    rects.push(removed);
    labelZ();
    draw();
    setRefreshKey();
    selectedRef.current[0] = removed;

    onRectSelect(removed, null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
}

  const hitTest = (x, y) => {
    const rects = rectsRef.current;
    for (let i = rects.length - 1; i >= 0; i--) {
      const r = rects[i];
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) 
      {
          rectTabSelectRef.current=i;
          return r;
       }

    }
    return null;
  };


  const hitResize = (x, y, selectedRect, handleSize = 20) => {
    if (!selectedRect) return false;
  
    const right = selectedRect.x + selectedRect.w;
    const bottom = selectedRect.y + selectedRect.h;
  
    // Check if the mouse is inside the resize handle zone
    if (
      x >= right - handleSize &&
      x <= right &&
      y >= bottom - handleSize &&
      y <= bottom
    ) {
      return true;
    }
  
    return false;
  };
  
  function addBezierPoint(xg, yg) {
    if (lineBeginRef.current === true) {
      // Start a new line (array of points)
      undoPlineStack.push(pointsRef.current);
      pointsRef.current.push({
        points: [],
        stroke: plineRef.current.stroke,
        strokeColor: plineRef.current.strokeColor,
        fill: false,
        fillColor: plineRef.current.fillColor,
        allowStroke: plineRef.current.allowStroke,
        allowFill: plineRef.current.allowFill,
        filleMode: plineRef.current.fillMode,
        gradientColor1: plineRef.current.gradientColor1,
        gradientColor2: plineRef.current.gradientColor2,
        gradientAngle: plineRef.current.gradientAngle,  
        alpha: plineRef.current.alpha,
        strokeWidth: plineRef.current.stroke
      });
  
      pointsRef.current.at(-1).points.push({ x: xg, y: yg });
  
      // Start matching new control line
      undoControlStack.push(controlRef.current);
      controlRef.current.push({ controls: [] });
  
      // First point: handles collapsed on the anchor
      controlRef.current.at(-1).controls.push({
        x1: xg, y1: yg, x2: xg, y2: yg
      });
  
      lineBeginRef.current = false;
      return;
    }
    
    const {nLines:l, nControls:c} = removeStray(pointsRef.current, controlRef.current, operationRef.current, true);
    pointsRef.current = l;
    controlRef.current = c;

    const pointArr = pointsRef.current.at(-1).points;
    const ctrlArr = controlRef.current.at(-1).controls;
  
    const prev = pointArr.at(-1);
  
    // Prevent adding the same point twice
    if (prev.x === xg && prev.y === yg) {
      return;
    }

    
    // push undo state (keep same behavior you had)
    undoPlineStack.push(pointsRef.current);
    pointArr.push({ x: xg, y: yg });
  
    undoControlStack.push(controlRef.current);
  
    // chord vector from prev -> new
    const dx = xg - prev.x;
    const dy = yg - prev.y;
    const len = Math.sqrt(dx * dx + dy * dy);
  

    // Fractions along the chord
    // CHANGED: Reduce handle fraction for very small segments
    // This prevents handles from becoming too long relative to the segment.
    const handleFrac = Math.min(1 / 3, len / 20);

    const outFrac = handleFrac;
    const inFrac = handleFrac;

  
    // Perpendicular unit vector (for offset). Guard against zero length.
    /*
    let nx = 0, ny = 0;
    if (len > 1e-6) {
      nx = -dy / len;
      ny = dx / len;
    }
    */

    let ux = 0, uy = 0;
    if (len > 1e-6) 
    {
        ux = dx / len;
        uy = dy / len;
    }
  

    // CHANGED: Offset now scales with segment length.
    // Previously this was fixed at 10, which caused short segments
    // to push handles beyond the anchor points.
    const maxOffset = 10;
    const minOffset = 1;

    const offset = Math.max(
        minOffset,
        Math.min(maxOffset, len * 0.15)
    );
    // Offset magnitude (you can tune or base it on len if you prefer)
    //const offset = 10;
  
    // Compute prev outgoing handle (1/3 from prev, plus perpendicular offset)
    // const prevOutX = prev.x + dx * outFrac + nx * offset;
    // const prevOutY = prev.y + dy * outFrac + ny * offset;

    const prevOutX = prev.x + dx * outFrac + ux * offset;
    const prevOutY = prev.y + dy * outFrac + uy * offset;
  
    // Write into previous control object's x2,y2.
    // Ensure a previous control object exists (it should for normal flow)
    if (!ctrlArr[ctrlArr.length - 1]) {
      // defensive: create a control object if missing
      ctrlArr[ctrlArr.length - 1] = { x1: prev.x, y1: prev.y, x2: prevOutX, y2: prevOutY };
    } else {
      ctrlArr[ctrlArr.length - 1].x2 = prevOutX;
      ctrlArr[ctrlArr.length - 1].y2 = prevOutY;
    }
  
    // Compute new incoming handle: place at new - (1/3)*(new-prev) (=> 2/3 from prev)
    // Mirror the perpendicular offset (opposite sign) for smoothness
    // const newInX = xg - dx * inFrac - nx * offset;
    // const newInY = yg - dy * inFrac - ny * offset;

    const newInX = xg - dx * inFrac - ux * offset;
    const newInY = yg - dy * inFrac - uy * offset;
  
    // New control for the new point: incoming = newIn, outgoing = anchor (collapsed)

    const newHandle = {
      x1: newInX,
      y1: newInY,
      x2: xg,
      y2: yg
    };

  
    ctrlArr.push(newHandle);
  
    // Debug
    console.log(
      `Added Control: prevOut(${prevOutX.toFixed(2)},${prevOutY.toFixed(2)}), newIn(${newInX.toFixed(2)},${newInY.toFixed(2)}) anchor(${xg},${yg})`
    );
  }
  
  function addPLinePoint(xg, yg) {
    // Start a new polyline if needed
    if (lineBeginRef.current === true) {
      undoPlineStack.push(pointsRef.current);
  
      pointsRef.current.push({
        points: [],
        stroke: plineRef.current.stroke,
        strokeColor: plineRef.current.strokeColor,
        fill: false,
        fillColor: plineRef.current.fillColor,
        allowStroke: plineRef.current.allowStroke,
        allowFill: plineRef.current.allowFill,
        filleMode: plineRef.current.fillMode,
        gradientColor1: plineRef.current.gradientColor1,
        gradientColor2: plineRef.current.gradientColor2,
        gradientAngle: plineRef.current.gradientAngle,  
        alpha: plineRef.current.alpha,
        strokeWidth: plineRef.current.stroke

      });
  
      // Add first point
      pointsRef.current.at(-1).points.push({ x: xg, y: yg });
  
      lineBeginRef.current = false;
      return;
    }

    
    const {nLines:l, nControls:c} = removeStray(pointsRef.current, controlRef.current, operationRef.current, true);
    pointsRef.current = l;
    controlRef.current = c;
      
    // Add point to existing polyline
    const pointArr = pointsRef.current.at(-1).points;
    if (!pointArr) return;
  
    const prev = pointArr.at(-1);
    if (prev && prev.x === xg && prev.y === yg) return;
  
    // Push undo before modifying
    undoPlineStack.push(pointsRef.current);
  
    // Append new vertex
    pointArr.push({ x: xg, y: yg });
  
    console.log(`Added PLine point: (${xg.toFixed(2)}, ${yg.toFixed(2)})`);
  }
  
  /*
  const hitTest = (x, y) => {
    // const { x, y } = screenToPage(screenX, screenY);
    
    return rectsRef.current.find(r => x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h);
  };
  */


  const undoBezierOperation = () =>
  {
    let undo = undoPlineStack.pop();
    if (undo && undo.length !== 0) {
      pointsRef.current = undo;
    } else {
      lineBeginRef.current = true;
      pointsRef.current = [];
    }
  
    let undoC = undoControlStack.pop();
    if (undoC && undoC.length !== 0) {
      controlRef.current = undoC;
    } else {
      controlRef.current = [];
    }
  }

  const undoPlineOperation = () =>
  {

    let undo = undoPlineStack.pop();
    if(undo != undefined && undo.length != 0)
    {            
        
      // pointsRef.current.length = 0;   
      // pointsRef.current.push(...undo);    
      pointsRef.current = undo;

    }
    else
    {
      lineBeginRef.current = true;
      // pointsRef.current.length = 0;
      // pointsRef.current.push([]);
      pointsRef.current = [];
    }  
    // console.log("Lines:", pointsRef.current);
    return;
  }

  const undoFreeHandOperation = () =>
  {
    lineBeginRef.current = false;

    console.log("Undo");
    for(let i = 0 ; i< 5; i++)
    {
      let undo = undoPlineStack.pop();
      if(undo != undefined && undo.length != 0)
      {            
        
        // pointsRef.current.length = 0;   
        // pointsRef.current.push(...undo);    
        pointsRef.current = undo;
      }
      else
      {
        lineBeginRef.current = true;
        // pointsRef.current.length = 0;
        // pointsRef.current.push([]);
        pointsRef.current = [];
      }
    }
  }

  const handleMouseDown = (e) => {    
    e.preventDefault();

    console.log("Mouse Down");

    const { x: xM, y: yM } =  getMouse(e);
    const { x, y } = screenToPage(xM, yM, viewPortRef);
  
    onCoordUpdate(x, y);
  
    // const now = Date.now();
    // const DOUBLE_CLICK_DELAY = 300;

    const isLeftClick = e.button === 0;
    const isMiddleClick = e.button === 1;
    const isRightClick = e.button === 2;    

    if(isMiddleClick === true)
    {

      console.log("select or middle clicked");
      draggingRef.current = true;
      mouseDownScreenRef.current = { x: xM, y: yM };
      draw();
      return;
    }
  

    if(isRightClick && (segmentOpRef.current === 'lineop' || 
                        operationRef.current === 'adjimage' || 
                          animateOpRef.current === 'selectMPath' || 
                          animateOpRef.current === 'selectMObj'  ||
                          animateOpRef.current === 'handleOffset' ||
                          animateOpRef.current === 'handleOffsetPath' ||
                          animateOpRef.current === 'resizeView'))
    {
      console.log("lineop Right click");
      onRectSelect([],e, false, segmentsRef.current, aSegmentRef.current,pointsRef.current);
      draw();
      return;
    }


    if(isRightClick && operationRef.current !=='pline' && 
                    operationRef.current !=='paste' && 
                    operationRef.current !== 'freehand' && 
                    operationRef.current !== 'bezier' && 
                    operationRef.current !== 'adjimage')
    {
      if(operationRef.current === 'pline')
      {
        setOperation('select');
      }

      if(onClipRef.current ===false)
      {      
        onRectSelect(selectedRef.current, e, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      }
      else
      {
          onRectSelect([],e, true, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      }
      draw();
      return;
    }
    

    const isShiftPressed = e.shiftKey; // Check if Shift is held
    const isCtrlKeyPressed = e.ctrlKey;

    if(animateOpRef.current != 'none')
    {

      if(e.key === 'Escape')
      {
        animateOpRef.current = 'none';
        draw();
        return;
      }

      if(animateOpRef.current ==='selectMPath' || 
        animateOpRef.current === 'selectMObj')
      {        
        const hit = hitTest(x, y);
        if (hit) 
        {
    
          if(animateOpRef.current === 'selectMPath')
          {
            if((hit.type === 'Bezier' && hit.path.length <= 1) || 
                (hit.type === 'Prect' && hit.path.length <= 1) ||  
                hit.subType === 'SvgRect' || 
                hit.subType === 'SvgSquare' ||
                hit.subType === 'SvgCircle' ||
                hit.subType === 'SvgEllipse' )
            {
              console.log("Inserting MPath");
              insertMPath(hit, selectedRef.current[0]);
            }              
          }
          if(animateOpRef.current === 'selectMObj')
          {
            if(hit.type === 'SvgText')
            {
              insertMObjText(hit,selectedRef.current[0]);  
            }
            else
            {
              insertMObj(hit,selectedRef.current[0]);
            }
          }
          
          onAnimateSelect(hit);          
          draw();
          return;      
        }
        else
        {
          if(animateOpRef.current === 'selectMPath')
          {
            insertMPath(null, selectedRef.current[0]);            
            onAnimateSelect(hit);          
            draw();
            return;

          }

          if(animateOpRef.current === 'selectMObj')
          {
            insertMObj(hit,selectedRef.current[0]);            
            onAnimateSelect(hit);          
            draw();
            return;

          }

        }
      }

      if(animateOpRef.current === 'handleOffset')
      {
        if(isRightClick || e.key === 'Escape')
        {
          setOperation('select');
          draw();
          return;
        }
  
        let r = selectedRef.current[0];    
        if(r.subType === 'Animate')
        {
          let m = r.mObj;
          if (x >= m.x && x <= m.x + m.w && y >= m.y && y <= m.y + m.h) 
          {
            console.log("Clicked on mObj");
  
            draggingRef.current = true;
            mouseDownPageRef.current = { x: x, y: y }; 
            viewPortRef.current.originalRect = JSON.parse(JSON.stringify(m));
          }
          else
          {
            setOperation('select');
            animateOpRef.current = 'none';
            onAnimateSelect(null);
          }        
        }          
        draw();
        return;  
      }

      if(animateOpRef.current === 'handleOffsetPath')
      {
        if(isRightClick || e.key === 'Escape')
        {
          setOperation('select');
          draw();
          return;
        }
  
        let r = selectedRef.current[0];    
        if(r.subType === 'Animate')
        {
          let m = r.mPath;

          if(m === undefined)
          {
            m = r.mObj;
          }

          if (x >= m.x && x <= m.x + m.w && y >= m.y && y <= m.y + m.h) 
          {
            console.log("Clicked on mObj");
  
            draggingRef.current = true;
            mouseDownPageRef.current = { x: x, y: y }; 
            viewPortRef.current.originalRect = JSON.parse(JSON.stringify(m));
          }
          else
          {
            setOperation('select');
            animateOpRef.current = 'none';
            onAnimateSelect(null);
          }        
        }          
        draw();
        return;  
      }

      if(animateOpRef.current === 'resizeView')
      {
        let r = selectedRef.current[0];    
        if(r.subType === 'Animate')
        {

            // Replace selection with just the hit item
            let hit = r;
            selectedRef.current = [hit];

            viewPortRef.current.originalWidth = hit.w;
            viewPortRef.current.originalHeight = hit.h;
            viewPortRef.current.originalPath = hit?.path;            
            viewPortRef.current.originalRect = JSON.parse(JSON.stringify(hit));
            viewPortRef.current.originalControl = hit?.control;
            viewPortRef.current.originalSvgPath = hit?.svgText;

            if(hitResize(x,y,hit))
            {
                viewPortRef.current.isResizing = true;
                setOperation('resize');
            }
            else
            {            
                viewPortRef.current.isResizing = false;
                setOperation('select');
                onAnimateSelect(null);          
            }

            /* to move selected box without the move command */
            historyStack.push(pageNum, rectsRef.current);
            draggingRef.current = true;
            mouseDownPageRef.current = { x: x, y: y };    
        }
        
        draw();
        return;
      }
  
    }


    if(operationRef.current === 'adjimage')
    {
      if(isRightClick || e.key === 'Escape')
      {
        setOperation('select');
        draw();
        return;
      }

      let r = selectedRef.current[0];    
      if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) 
      {
        console.log("Clicked on image");

        draggingRef.current = true;
        mouseDownPageRef.current = { x: x, y: y }; 
      }
      else
      {
        setOperation('select');
      }
      draw();
      return;
    }

    if(segmentOpRef.current === 'lineop')
    {      

        if(segmentActionRef.current === 'rotateLine')
        {
          const {width, height, snapToGrid} = plineRef.current;
          let xg = x;
          let yg = y;
          if (snapToGrid && !e.altKey  && !e.shiftKey) 
          {
            xg = snap(x,width);
            yg = snap(y, height);
          }
     
          if(e.key === 'Escape')
          {
            mirrorPoint1Ref.current = null;
            mirrorPoint2Ref.current = null;
            mirrorLineRef.current = null;
            mirrorControlRef.current = null;
            rotateLineRef.current = null;
            rotatePointRef.current = null;
            rotateControlRef.current = null;
            
            segmentsRef.current = [];
            aSegmentRef.current = null;
            segmentOpRef.current = 'none';
            segmentActionRef.current = 'none';
            canvasRef.current.style.cursor = 'crosshair';
            onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);

            draw();
            return;
          }


          if(mirrorPoint1Ref.current == null)
          {
            mirrorPoint1Ref.current = {x:xg, y:yg};
            
            // let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2010.000000%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2018.271174%2010.000000%20L%2018.271174%2017.730080%20L%2010.000000%2017.730080%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2026.458800%2010.390408%20L%2026.542347%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
            let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%207.168656%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%2015.439830%207.683889%20L%2015.439830%2015.413969%20L%207.168656%2015.413969%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2023.627456%208.074297%20L%2023.711003%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
            canvasRef.current.style.cursor=`url('${dataUrl}') 16 16, auto`;  
          }
          else
          {

            if(mirrorPoint2Ref.current === null)
            {
              mirrorPoint2Ref.current = {x:xg, y:yg};
            }
            else
            {
              rotateLine();

              console.log("Finalize and create mirror");
              mirrorPoint1Ref.current = null;
              mirrorPoint2Ref.current = null;
              mirrorLineRef.current = null;
              mirrorControlRef.current = null;
              rotatePointRef.current = null;
              rotateLineRef.current = null;
              rotateControlRef.current = null;
  
              segmentOpRef.current="none";
              segmentActionRef.current = 'none';

              segmentsRef.current = [];
              segmentOpRef.current = 'none';
              canvasRef.current.style.cursor = 'crosshair';
              onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
            }
  
          }
          draw();
          return;
        }

        if(segmentActionRef.current === 'resizeLine')
        {
          const {width, height, snapToGrid} = plineRef.current;
          let xg = x;
          let yg = y;
          if (snapToGrid && !e.altKey && !e.shiftKey) 
          {
            xg = snap(x,width);
            yg = snap(y, height);
          }
     
          if(e.key === 'Escape')
          {
            mirrorPoint1Ref.current = null;
            mirrorPoint2Ref.current = null;
            mirrorLineRef.current = null;
            mirrorControlRef.current = null;
            rotateLineRef.current = null;
            rotatePointRef.current = null;
            rotateControlRef.current = null;
            
            segmentsRef.current = [];
            aSegmentRef.current = null;
            segmentOpRef.current = 'none';
            segmentActionRef.current = 'none';
            canvasRef.current.style.cursor = 'crosshair';
            onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);

            draw();
            return;
          }


          if(mirrorPoint1Ref.current == null)
          {
            mirrorPoint1Ref.current = {x:xg, y:yg};
            
            // let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2010.000000%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2010.000000%2010.000000%20L%2018.271174%2010.000000%20L%2018.271174%2017.730080%20L%2010.000000%2017.730080%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2026.458800%2010.390408%20L%2026.542347%2025.460161%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%235112e2%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
            let dataUrl="data:image/svg+xml,%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2232%22%20height%3D%2232%22%20viewBox%3D%220%200%2032%2032%22%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%207.168656%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%207.168656%207.683889%20L%2015.439830%207.683889%20L%2015.439830%2015.413969%20L%207.168656%2015.413969%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3Cpath%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20d%3D%22M%2023.627456%208.074297%20L%2023.711003%2023.144050%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke%3D%22%23f00a0a%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20stroke-width%3D%222%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20fill%3D%22none%22%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%2F%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%20%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20%3C%2Fsvg%3E%0A%20%20%20%20%20%20%20%20%20%20%20%20%20%20"
            canvasRef.current.style.cursor=`url('${dataUrl}') 16 16, auto`;  
          }
          else
          {

            if(mirrorPoint2Ref.current === null)
            {
              mirrorPoint2Ref.current = {x:xg, y:yg};
            }
            else
            {
              resizeLine();

              console.log("Finalize and create mirror");
              mirrorPoint1Ref.current = null;
              mirrorPoint2Ref.current = null;
              mirrorLineRef.current = null;
              mirrorControlRef.current = null;
              rotatePointRef.current = null;
              rotateLineRef.current = null;
              rotateControlRef.current = null;
  
              segmentOpRef.current="none";
              segmentActionRef.current = 'none';

              segmentsRef.current = [];
              segmentOpRef.current = 'none';
              canvasRef.current.style.cursor = 'crosshair';
              onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
            }  
          }
          draw();
          return;
        }

        
        if(segmentActionRef.current === 'mirrorLine')
        {
          const {width, height, snapToGrid} = plineRef.current;
          let xg = x;
          let yg = y;
          if (snapToGrid && !e.altKey && !e.shiftKey) 
          {
            xg = snap(x,width);
            yg = snap(y, height);
          }
     
          if(e.key === 'Escape')
          {
            mirrorPoint1Ref.current = null;
            mirrorPoint2Ref.current = null;
            mirrorLineRef.current = null;
            mirrorControlRef.current = null;
            rotatePointRef.current = null;
            rotateLineRef.current = null;
            rotateControlRef.current = null;

            
            segmentsRef.current = [];
            aSegmentRef.current = null;
            segmentOpRef.current = 'none';
            segmentActionRef.current = 'none';
            canvasRef.current.style.cursor = 'crosshair';
            onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);

            draw();
            return;
          }

          if(mirrorPoint1Ref.current == null)
          {
            mirrorPoint1Ref.current = {x:xg, y:yg};
          }
          else
          {

            mirrorPoint2Ref.current = {x:xg, y:yg};
            mirrorLine();

            console.log("Finalize and create mirror");
            mirrorPoint1Ref.current = null;
            mirrorPoint2Ref.current = null;
            mirrorLineRef.current = null;
            rotatePointRef.current = null;
            rotateLineRef.current = null;
            rotateControlRef.current = null;
            mirrorControlRef.current = null;
            segmentOpRef.current="none";
            segmentActionRef.current = 'none';

            segmentsRef.current = [];
            segmentOpRef.current = 'none';
            canvasRef.current.style.cursor = 'crosshair';
            onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
  
          }

          draw();
          return;
        }

        if(segmentActionRef.current === 'moveAll' ||
           segmentActionRef.current === 'moveSelected' ||
           segmentActionRef.current === 'moveLine' ||
           segmentActionRef.current === 'moveSegment')
        {
          console.log("Initiating a move");
          undoPlineStack.push(pointsRef.current);   
          if(operationRef.current === 'bezier') 
          {
            undoControlStack.push(controlRef.current);
          }        
          undoSegmentsStack.push(segmentsRef.current);

          draggingRef.current = true;
          mouseDownPageRef.current = { x: x, y: y }; 
          canvasRef.current.style.cursor = 'move';
          draw();
          return;  
        }

        let prevSelect = aSegmentRef.current;

        let s = getSegmentAtPoint(x, y, segmentsRef.current, 5);

        /*
        aSegmentRef.current = null;

        if(s != null)
        {
          
          if(pointsRef.current[s.lineIndex].points.length > 2)
          {
            aSegmentRef.current = s;
          }
        }
        */

        aSegmentRef.current = s;

        //this is the ref in CreateTemplate.js
        pASegmentRef.current = s;

        let selected = false;
        if(aSegmentRef.current != null)
        {

          draggingRef.current = true;
          mouseDownPageRef.current = { x: x, y: y }; 
          // canvasRef.current.style.cursor = 'move';   
          segmentActionRef.current = 'moveSegment';       

          undoPlineStack.push(pointsRef.current);   
          if(operationRef.current === 'bezier') 
          {
            undoControlStack.push(controlRef.current);
          }        
          undoSegmentsStack.push(segmentsRef.current);


        }
        else
        {         
                    
          let r = getSegmentsRect(segmentsRef.current);

          if(r != null)
          {

            if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h) 
            {
              selected = true;
            }            
            else
            {
              selected = false;
            }

            if(selected) 
            {
              draggingRef.current = true;
              mouseDownPageRef.current = { x: x, y: y }; 
              // canvasRef.current.style.cursor = 'move';   

              let sameLine = isSameLine(segmentsRef.current);
              if(sameLine)
              {
                segmentActionRef.current = 'moveLine';       

                undoPlineStack.push(pointsRef.current);   
                if(operationRef.current === 'bezier') 
                {
                  undoControlStack.push(controlRef.current);
                }        
                undoSegmentsStack.push(segmentsRef.current);
      
              }
              else
              {

                // segmentActionRef.current = 'moveAll';       

                undoPlineStack.push(pointsRef.current);   
                if(operationRef.current === 'bezier') 
                {
                  undoControlStack.push(controlRef.current);
                }        
                undoSegmentsStack.push(segmentsRef.current);
      
              }
            }
          }
          
        }
        
        if(s === null && prevSelect === null && selected === false)
        {
          segmentsRef.current = [];
          segmentOpRef.current = 'none';
          onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
        }

        draw();
        return;
    }

    if(operationRef.current === 'paste')
    {
      setOperation("select");

      let cr = getClipRect(clipIndexRef.current);

      if(cr === undefined || cr === null)
      {
        return;
      }
       // Deep copy rect (handles nested objects/arrays)

      let newRect = undefined;
      if(cr.pasteRect)
      {
        newRect = JSON.parse(JSON.stringify(cr.pasteRect));
      }
      else
      {
        newRect = JSON.parse(JSON.stringify(cr));
      }

      const {x:xS, y:yS }=clipBoardToPage(newRect.x, newRect.y, viewPortRef, pageSizeRef);

      newRect.x = xS;
      newRect.y = yS;
      historyStack.push(pageNum, rectsRef.current);
      rectsRef.current.push(newRect);
      pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
      
      
      draw();

      let i=0;
      const timer = createLoop((stop) => {

        newRect.x = newRect.x - 100;

        if(newRect.mObj)
        {
          newRect.mObj.x = newRect.x - newRect.offsetViewX;
        }
        if(newRect.mPath)
        {
          newRect.mPath.x = newRect.x - newRect.offsetViewX;
        }
        
        if(newRect.x < pageWidth/2)
        {          
          stop();
        }
        draw();

      });

      console.log("starting animation");
      timer.start();

      clipIndexRef.current = -1;      
  
      return;
    }

    if(operationRef.current === 'freehand')
    {
      
      mouseDownScreenRef.current = { x: xM, y: yM };
     // mouseDownPageRef.current = { x: x, y: y }; 
     // mouseDownPageRef.current = { x: x, y: y };    

     const {width, height, snapToGrid} = plineRef.current;

     let xg = x;
     let yg = y;
     /*
     if (snapToGrid && !e.altKey) 
     {
       xg = snap(x,width);
       yg = snap(y, height);
     }
     */

     if(lineBeginRef.current === true)
     {
         // Start a new line (array of points)
         undoPlineStack.push(pointsRef.current);
         pointsRef.current.push({points:[], 
                                stroke:plineRef.current.stroke, 
                                strokeColor:plineRef.current.strokeColor, 
                                fill:false, 
                                fillColor: plineRef.current.fillColor,
                                allowStroke: plineRef.current.allowStroke,
                                allowFill: plineRef.current.allowFill,
                                fillMode: plineRef.current.fillMode,
                                gradientColor1: plineRef.current.gradientColor1,
                                gradientColor2: plineRef.current.gradientColor2,
                                gradientAngle: plineRef.current.angle,                          
                                alpha: plineRef.current.alpha,
                                strokeWidth: plineRef.current.stroke
                        }); // Push empty line
         // Push the first point to the new line
         pointsRef.current[pointsRef.current.length - 1].points.push({ x: xg, y: yg });
         // pointsRef.current.push({x:xg,y:yg})      
         lineBeginRef.current=false;
     }

      isDrawingRef.current = true;
      draggingRef.current = true;
          
      if(isRightClick)
      {    

        undoFreeHandOperation();
        /*
        lineBeginRef.current = false;

        console.log("Undo");
        for(let i = 0 ; i< 5; i++)
        {
          let undo = undoPlineStack.pop();
          if(undo != undefined && undo.length != 0)
          {            
            
            // pointsRef.current.length = 0;   
            // pointsRef.current.push(...undo);    
            pointsRef.current = undo;
          }
          else
          {
            lineBeginRef.current = true;
            // pointsRef.current.length = 0;
            // pointsRef.current.push([]);
            pointsRef.current = [];
          }
        }
        */
      }

      draw();
      // console.log("Lines:", pointsRef.current);
      return;
    }

    if(isCtrlKeyPressed === true /*&& (operationRef.current === "bezier" || operationRef.current === 'pline')*/)
    {
      draggingRef.current = true;
      mouseDownPageRef.current = { x: x, y: y }; 

      tempRectRef.current={x:x, y:y, w:10, h:10};      
      return;

    }


    if(operationRef.current === 'pline')
    {
      if (e.key === 'Escape') 
      {  
        lineBeginRef.current = true;
      }

      if(isLeftClick)
      {
        const {width, height, snapToGrid} = plineRef.current;

        let xg = x;
        let yg = y;
        
        if (snapToGrid  && !e.altKey && !e.shiftKey) 
        {
          xg = snap(x,width);
          yg = snap(y, height);
        }
        

        const pPoint = pHit(x, y, pointsRef.current, viewPortRef.current.zoom);
        const {showEndPoints} = plineRef.current;

        if(pPoint != null && showEndPoints === true)
        {        
          console.log(`hit point: ${pPoint.x}, ${pPoint.y}`);
          console.log("select or middle clicked");
          draggingRef.current = true;
          mouseDownScreenRef.current = { x: xM, y: yM };
          pointDragRef.current = pPoint;
          pointDownRef.current = {x:xg, y:yg};
          undoPlineStack.push(pointsRef.current);    
          undoControlStack.push(controlRef.current);  
          // undoPlineStack.push(pointsRef.current);      handleMouseDown
          // draw();
          return;
        }
        else
        {
          pointDragRef.current = null;
          pointDownRef.current = null;
          controlDragRef.current = null;
          draggingRef.current = false;
        }

        addPLinePoint(xg,yg);        
      } 
      else
      {
       
        if(isRightClick)
        {                    
          undoPlineOperation();
          /*
          let undo = undoPlineStack.pop();
          if(undo != undefined && undo.length != 0)
          {            
            
            // pointsRef.current.length = 0;   
            // pointsRef.current.push(...undo);    
            pointsRef.current = undo;
          }
          else
          {
            lineBeginRef.current = true;
            // pointsRef.current.length = 0;
            // pointsRef.current.push([]);
            pointsRef.current = [];
          }
          */
        }
 
      }
      draw();
      // console.log("Lines:", pointsRef.current);
      return;
    }


    if(operationRef.current === 'bezier')
    {

      if (e.key === 'Escape') 
      {
        lineBeginRef.current = true;
      }

      if (isLeftClick) {
        const { width, height, snapToGrid } = plineRef.current;
      
        let xg = x;
        let yg = y;
        if (snapToGrid && !e.altKey && !e.shiftKey) {
          xg = snap(x, width);
          yg = snap(y, height);
        }
      
        
        const cPoint = bezierCHit(x, y, controlRef.current, viewPortRef.current.zoom);
        const {showControls} = plineRef.current;

        if(cPoint != null && showControls === true)
        {

          console.log(`hit Control point: ${cPoint.loc},`)
          draggingRef.current = true;
          // pointDragRef.current = {x:xg, y:yg}
          pointDragRef.current = null;
          controlDragRef.current = cPoint;
          undoPlineStack.push(pointsRef.current);    
          undoControlStack.push(controlRef.current);  
          return;
        }
        else
        {
          pointDragRef.current = null;
          pointDownRef.current = null;
          controlDragRef.current = null;
          draggingRef.current = false;

        }

        const pPoint = bezierPHit(x, y, pointsRef.current, viewPortRef.current.zoom);
        const {showEndPoints} = plineRef.current;

        if(pPoint != null && showEndPoints === true)
        {
          console.log(`hit point: ${pPoint.x}, ${pPoint.y}`)
          console.log("select or middle clicked");
          draggingRef.current = true;
          mouseDownScreenRef.current = { x: xM, y: yM };
          pointDragRef.current = pPoint;
          pointDownRef.current = {x:xg, y:yg};
          undoPlineStack.push(pointsRef.current);    
          undoControlStack.push(controlRef.current);  
          // undoPlineStack.push(pointsRef.current);      handleMouseDown
          // draw();
          return;
        }
        else
        {
          pointDragRef.current = null;
          pointDownRef.current = null;
          controlDragRef.current = null;
          draggingRef.current = false;
        }

        addBezierPoint(xg, yg);
      
      } else if (isRightClick) {
      
        undoBezierOperation();
        /*
        let undo = undoPlineStack.pop();
        if (undo && undo.length !== 0) {
          pointsRef.current = undo;
        } else {
          lineBeginRef.current = true;
          pointsRef.current = [];
        }
      
        let undoC = undoControlStack.pop();
        if (undoC && undoC.length !== 0) {
          controlRef.current = undoC;
        } else {
          controlRef.current = [];
        }
        */
      }
      
      draw();
      return;
      
    }
  
    if (operationRef.current === 'rect' ||  
          operationRef.current === 'image' || 
          operationRef.current === 'airect' || 
          operationRef.current === 'aiart' || 
          operationRef.current === 'animate' || 
          operationRef.current === 'menubar' || 
          operationRef.current === 'svgtext' ||
          operationRef.current === 'svgemoji' ||
          operationRef.current === 'svgcircle' ||
          operationRef.current === 'svgsquare' ||
          operationRef.current === 'svgrect' ||
          operationRef.current === 'svgellipse') {
      const {width, height, snapToGrid} = plineRef.current;
      let xg = x;
      let yg = y;
      if (snapToGrid) 
      {
        xg = snap(x,width);
        yg = snap(y, height);
      }

      let newRect = null;

      if(operationRef.current == 'menubar')
      {
        newRect = createNewRect(xg, yg,"Menubar"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
      }
      
      if(operationRef.current === 'rect')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.plainText = "";
        newRect.richText = "";
      }

      if(operationRef.current === 'image')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "Image";
      }

      if(operationRef.current === 'airect')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "Custom";
        newRect.aiCode = "";
      }

      if(operationRef.current === 'aiart')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "AiArt";
      }

      if(operationRef.current === 'animate')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "Animate";

      }

      if(operationRef.current === 'svgcircle')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "SvgCircle";
      }

      if(operationRef.current === 'svgsquare')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "SvgSquare";
      }

      if(operationRef.current === 'svgrect')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "SvgRect";
      }



      if(operationRef.current === 'svgellipse')
      {
        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = "SvgEllipse";
      }




      if( operationRef.current === 'svgtext')
      {
        newRect = createNewRect(xg, yg,"SvgText"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.allowStroke = false;
        newRect.allowFill = true;

        newRect.fontUrl = fontTypeRef.current

        adjustText(newRect, "Default Text", false);
        draw();

        /*
        let fontUrl = `/fonts/static/${fontTypeRef.current}`;
        newRect.fontUrl = fontUrl;
        newRect.fontText = "Default Text"

       
        let fontSize = 16;
        const opts = {x:0, y:0, fontSize:fontSize,rotation:0}
        textToSvgPath(newRect.fontText, newRect.fontUrl, opts, newRect, ({ path, svgPath, rotation, rect, descent, maxWidth, inRect}) => {
            inRect.svgText = svgPath;
            inRect.rotation = rotation;
            // newRect.y = newRect.y;     
            inRect.y = inRect.y;       
            inRect.w = rect.w +  maxWidth ;
            inRect.h = fontSize + descent + 5;
            inRect.originalSvgPath = svgPath;
            inRect.originalWidth = inRect.w;
            inRect.originalHeight = inRect.h;
            
            console.log("Height: ", rect.h);

            viewPortRef.current.originalWidth = inRect.w;
            viewPortRef.current.originalHeight = inRect.h;
            viewPortRef.current.originalSvgPath = svgPath;

            // console.log("svgText: ", rect.svgText);
            draw(); 
        });
        */
      }

      if( operationRef.current === 'svgemoji')
      {

        newRect = createNewRect(xg, yg,"Rectangle"); // { x, y, w: 100, h: 60, id: '', description: '', zIndex: -1 };
        newRect.subType = Date.now().toString();

        const loadBezier = async (inRect) =>
        {

          const index = rectsRef.current.indexOf(inRect);

          if (index !== -1) 
          {
            rectsRef.current.splice(index, 1);
          }

          let t = JSON.parse(JSON.stringify(inRect));
          t.localPath = null;
          t.localControlPath = null;
          t.fillColor = plineRef.current.strokeColor;
          t.strokeWidth = plineRef.current.stroke;
          
          let emoji = "1F60A";
          let svgUrl = `https://myaiweb.ca/openmoji/color/svg/${emoji}.svg`;
  
          let r = await svgFileToBezier(t,false, svgUrl);


          if(r != null)
          {
    
            historyStack.push(pageNum, rectsRef.current);
            rectsRef.current.push(r);
            pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
            labelZ();
          }    
          selectedRef.current = [r];
          onRectSelect(selectedRef.current, e, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
          setOperation('select');
          draw();
        }


        historyStack.push(pageNum, rectsRef.current);
        rectsRef.current.push(newRect);
        pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
        labelZ();
  

        // don't want the gui to stall so this is an async defined above
        loadBezier(newRect);
                
        // adjustSvg(newRect, "1F60A", false);
        draw();
  
        onRectSelect(selectedRef.current, e, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
        setOperation('select');
    
        return;
      }

      historyStack.push(pageNum, rectsRef.current);
      rectsRef.current.push(newRect);
      pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
      labelZ();
  
      if (isShiftPressed) {
        selectedRef.current.push(newRect);
      } else {
        selectedRef.current = [newRect];
      }
  

      onRectSelect(selectedRef.current, e, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      setOperation('select');
      draw();
  
    } else if (operationRef.current === 'move' || operationRef.current === 'resize') {
      // If operation is 'move' or 'resize', allow for dragging
      if (selectedRef.current.length > 0) {
        historyStack.push(pageNum, rectsRef.current);
        draggingRef.current = true;
        mouseDownPageRef.current = { x: x, y: y }; 
        if(operationRef.current==='resize')
        {

          const rects = rectsRef.current;
          const hit = rects[rectTabSelectRef.current];
              
          viewPortRef.current.originalWidth = hit.w;
          viewPortRef.current.originalHeight = hit.h;
          viewPortRef.current.originalPath = hit?.path;            
          viewPortRef.current.originalRect = JSON.parse(JSON.stringify(hit));
          viewPortRef.current.originalControl = hit?.control;
          viewPortRef.current.originalSvgPath = hit?.svgText;
          if(hit?.type === "Group")
          {
            viewPortRef.current.originalGroup = JSON.stringify(hit);
          }

          viewPortRef.current.isResizing = true;
        }
        // const firstRect = selectedRef.current[0];
        // offsetRef.current = { x: x - firstRect.x, y: y - firstRect.y };
      }
    } else {
      let hit = hitTest(x, y);


      if (hit) 
      {
        
        onClipRef.current = false;
        if (isShiftPressed) {
          /* to move selected box without the move command */
          historyStack.push(pageNum, rectsRef.current);
          draggingRef.current = true;
          mouseDownPageRef.current = { x: x, y: y }; 
          
          // Add to selection if Shift is held
          if (!selectedRef.current.includes(hit)) {

            selectedRef.current.push(hit);
          }
        } else {

          const {width, height, snapToGrid} = plineRef.current;
          
          if (snapToGrid) 
          {
            
            const xg = snap(hit.x,width);
            const yg = snap(hit.y, height);

            /*
            if(hit.type !== "Group")
            {
              hit.x = xg;
              hit.y = yg;
            }
            */
          }

            // Replace selection with just the hit item
            selectedRef.current = [hit];

            viewPortRef.current.originalWidth = hit.w;
            viewPortRef.current.originalHeight = hit.h;
            viewPortRef.current.originalPath = hit?.path;            
            viewPortRef.current.originalRect = JSON.parse(JSON.stringify(hit));
            viewPortRef.current.originalControl = hit?.control;
            viewPortRef.current.originalSvgPath = hit?.svgText;
            if(hit?.type === "Group")
            {
              viewPortRef.current.originalGroup = JSON.stringify(hit);
            }

            if(hitResize(x,y,hit))
            {
                viewPortRef.current.isResizing = true;
                setOperation('resize');
            }
            else
            {            
                viewPortRef.current.isResizing = false;
                setOperation('select');
            }

            /* to move selected box without the move command */
            historyStack.push(pageNum, rectsRef.current);
            draggingRef.current = true;
            mouseDownPageRef.current = { x: x, y: y };    
        }
  
        onRectSelect(selectedRef.current, e, false, segmentsRef.current, aSegmentRef.current,pointsRef.current);
        draw();
      }       
      else 
      {
        /*Mouse move*/
        draggingRef.current = false;

        const {x,y} = getMouse(e);
        if(clipBoardRef.current===true && clipBoardHit(x,y, viewPortRef, pageSizeRef) == true)
        {
          onClipRef.current = true;
          selectedRef.current = [];
          onRectSelect([], e, true, segmentsRef.current, aSegmentRef.current,pointsRef.current);
          draw();

        }
        else
        {  
          // Deselect all if nothing is hit
          onClipRef.current = false;
          selectedRef.current = [];
  
          onRectSelect([], e, false, segmentsRef.current, aSegmentRef.current,pointsRef.current);
          setOperation('select');
        }

        onRectSelect([], e, false, segmentsRef.current, aSegmentRef.current,pointsRef.current);
      }
  
      if (operationRef.current === 'select') {
        // console.log("select or middle clicked");
        draggingRef.current = true;
        mouseDownScreenRef.current = { x: xM, y: yM };
      }
  
      draw();
    }
  };
  
  
  const handleMouseMove = (e) => {
    const { x, y } = getMouse(e);
    const { x: xP, y: yP } = screenToPage(x, y, viewPortRef);


    const isCtrlKeyPressed = e.ctrlKey;
    if(isCtrlKeyPressed && tempRectRef.current != null)
    {
      // const dx = xP - mouseDownPageRef.current.x;
      // const dy = yP - mouseDownPageRef.current.y;
                
      const newWidth = xP - tempRectRef.current.x;
      const newHeight = yP - tempRectRef.current.y;
      if(newWidth > 10 && newHeight > 10)
      {
        tempRectRef.current.w = newWidth;
        tempRectRef.current.h = newHeight;        
      }
      // Update drag origin to new point
      mouseDownPageRef.current = { x: xP, y: yP };
      draw();
      return;
    }
    
    if(segmentOpRef.current === 'lineop' && !(e.buttons & 4))
    {

      if(segmentActionRef.current === 'rotateLine')
      {
        const {width, height, snapToGrid} = plineRef.current;
        let xg = xP;
        let yg = yP;
        /*
        if (snapToGrid && !e.altKey) 
        {
          xg = snap(x,width);
          yg = snap(y, height);
        }
        */

        if(mirrorPoint1Ref.current != null && mirrorPoint2Ref.current !=null)
        {
          rotatePointRef.current = {x:xg,y:yg};   
          canvasRef.current.style.cursor='pointer';  

          let li=segmentsRef.current[0].lineIndex;
    
          let points = pointsRef.current[li].points;
          let controls = [];    
      
          if(operationRef.current === "bezier")
          {
            let control = controlRef.current[li];
            controls=control.controls;
          }
      
          let {rotatedPoints:rp,rotatedControls:rc} = rotateFunc(mirrorPoint1Ref.current, mirrorPoint2Ref.current, rotatePointRef.current, points, controls);
      
          rotateLineRef.current = rp;
          rotateControlRef.current = rc;


        }
        draw();
        return;
      }

      if(segmentActionRef.current === 'resizeLine')
      {
        const {width, height, snapToGrid} = plineRef.current;
        let xg = xP;
        let yg = yP;
        /*
        if (snapToGrid && !e.altKey) 
        {
          xg = snap(x,width);
          yg = snap(y, height);
        }
        */

        if(mirrorPoint1Ref.current != null && mirrorPoint2Ref.current !=null)
        {
          rotatePointRef.current = {x:xg,y:yg};   
          canvasRef.current.style.cursor='pointer';  

          let li=segmentsRef.current[0].lineIndex;
    
          let points = pointsRef.current[li].points;
          let controls = [];    
      
          if(operationRef.current === "bezier")
          {
            let control = controlRef.current[li];
            controls=control.controls;
          }
      
          let {resizedPoints:rp,resizedControls:rc} = resizeFunc(mirrorPoint1Ref.current, mirrorPoint2Ref.current, rotatePointRef.current, points, controls);
      
          rotateLineRef.current = rp;
          rotateControlRef.current = rc;


        }
        draw();
        return;
      }
      
      if(segmentActionRef.current === 'mirrorLine')
      {
        const {width, height, snapToGrid} = plineRef.current;
        let xg = xP;
        let yg = yP;
        /*
        if (snapToGrid && !e.altKey) 
        {
          xg = snap(x,width);
          yg = snap(y, height);
        }
        */

        if(mirrorPoint1Ref.current != null)
        {
          mirrorPoint2Ref.current = {x:xg,y:yg};   
          canvasRef.current.style.cursor='pointer';  

          let li=segmentsRef.current[0].lineIndex;
    
          let points = pointsRef.current[li].points;
          let controls = [];    
      
          if(operationRef.current === "bezier")
          {
            let control = controlRef.current[li];
            controls=control.controls;
          }
      
          let {mirroredPoints:mp, mirroredControls:mc} = mirror(mirrorPoint1Ref.current, mirrorPoint2Ref.current, points, controls);
      
          mirrorLineRef.current = mp;
          mirrorControlRef.current = mc;


        }
        draw();
        return;
      }

      if(draggingRef.current === true)
      {

        console.log("segmentActionRef ", segmentActionRef.current);
        const dx = xP - mouseDownPageRef.current.x;
        const dy = yP - mouseDownPageRef.current.y;

        if(segmentActionRef.current === 'moveSegment')
        {

          canvasRef.current.style.cursor = 'move';          
          segmentActionRef.current = 'moveSegment';

          // break line first
          let s = aSegmentRef.current;

          if(s != null)
          {
              s.p1.x += dx;
              s.p1.y += dy;
              s.p2.x += dx;
              s.p2.y += dy;
              
              let li = s.lineIndex;
              let si = s.segmentIndex;

              const lines = pointsRef?.current;
              let line = lines[li];

              let p1 = line.points[si];
              let p2 =  line.points[si+1];

              
              p1.x = s.p1.x;
              p1.y = s.p1.y;
              p2.x = s.p2.x;
              p2.y = s.p2.y;

              if(operationRef.current === 'bezier')
              {
                const controls = controlRef?.current;
                let control = controls[li];
                let c1 = control.controls[si];
                let c2 = control.controls[si+1];

                c1.x1 += dx;
                c1.x2 += dx;
                c2.x1 += dx;
                c2.x2 += dx;

                c1.y1 += dy;
                c1.y2 += dy;
                c2.y1 += dy;
                c2.y2 += dy;

              }

              // The underlying points from the segmentsRef.current have been moved, now adjust this array to match.
              for (const segment of segmentsRef.current)
              {

                let li = segment.lineIndex;
                let si = segment.segmentIndex;
  
                const lines = pointsRef?.current;
                let line = lines[li];
  
                let p1 = line.points[si];
                let p2 =  line.points[si+1];
  
                
                segment.p1.x = p1.x;
                segment.p1.y = p1.y;
                segment.p2.x = p2.x;
                segment.p2.y = p2.y;  

                if(operationRef.current === 'bezier')
                {
                  const controls = controlRef?.current;
                  let control = controls[li];
                  let c1 = control.controls[si];
                  let c2 = control.controls[si+1];
  
                  segment.c1.x1 = c1.x1;
                  segment.c1.x2 = c1.x2;
                  segment.c2.x1 = c2.x1;
                  segment.c2.x2 = c2.x2;

                  segment.c1.y1 = c1.y1;
                  segment.c1.y2 = c1.y2;
                  segment.c2.y1 = c2.y1;
                  segment.c2.y2 = c2.y2;

                }
              }
  
              if(operationRef.current==="bezier")
              {
                s.c1.x1 += dx;
                s.c1.x2 += dx;
                s.c2.x1 += dx;
                s.c2.x2 += dx;

                s.c1.y1 += dy;
                s.c1.y2 += dy;
                s.c2.y1 += dy;
                s.c2.y2 += dy;

              }
            }
        }


        if(segmentActionRef.current === 'moveLine')
        {

          canvasRef.current.style.cursor = 'move';          
          segmentActionRef.current = 'moveLine';

          // break line first
          let s = segmentsRef.current;

          if(s.length > 0)
          {
            const lines = pointsRef?.current;
            let li = s[0].lineIndex;
            let line = lines[li];

            for (const point of line.points) 
            {
              if (!point || typeof point.x !== "number" || typeof point.y !== "number") {
                console.warn("Invalid point object:", point);
                continue;                
              }

              point.x += dx;
              point.y += dy;              
            }
            
            
            for (const segment of segmentsRef.current)
            {
              segment.p1.x +=dx;
              segment.p1.y +=dy;
              segment.p2.x +=dx;
              segment.p2.y +=dy;

              if(operationRef.current === 'bezier')
              {
                segment.c1.x1 += dx;
                segment.c1.y1 += dy;
                segment.c1.x2 += dx;
                segment.c1.y2 += dy;
  
                segment.c2.x1 += dx;
                segment.c2.y1 += dy;
                segment.c2.x2 += dx;
                segment.c2.y2 += dy;
  
              }  
            }
            
            if(operationRef.current === "bezier")
            {
              const controls = controlRef?.current;
              let li = s[0].lineIndex;
              let control = controls[li];
  
              for (const points of control.controls) {
                if (!points || typeof points.x1 !== "number" || typeof points.y1 !== "number" ||
                               typeof points.x2 !== "number" || typeof points.y2 !== "number") {
                  console.warn("Invalid control poits object:", points);
                  continue;                
                }
  
                points.x1 += dx;
                points.y1 += dy;
                points.x2 += dx;
                points.y2 += dy;              
              }
            }

          }
        }


        if(segmentActionRef.current === 'moveAll')
        {
          const lines = pointsRef?.current;

          canvasRef.current.style.cursor = 'move';          
          segmentActionRef.current = 'moveAll';
          
          // Move all points
          for (const line of lines) 
          {  
            for (const point of line.points) {
              if (!point || typeof point.x !== "number" || typeof point.y !== "number") {
                console.warn("Invalid point object:", point);
                continue;                
              }

              point.x += dx;
              point.y += dy;
              
            }
          }  

          
          for (const segment of segmentsRef.current)
          {
            segment.p1.x +=dx;
            segment.p1.y +=dy;
            segment.p2.x +=dx;
            segment.p2.y +=dy;

            if(operationRef.current === 'bezier')
            {
              segment.c1.x1 += dx;
              segment.c1.y1 += dy;
              segment.c1.x2 += dx;
              segment.c1.y2 += dy;

              segment.c2.x1 += dx;
              segment.c2.y1 += dy;
              segment.c2.x2 += dx;
              segment.c2.y2 += dy;
            }
          }
        
          
          if(operationRef.current === "bezier")
          {
            const controls = controlRef?.current;

                      // Move all points
            for (const control of controls) {  
              for (const points of control.controls) {
                if (!points || typeof points.x1 !== "number" || typeof points.y1 !== "number" ||
                                typeof points.x2 !== "number" || typeof points.y2 !== "number") {
                  console.warn("Invalid control points object:", points);
                  continue;                
                }

                points.x1 += dx;
                points.y1 += dy;
                points.x2 += dx;
                points.y2 += dy;              
              }
            }  
          }
        }

        if (segmentActionRef.current === 'moveSelected')
        {
            const lines = pointsRef?.current;

            canvasRef.current.style.cursor = 'move';
            segmentActionRef.current = 'moveSelected';

            // ---------------------------------------------------------
            // Find which lines have at least one selected segment
            // ---------------------------------------------------------

            const selectedLineIndexes = new Set();

            for (const segment of segmentsRef.current)
            {
                if (segment.lineIndex !== undefined)
                {
                    selectedLineIndexes.add(segment.lineIndex);
                }
            }

            // ---------------------------------------------------------
            // Move all points belonging to selected lines
            // ---------------------------------------------------------

            for (const lineIndex of selectedLineIndexes)
            {
                const line = lines[lineIndex];

                if (!line || !Array.isArray(line.points))
                {
                    continue;
                }

                for (const point of line.points)
                {
                    if (!point ||
                        typeof point.x !== "number" ||
                        typeof point.y !== "number")
                    {
                        console.warn("Invalid point object:", point);
                        continue;
                    }

                    point.x += dx;
                    point.y += dy;
                }
            }

            // ---------------------------------------------------------
            // Move all controls belonging to selected lines
            // ---------------------------------------------------------

            if (operationRef.current === "bezier")
            {
                const controls = controlRef?.current;

                for (const lineIndex of selectedLineIndexes)
                {
                    const control = controls[lineIndex];

                    if (!control || !Array.isArray(control.controls))
                    {
                        continue;
                    }

                    for (const points of control.controls)
                    {
                        if (!points ||
                            typeof points.x1 !== "number" ||
                            typeof points.y1 !== "number" ||
                            typeof points.x2 !== "number" ||
                            typeof points.y2 !== "number")
                        {
                            console.warn("Invalid control points object:", points);
                            continue;
                        }

                        points.x1 += dx;
                        points.y1 += dy;
                        points.x2 += dx;
                        points.y2 += dy;
                    }
                }
            }

            // ---------------------------------------------------------
            // ALSO move the selected segment objects
            //
            // These may be references/copies used by the selection
            // system, so keep them synchronized with the actual
            // geometry.
            // ---------------------------------------------------------

            for (const segment of segmentsRef.current)
            {
                if (segment.p1)
                {
                    segment.p1.x += dx;
                    segment.p1.y += dy;
                }

                if (segment.p2)
                {
                    segment.p2.x += dx;
                    segment.p2.y += dy;
                }

                if (operationRef.current === 'bezier')
                {
                    if (segment.c1)
                    {
                        segment.c1.x1 += dx;
                        segment.c1.y1 += dy;
                        segment.c1.x2 += dx;
                        segment.c1.y2 += dy;
                    }

                    if (segment.c2)
                    {
                        segment.c2.x1 += dx;
                        segment.c2.y1 += dy;
                        segment.c2.x2 += dx;
                        segment.c2.y2 += dy;
                    }
                }
            }
        }

        // Update drag origin to new point
        mouseDownPageRef.current = { x: xP, y: yP };
        draw();
        
      }
      return;
    }


    if(operationRef.current === 'freehand' && !(e.buttons & 4))
    {

      
      if(!isDrawingRef.current)
      {
        return;
      }

      if(e.buttons===1)
      {
        const {width, height, snapToGrid} = plineRef.current;

        let xg = x;
        let yg = y;

        const { x: xP, y: yP } = screenToPage(xg, yg, viewPortRef);
        /*
        if (snapToGrid) 
        {
          xg = snap(x,width);
          yg = snap(y, height);
        }
        */

        const dx = xP -  0; // mouseDownPageRef.current.x;
        const dy = yP - 0; // mouseDownPageRef.current.y;

        // const dx = xP - mouseDownScreenRef.current.x;
        // const dy = yP - mouseDownScreenRef.current.y;


        let currTime = performance.now();
        let elapsedTime = currTime - lastMoveTimeRef.current;
        lastMoveTimeRef.current = currTime;        
  
        if(lineBeginRef.current === true)
        {
            // Start a new line (array of points)
            undoPlineStack.push(pointsRef.current);
            pointsRef.current.push({points:[], 
                                    stroke:plineRef.current.stroke, 
                                    strokeColor:plineRef.current.strokeColor, 
                                    fill:false, 
                                    fillColor: plineRef.current.fillColor,
                                    allowStroke: plineRef.current.allowStroke,
                                    allowFill: plineRef.current.allowFill,
                                    fillMode: plineRef.current.fillMode,
                                    gradientColor1: plineRef.current.gradientColor1,
                                    gradientColor2: plineRef.current.gradientColor2,
                                    gradientAngle: plineRef.current.angle,                              
                                    alpha: plineRef.current.alpha,
                                    strokeWidth: plineRef.current.stroke
                            }); // Push empty line
            // Push the first point to the new line
            // pointsRef.current[pointsRef.current.length - 1].points.push({ x: dx, y: dy });
            addFilteredPoint(pointsRef.current[pointsRef.current.length - 1].points, dx, dy, elapsedTime);
            // pointsRef.current.push({x:xg,y:yg})      
            lineBeginRef.current=false;
        }
        else
        {          
            // Add point to the last line
          undoPlineStack.push(pointsRef.current);
          const currentLine = pointsRef.current[pointsRef.current.length - 1].points;
          if (currentLine) {
              // currentLine.push({ x: dx, y: dy });
              addFilteredPoint(currentLine, dx, dy, elapsedTime);
          }
        }

         mouseDownScreenRef.current = { x, y };
        // Update drag origin to new point
        // mouseDownPageRef.current = { x: xP, y: yP };

        draw();       
      }
      return;
    }

    if(operationRef.current === 'paste')
    {
      clipIndexRef.current = getClipIndex(x,y,viewPortRef, pageSizeRef);
      draw();
      // console.log("index: ", clipIndexRef.current);
    }
  
    onCoordUpdate(xP, yP);
  
    // When dragging is active
    if (draggingRef.current) {
      // Pan the page (when no specific operation like 'move' is active)
      if ((operationRef.current === 'select') && 
            (mouseDownScreenRef.current && selectedRef.current.length === 0) || 
            (selectedRef.current.length === 1 && selectedRef.current[0].locked) ||
            (e.buttons & 4 || segmentOpRef.current === 'lineop') )
      {
        const prevPage = screenToPage(mouseDownScreenRef.current.x, mouseDownScreenRef.current.y, viewPortRef);
        const currPage = screenToPage(x, y, viewPortRef);
  
        // Reverse subtraction: move the view *with* the mouse
        viewPortRef.current.offsetX += currPage.x - prevPage.x;
        viewPortRef.current.offsetY += currPage.y - prevPage.y;
  
        mouseDownScreenRef.current = { x, y };
        draw();
        return;
      }

      if(operationRef.current === 'bezier' || operationRef.current === 'pline')
      {
          if(draggingRef.current)
          {
       
            if(pointDragRef.current != null)
            {
              pointDragRef.current.x = xP;
              pointDragRef.current.y = yP;
            }      

            if(controlDragRef.current != null)
            {
              let cp = controlDragRef.current;

              if(cp.loc === 0 )
              {
                cp.control.x1 = xP;
                cp.control.y1 = yP;
              }
              if(cp.loc === 1)
              {
                cp.control.x2 = xP;
                cp.control.y2 = yP;
              }

            }

            if(pointDownRef.current != null)
            {
              if(pointDragRef.current.x != pointDownRef.current.x &&
                pointDragRef.current.y != pointDownRef.current.y)
                {
                  pointDownRef.current = null;
                }

            }
            // Update drag origin to new point
            mouseDownPageRef.current = { x: xP, y: yP };
            draw();
            return;
          }
      }

      if ((operationRef.current === 'move' || operationRef.current === 'adjimage' || animateOpRef.current==='handleOffset' || animateOpRef.current==='handleOffsetPath' ||
              (draggingRef.current && !viewPortRef.current.isResizing)) && 
                selectedRef.current.length > 0) 
      {
        if(operationRef.current != 'adjimage' && 
            animateOpRef.current !== 'handleOffset' &&
            animateOpRef.current !== 'handleOffsetPath')
        {
          operationRef.current = 'move';
          canvasRef.current.style.cursor = 'move';
        }
        const dx = xP - mouseDownPageRef.current.x;
        const dy = yP - mouseDownPageRef.current.y;
      
        if(operationRef.current !== 'adjimage' &&
            animateOpRef.current !== 'handleOffset' &&
            animateOpRef.current !== 'handleOffsetPath')
        {
          selectedRef.current.forEach((rect) => {


            if(rect.locked === true) return;

            if(rect.type === "Group")
            {
              rect.x += dx;
              rect.y += dy;
              // moveGroup(rect,dx,dy);
            }
            else
            {
              rect.x += dx;
              rect.y += dy;
            }

            if(rect.subType === 'Animate')
            {
              if(rect.mPath != undefined &&
                 rect.mPath != null)
              {
                rect.mPath.x += dx;
                rect.mPath.y += dy;
              }

              if(rect.mObj != undefined &&
                rect.mObj != null)
              {
                rect.mObj.x += dx;
                rect.mObj.y += dy;
              }
            }

          });
        }
        else
        {
          if(operationRef.current === 'adjimage')
          {
            let rect = selectedRef.current[0];

            rect.imagePanX += dx;
            rect.imagePanY += dy;
          }
          else
          {
            if(animateOpRef.current === 'handleOffset')
            {
              let rect = selectedRef.current[0];                          
              let m = rect.mObj;

              if(m.type === 'Bezier' || m.type === 'Prect')
              {
                offsetPath(m, dx, dy);
                m.imagePanX += dx;
                m.imagePanY += dy;
              }
              else
              {
                if(m.type === 'SvgText')
                {
                  offsetText(m, dx, dy);
                }
              }
            }
            else
            {
              let rect = selectedRef.current[0];        
              offsetView(rect,dx, dy);


              /*
              let m = rect.mPath;
              if(m.type === 'Bezier' || m.type === 'Prect')
              {
                offsetPath(m, dx, dy);
              }
              else
              {

                if(m.subType === 'SvgRect')
                {
                  offsetView(rect, dx, dy);
                }
              }
              */
            }
          }


          // console.log("dx: ", dx);
          // console.log("dy: ", dy);
          // console.log("Move Image");
        }
      
        // Update drag origin to new point
        mouseDownPageRef.current = { x: xP, y: yP };
        draw(true);
        return;
      }
        
      // Resize only the first selected rectangle
       if ((operationRef.current === 'resize'  || (draggingRef.current && viewPortRef.current.isResizing)) && (selectedRef.current.length === 1 && !selectedRef.current[0].locked)) {

         operationRef.current = 'resize';
         canvasRef.current.style.cursor = 'nwse-resize';

          const rect = selectedRef.current[0];


          // Calculate the new width and height based on displacement (xP, yP)
          const newWidth = xP - rect.x;
          const newHeight = yP - rect.y;

          if(newWidth > 30 && newHeight > 30)
          {
            // Calculate the zoom factors (relative change in width/height)
            const rZoomX = newWidth / viewPortRef.current.originalWidth;  // Zoom factor in the X direction
            const rZoomY = newHeight / viewPortRef.current.originalHeight; // Zoom factor in the Y direction
            viewPortRef.current.rZoomX = rZoomX;
            viewPortRef.current.rZoomY = rZoomY;
            viewPortRef.current.isResizing = true;


            if(rect.type === 'Group')
            {
              let originalGroupStr = viewPortRef.current.originalGroup;
              let o = null;
              if(originalGroupStr)              
              {
                o = JSON.parse(originalGroupStr);
              }
              
              resizeGroup(o, rect, xP, yP);
            }
            else
            {

              if(resizeViewRef.current === true)
              {
                  let nW = xP - rect.x;
                  let nH = yP - rect.y;
                  
                  doResize(rect, nW, nH);
              }
              else
              {
                rect.w = xP - rect.x;
                rect.h = yP - rect.y;

                rect.imagePanX = viewPortRef.current.originalRect.imagePanX*rZoomX;
                rect.imagePanY = viewPortRef.current.originalRect.imagePanY*rZoomY;
              }
            }

            if(rect.type === 'SvgText')
            {
              rect.rZoomX = rZoomX;
              rect.rZoomY = rZoomY;
              const originalSvgPath = viewPortRef.current.originalSvgPath;

              
              if(originalSvgPath !== null && originalSvgPath !== "")
              {
                let scaledPathString = scaleSvgPathString(originalSvgPath, rZoomX, rZoomY);
                let sane = sanitizePath(scaledPathString);
                rect.svgText = sane;
              }
              
            }

            if(rect.type === 'Prect')
            {
              
              const originalPath = viewPortRef.current.originalPath;
              if(originalPath != null)
              {

                const newPath = resize(originalPath, rZoomX, rZoomY);
                rect.path = newPath;
              }
            }

            if(rect.type === 'Bezier')
            {
              
              const originalPath = viewPortRef.current.originalPath;
              if(originalPath != null)
              {
                const newPath = resize(originalPath, rZoomX, rZoomY);
                rect.path = newPath;
              }

              const originalControl = viewPortRef.current.originalControl;
              if(originalControl != null)
              {
                const newControl = resizeC(originalControl, rZoomX, rZoomY);
                rect.control = newControl;
              }
            }

            if(rect.type === 'Rectangle')
            {
              if(rect.subType === 'Animate')
              {
                                                
                let eZoomX = rZoomY;
                let eZoomY = rZoomY;

                if(rZoomX > rZoomY)
                {
                  eZoomX = rZoomX;
                  eZoomY = rZoomX;
                }

                if(resizeViewRef.current === false)
                {
                  let tRect = viewPortRef.current.originalRect;
                  resizeAnimate(rect, tRect, eZoomX, eZoomY);
                }  // end resizeAnimate              
              }
            }
            draw(true);
          }
          return;
        }
        draw(true);      
    }
  }
  
  const handleMouseUp = (e) => {
    draggingRef.current = false;

    if(tempRectRef.current != null /*&& (operationRef.current === 'bezier' || operationRef.current === 'pline')*/)
    {
      if(operationRef.current === 'pline')
      {
        pointDragRef.current = null;
        // highlight action here for bezier points
        // onSegmentPoints();
        applyLineOp();
        return;
        // tempRectRef.current = null;
      }
      if(operationRef.current === 'bezier')
      {
        pointDragRef.current = null;
        // highlight action here for bezier points
        // onSegmentPoints();
        applyLineOpB();
        return;
        // tempRectRef.current = null;
      }

      selectAll();
      pointDragRef.current = null;
      tempRectRef.current = null;
            
      draw();
      return;

      // select all rectangles that fall within this rect

    }


    if(segmentOpRef.current === 'lineop')
    {
      if(segmentActionRef.current === 'moveAll' || 
          segmentActionRef.current === 'moveSelected' ||
          segmentActionRef.current === 'moveLine' ||
          segmentActionRef.current === 'moveSegment')
      {
          canvasRef.current.style.cursor = 'crosshair';
          segmentActionRef.current = 'none';
      }
    }

    if(operationRef.current === 'freehand')
    {
     
      if (e.button === 0) 
      {
        isDrawingRef.current = false;

        lineBeginRef.current = true;
        console.log("finish a stroke");
        // finalize drawing
      }
    }

    if(operationRef.current === 'bezier' || operationRef.current === 'pline')
    {
      
      if(e.button === 1) return;

      const {width, height, snapToGrid} = plineRef.current;

      const {x,y} = getMouse(e);

      const { x: xP, y: yP } = screenToPage(x, y, viewPortRef);
      let xg = xP;
      let yg = yP;

      if (snapToGrid && !e.altKey && !e.shiftKey) 
      {
        xg = snap(xg,width);
        yg = snap(yg, height);
      }
    

      if(pointDownRef.current != null)
      {

        if(operationRef.current === 'bezier')
        {
          console.log("Adding another point rather than moving");
          addBezierPoint(xg, yg);
          pointDownRef.current = null;
          
        }

        if(operationRef.current === 'pline')
        {
          console.log("Adding another point rather than moving");
          addPLinePoint(xg, yg);
          pointDownRef.current = null;          
        }
      }


      if(pointDragRef.current !== null && controlDragRef.current === null)
      {

         pointDragRef.current.x = xg;
         pointDragRef.current.y = yg;

        
        //  pointDragRef.current = null;

        draggingRef.current = false;
      }      

      draw();
      return;
    }



    if( (operationRef.current === 'move' || operationRef.current === 'resize') && operationRef.current !=='adjimage')
    {

      // historyRef.current.push(JSON.parse(JSON.stringify(rectsRef.current)))
      // historyStack.push(rectsRef.current);

      
      const {width, height, snapToGrid} = plineRef.current;
          
      
      if(selectedRef.current.length > 0 && snapToGrid===true && !e.altKey && !e.shiftKey) {
      
        selectedRef.current.forEach((rect) => {
          
          let xg = snap(rect.x,width);
          let yg = snap(rect.y, height);

          if(rect.locked)
          {
            xg = rect.x;
            yg = rect.y;
          }
          // const wg = snap(rect.w, width);
          // const hg = snap(rect.h, height);

          // don't snap width and height, gets annoying for resizing
          const wg = rect.w;
          const hg = rect.h;


          /*
          if(rect.type==="Group" && operationRef.current === 'move')
          {
            let dx = xg - rect.x;
            let dy = yg - rect.y;
            moveGroup(rect,dx,dy);
          }
          */

          if(rect.type ==="Group" ||
            (rect.type === "Rectangle" && rect.subType === "Animate") ||
            (rect.type === "Bezier" && operationRef.current === 'resize') ||
            (rect.type === "Prect" && operationRef.current === 'resize') ||
            selectedRef.current.length > 1)
          {
            // console.log("NO SNAP");
            // don't snap on mouse up for groups. It messes them up
          }
          else
          {          
            console.log("SNAP");
            rect.x= xg;
            rect.y=yg;
            rect.w = wg;
            rect.h = hg;
          }

          operationRef.current = 'select';
          canvasRef.current.style.cursor = 'pointer';
    
          setOperation('select');
        });

        if(selectedRef.current.length ===1)
        {
          if(selectedRef.current[0].locked === true)
          {
            return;
          }

          if(selectedRef.current[0].type === 'Prect')
          {
            let r = selectedRef.current[0];
            // Calculate the zoom factors (relative change in width/height)
            const rZoomX = r.w / viewPortRef.current.originalWidth;  // Zoom factor in the X direction
            const rZoomY = r.h / viewPortRef.current.originalHeight; // Zoom factor in the Y direction

            const originalPath = viewPortRef.current.originalPath;
            if(originalPath != null)
            {
              const newPath = resize(originalPath, rZoomX, rZoomY);
              r.path = newPath;
            }            
          }


          if(selectedRef.current[0].type === 'SvgText')
          {

            let r = selectedRef.current[0];
            // Calculate the zoom factors (relative change in width/height)
            const rZoomX = r.w / viewPortRef.current.originalWidth;  // Zoom factor in the X direction
            const rZoomY = r.h / viewPortRef.current.originalHeight; // Zoom factor in the Y direction

            const originalSvgPath = viewPortRef.current.originalSvgPath;

            if(originalSvgPath !== null && originalSvgPath !== "")
              {
                let scaledPathString = scaleSvgPathString(originalSvgPath, rZoomX, rZoomY);
                let sane = sanitizePath(scaledPathString);
                r.svgText = sane;
              }
          }
        }


        if(animateOpRef.current === 'resizeView')
        {
          resizeViewRef.current = false;
          onAnimateSelect(null);
          animateOpRef.current = 'none';

        }
        
        // draw();

        viewPortRef.current.isResizing = false;
      }

      canvasRef.current.style.cursor = 'pointer';
      operationRef.current = 'select';
      viewPortRef.current.isResizing = false;        
      setOperation('select');
      draw();
    
    } 
  }

  
  const undo = () =>
  {

    if(historyStack.getLength(pageNum) === 0)
    {
       setCanUndo(false);
       return;
    }

    
    const previous = historyStack.pop(pageNum);
    redoStack.push(pageNum,rectsRef.current);
    rectsRef.current = previous;
    // ALSO update pagesRef!
    pagesRef.current.pages[pageNum-1].layout = previous;

    draw();

    if(historyStack.getLength(pageNum) === 0)
    {
       setCanUndo(false);
       return;
    }
    setCanUndo(true);

    
  }


  const redo = () =>
  {

    if(redoStack.getLength(pageNum) === 0)
    {
      setCanRedo(false);
      return;
    }
    
    const previous = redoStack.pop(pageNum);
    historyStack.push(pageNum,rectsRef.current);
    rectsRef.current = previous;
    pagesRef.current.pages[pageNum-1].layout = previous;

    draw();

    if(redoStack.getLength(pageNum) === 0)
    {
      setCanRedo(false);
      return;
    }
    setCanRedo(true);
  }  

  /*
  const labelZ = () => {
    rectsRef.current.forEach((rect, index) => {
      if(rect.type !== "Menubar")
      {
          rect.zIndex = index;
      }
    });
  }
  */


  const labelZ = () => {
    let zIndex = 0; // Start the zIndex from 0 for the topmost element
    let hitArray = [];  // To store elements in the order they are processed (depth-first)
    let renderArray = [];  // To store the order of elements for rendering
  
    // Stack-based DFS traversal
    const stack = [...rectsRef.current].reverse();  // Initialize the stack with the root-level elements
    
    // Store parent-child relationships using references
    const parentReferences = new Map();
  
    while (stack.length > 0) {
      const rect = stack.pop(); // Pop the last element from the stack
  
      // If the rect is a "Group", process its children and push them to the stack
      if (rect.type === "Group") {
        // Sort children by their zIndex before processing
        rect.children.sort((a, b) => a.zIndex - b.zIndex);
  
        // Add the group itself to hitArray for tracking the zIndex (to render later)
        hitArray.push({ rect: rect, zIndex: zIndex, parent: null });  // No parent for the root Group
        renderArray.push(rect);  // Add group to renderArray
  
        // Process children in sorted order (depth-first traversal)
        rect.children.reverse().forEach((child) => {
          // Track the parent-child relationship using the actual parent reference
          parentReferences.set(child, rect);
  
          // Push the child to the stack for further processing
          stack.push(child);
  
          // Add child to hitArray with parent reference
          hitArray.push({ rect: child, zIndex: zIndex++, parent: rect });
          renderArray.push(child);  // Add child to renderArray
        });
      } else {
        // Process non-Group elements directly
         // For non-group elements, retrieve the parent from parentReferences map (if nested)
        let parent = parentReferences.get(rect) || null;
      
        // Add non-group to hitArray with the correct parent
        hitArray.push({ rect: rect, zIndex: zIndex++, parent: parent });
        // hitArray.push({ rect: rect, zIndex: zIndex++, parent: null });  // No parent for non-group at the root level
        renderArray.push(rect);  // Add non-group to renderArray
      }
    }
  
    // After DFS traversal, we have processed all elements
    // Update the zIndex for all elements in rectsRef based on the hitArray
    hitArray.forEach((item, index) => {
      // Find the actual rect object by reference and update its zIndex
      item.rect.zIndex = index;
    });
    
    hitRef.current = hitArray;
    renderRef.current = renderArray;
    parentsRef.current = parentReferences;

  }


  const regenUnique = (pages) =>
  {
    // let renderArray = renderRef.current;
    pages.forEach(page =>{
        let rects = page.layout;
        // console.log(JSON.stringify(page));

        rects.forEach(r=>{

          if(r.type != "Group")
          {
            let unique = getUnique(0);
            let idStr = unique.slice(0,10);
            let sA = r.id.split("_");
            r.id = sA[0]+ '_' +sA[1] + '_' + idStr;
            console.log("Unique: ", unique);
          }
          else
          {
              // Initialize a queue with the selected group
              const queue = [r];  
              while (queue.length > 0) 
              {
                // Get the current group from the queue
                const currentGroup = queue.shift();
                      // Adjust the positions of the children
                currentGroup.children.forEach((child,index) => 
                {
                  let unique = getUnique(0);
                  let idStr = unique.slice(0,10);
                  let sA = child.id.split("_");
                  child.id = sA[0]+ '_' +sA[1] + '_' + idStr;    

                  
                  // If the child is a group, add it to the queue to be moved next
                  if (child.children && child.children.length > 0) 
                  {
                        queue.push(child);          
                  }
                });
              }
            }
          });
    });
  }
  
  const createNewRect = (x,y, type) =>{    
    let newRect = { x, y, w: 100, h: 60, id: '', type:type, description: '', subType:"Text", zIndex:-1, 
                        global:false, children:[], 
                        keepAspect:false, 
                        enablePopup:false,
                        clickable: false,
                        useLocal: true,
                        aiSvg: "",
                        localLink: "",
                        externalLink:"",
                        imageName:"",
                        fillColor:"#000000", 
                        strokeColor: "#000000", 
                        strokeWidth: 1,
                        allowStroke: true,
                        allowFill: false,
                        fillMode:"solid",
                        gradientColor1: "#000000",
                        gradientColor2: "#FFFFFF",
                        gradientAngle: 0,                      
                        hasParent:false,
                        parentAlpha: 1,
                        fillAlpha: 1,
                        bAlpha: 1,
                        prevEffects: null,
                        shadowColor:"#000000",
                        shadowBlur:0,
                        regularBlur:0,
                        locked: false,
                        shadowOffsetX:0,
                        shadowOffsetY:0,
                        isDropShadow:false,
                        isBlur:false,
                        isGlow:false,
                        cornerTL:0,
                        cornerTR:0,
                        cornerBR:0,
                        cornerBL:0,
                        imagePanX:0,
                        imagePanY:0,                        
                        iX:0,
                        iY:0,
                        iW:0,
                        iH:0,
                        offsetViewX:0,
                        offsetViewY:0,   
                        rotation:0,
                        tl: {x:0,y:0},
                        tr: {x:0, y:0},
                        bl: {x:0, y:0},
                        br: {x:0, y:0},
                        imageZoom: 1,
                        mPath:undefined,
                        mObj:undefined, 
                        animationSettings:{}
                        
                      }

    if(type === "Menubar")
    {
      newRect = { x, y, w: 100, h: 60, id: '', type:type, description: '', zIndex:9999, global:true, children: [] };
    }

    if(type === "SvgText")
    {
      newRect = { x, y, w: 100, h: 60, id: '', 
                        type:type, description: '', zIndex:-1, global:false, rotation:0, svgText:"", 
                        rZoomX:1, rZoomY:1, 
                        fontUrl: "",
                        fontText: "",
                        originalSvgPath:"",
                        originalWidth: 0,
                        originalHeight: 0,
                         bound:{},
                         children:[],
                         clickable: false,
                         useLocal: true,
                         aiSvg:"",
                         localLink: "",
                         externalLink:"",
                         fillColor:"#000000", 
                         strokeColor: "#000000", 
                         strokeWidth: 1,
                         allowStroke: true,
                         allowFill: false,
                         fillMode:"solid",
                         gradientColor1: "#000000",
                         gradientColor2: "#FFFFFF",
                         gradientAngle: 0,                       
                         hasParent: false,
                         locked:false,
                         parentAlpha:1,
                         fillAlpha: 1,
                         bAlpha: 1,
                         prevEffects: null,
                         shadowColor:"#000000",
                         shadowBlur:0,
                         regularBlur:0,
                         shadowOffsetX:0,
                         shadowOffsetY:0,
                         isDropShadow:false,
                         isBlur:false,
                         isGlow:false,
                         cornerTL:0,
                         cornerTR:0,
                         cornerBR:0,
                         cornerBL:0, 
                         imagePanX:0,
                         imagePanY:0,
                         imageZoom: 1,
                         iX:0,
                         iY:0,
                         iW:0,
                         iH:0,
                         offsetViewX:0,
                         offsetViewY:0,    
                         tl: {x:0,y:0},
                         tr: {x:0, y:0},
                         bl: {x:0, y:0},
                         br: {x:0, y:0}, 
                         mPath:null,
                         mObj:null, 
                         animationSettings:{}
                         };
    }
    

    

    let n = "null"
    if(filenameRef.current != '')
    {
      n = `${filenameRef.current}_${pageNum}_`;
    }

     let identifier = rectsRef.current.length;

    // let identifier = getUnique(0);
    // let slId = identifier.slice(0,5);

    let unique = n + identifier;
    // let unique = n + slId;
    let found = rectsRef.current.find(rect => rect.id === unique);

    while (found && identifier < 10000)
    {
      identifier++;
      // identifier = getUnique(0);
      // slId = identifier.slice(0,5);
      unique = n + identifier;
      // unique = n + slId;
      found = rectsRef.current.find(rect => rect.id === unique);
    }

    newRect.id = unique;

    if(type==='Menubar')
    {
      newRect.id="menuBar9999";
    }
    return newRect;
  }

  function resizeGroup(o, rect, xP, yP)
  {
    const group = rect;  // Assuming you're moving a single group
    if (!group) return;
  

      // Calculate the new width and height based on displacement (xP, yP)
    let newWidth = 0;
    let newHeight = 0;
      
    newWidth = xP - rect.x;
    newHeight = yP - rect.y;

    let gZoomX = 1;
    let gZoomY = 1;

    if(newWidth > 30 && newHeight > 30)
    {
        // Calculate the zoom factors (relative change in width/height)
        gZoomX = newWidth / o.w;  // Zoom factor in the X direction
        gZoomY = newHeight / o.h; // Zoom factor in the Y direction

         // Choose the smaller zoom factor to keep the zoom proportional
        let gZoom = Math.min(gZoomX, gZoomY);
        
        gZoomX = gZoom;
        gZoomY = gZoom;

         // Apply the proportional zoom factor to both width and height
        rect.w = o.w * gZoom;
        rect.h = o.h * gZoom;
        // rect.offsetViewX = o.offsetViewX*gZoomX;
        // rect.offsetViewY = o.offsetViewY*gZoomY;

        // rect.imagePanX = o.imagePanX*gZoom;
        // rect.imagePanY = o.imagePanY*gZoom;
  

        if(rect.subType === 'Animate')
        {
          if(rect.mPath != undefined)
          {

            /*
            rect.mPath.x = rect.x;
            rect.mPath.y = rect.y;

            rect.mPath.w = rect.w;
            rect.mPath.h = rect.h;
            
            
            if(rect.mObj != undefined)
            {
              rect.mObj.w = o.mObj.w* gZoom;
              rect.mObj.h = o.mObj.h*gZoom;
            }
            */
          }
        }


    }

    // Initialize a queue with the selected group
    const queue = [group];
    const original = [o];
  
    while (queue.length > 0) {
      // Get the current group from the queue
      const currentGroup = queue.shift();
      const originalGroup = original.shift();
  
      // Move the group itself
      // currentGroup.x += dx;
      //currentGroup.y += dy;
  
      // Adjust the positions of the children
      currentGroup.children.forEach((child,index) => {

        // viewPortRef.current.originalWidth = hit.w;
        // viewPortRef.current.originalHeight = hit.h;
        // viewPortRef.current.originalPath = hit?.path;
        // viewPortRef.current.originalControl = hit?.control;
        // viewPortRef.current.originalSvgPath = hit?.svgText;
            

       // Calculate the child's relative position within the group (before resizing)
       const childOriginal = originalGroup.children[index];

        // Get the child's current position relative to the group's origin (rect.x, rect.y)
        /*
        const relativeX = childOriginal.x - rect.x;
        const relativeY = childOriginal.y - rect.y;


        // Now scale the child's relative position
        const scaledX = rect.x + (relativeX * gZoomX); // Apply zoom to X
        const scaledY = rect.y + (relativeY * gZoomY); // Apply zoom to Y
        */

        // Get the child's current position relative to the group's origin (rect.x, rect.y)
        const relativeX = childOriginal.x;
        const relativeY = childOriginal.y;
                
        // Now scale the child's relative position
        const scaledX = (relativeX * gZoomX); // Apply zoom to X
        const scaledY = (relativeY * gZoomY); // Apply zoom to Y
        
        // Adjust the child's size based on the zoom factor
        child.x = scaledX;
        child.y = scaledY;
        child.w = childOriginal.w * gZoomX;
        child.h = childOriginal.h * gZoomY;
        child.offsetViewX = childOriginal.offsetViewX*gZoomX;
        child.offsetViewY = childOriginal.offsetViewY*gZoomY;

        child.imagePanX = childOriginal.imagePanX*gZoomX;
        child.imagePanY = childOriginal.imagePanY*gZoomY;
      
        if(child.subType === 'Animate')
        {
          if(child.mPath)
          {
            child.mPath.x = child.x - child.offsetViewX;
            child.mPath.y = child.y - child.offsetViewY;
            child.mPath.w = childOriginal.mPath.w * gZoomX;
            child.mPath.h = childOriginal.mPath.h * gZoomY;
          }
          if(child.mObj)
          {
              // Get the child's current position relative to the group's origin (rect.x, rect.y)
              const rX = childOriginal.mObj.x;
              const rY = childOriginal.mObj.y;
              const rW = childOriginal.mObj.w * gZoomX;
              const rH = childOriginal.mObj.h * gZoomY;
                        
              // Now scale the child's relative position
              // const sX = (rX * gZoomX); // Apply zoom to X
              // const sY = (rY * gZoomY); // Apply zoom to Y          
              // child.mObj.x = sX ;
              // child.mObj.y = sY;

              child.mObj.x =  child.x - child.offsetViewX;
              child.mObj.y = child.y - child.offsetViewY;
              child.mObj.w = rW;
              child.mObj.h = rH;

              child.mObj.imagePanX = childOriginal.mObj.imagePanX * gZoomX;
              child.mObj.imagePanY = childOriginal.mObj.imagePanY * gZoomY;
          }
        }

        if(child.type === 'SvgText')
        {
          child.rZoomX = gZoomX;
          child.rZoomY = gZoomY;
          const originalSvgPath = originalGroup.children[index].svgText;

          // console.log("GRoup svg Text");
          if(originalSvgPath !== null && originalSvgPath !== "")
          {
            
            // console.log("HELP");
            let scaledPathString = scaleSvgPathString(originalSvgPath, gZoomX, gZoomY);
            let sane = sanitizePath(scaledPathString);
            child.svgText = sane;
          }          
        }

        if(child.type === 'Prect')
        {
          
          const originalPath = childOriginal.path;
          if(originalPath != null)
          {

            const newPath = resize(originalPath, gZoomX, gZoomY);
            child.path = newPath;
          }
        }

        if(child.type === 'Bezier')
        {
          
          const originalPath = childOriginal.path;
          if(originalPath != null)
          {
            const newPath = resize(originalPath, gZoomX, gZoomY);
            child.path = newPath;
          }

          const originalControl = childOriginal.control;
          if(originalControl != null)
          {
            const newControl = resizeC(originalControl, gZoomX, gZoomY);
            child.control = newControl;
          }
        }

        if(child.subType === 'Animate')
        {
          if(child.mPath)
          {
            const originalPath = childOriginal.mPath.path;
            if(originalPath != null)
            {
              const newPath = resize(originalPath, gZoomX, gZoomY);
              child.mPath.path = newPath;
            }
          
            if(childOriginal.mPath.type === 'Bezier')
            {
              const originalControl = childOriginal.mPath.control;
              if(originalControl != null)
              {
                const newControl = resizeC(originalControl, gZoomX, gZoomY);
                child.mPath.control = newControl;
              }
            }

          }  // end if mPath
          if(childOriginal.mObj != undefined)
          {
              if(childOriginal.mObj.type === 'Bezier' || childOriginal.mObj.type === 'Prect')
              {
                const oMObj = childOriginal.mObj.path;
                if(oMObj != null)
                {                    
                  const nMObj = resize(oMObj, gZoomX, gZoomY);
                  child.mObj.path = nMObj;                      
                }

                if(child.mObj.type === 'Bezier')
                {

                  const oCObj = childOriginal.mObj.control;
                  if(oCObj != null)
                  {
                    const nCObj = resizeC(oCObj, gZoomX, gZoomY);
                    child.mObj.control = nCObj;
                  }
                }  
              }
              if(childOriginal.mObj.type === 'SvgText')
              {
                  child.rZoomX = gZoomX;
                  child.rZoomY = gZoomY;
                  const originalSvgPath = childOriginal.mObj.svgText;
        
                  // console.log("GRoup svg Text");
                  if(originalSvgPath !== null && originalSvgPath !== "")
                  {
                    
                    // console.log("HELP");
                    let scaledPathString = scaleSvgPathString(originalSvgPath, gZoomX, gZoomY);
                    let sane = sanitizePath(scaledPathString);
                    child.mObj.svgText = sane;
                  }                
              } 
            }
        }

        // child.x += dx;
        // child.y += dy;
  
        // If the child is a group, add it to the queue to be moved next
        if (child.children && child.children.length > 0) {
          queue.push(child);
          original.push(childOriginal);
        }
      });
    }
  }

  /*
  function moveGroup(rect, dx, dy) {
    const group = rect;  // Assuming you're moving a single group
    if (!group) return;
  
    // Initialize a queue with the selected group
    const queue = [group];
  
    while (queue.length > 0) {
      // Get the current group from the queue
      const currentGroup = queue.shift();
  
      // Move the group itself
      currentGroup.x += dx;
      currentGroup.y += dy;
  
      // Adjust the positions of the children
      currentGroup.children.forEach(child => {
         //child.x += dx;
         // child.y += dy;
  
        // If the child is a group, add it to the queue to be moved next
        if (child.children && child.children.length > 0) {
          queue.push(child);
        }
      });
    }
  }  
  */

/*
  function ungroup() {
    const selected = selectedRef.current;
    if (!selected || selected.length !== 1) return;
  
    const group = selected[0];
    if (group.type !== "Group") return;
  
    const parents = parentsRef.current;
  
    // --- 1️⃣ Helper: get world position ---
    function getWorldPosition(rect) {
      let x = rect.x;
      let y = rect.y;
  
      let parent = parents.get(rect);
      while (parent) {
        x += parent.x;
        y += parent.y;
        parent = parents.get(parent);
      }
  
      return { x, y };
    }
  
    const groupWorld = getWorldPosition(group);
    const parent = parents.get(group);
  
    // --- 2️⃣ Remove group from its real parent ---
    if (parent) {
      parent.children = parent.children.filter(c => c !== group);
    } else {
      rectsRef.current = rectsRef.current.filter(c => c !== group);
    }
  
    let newSelected = [];
  
    // --- 3️⃣ Move children up one level ---
    group.children.forEach(child => {
      // Child world position
      const childWorld = {
        x: groupWorld.x + child.x,
        y: groupWorld.y + child.y
      };
  
      if (parent) {
        // Convert world → parent local
        const parentWorld = getWorldPosition(parent);
  
        child.x = childWorld.x - parentWorld.x;
        child.y = childWorld.y - parentWorld.y;
  
        parent.children.push(child);
      } else {
        // Convert world → root
        child.x = childWorld.x;
        child.y = childWorld.y;
  
        rectsRef.current.push(child);
      }
  
      newSelected.push(child);
    });
  
    // --- 4️⃣ Clear children (optional safety)
    group.children = [];
  
    // --- 5️⃣ Recompute z + parents ---
    labelZ();
  
    selectedRef.current = newSelected;
    pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
  }
  
  */
  function ungroup() 
  {
    console.log("ungrouping");

    const selected = selectedRef.current;
    const rects = rectsRef.current;

    if (selected.length === 0 || selected.length > 1 || selected[0].type !== "Group") {
        return;
    }

    let current = selected[0];

    // 3️⃣ Remove selected from top-level rects
    rectsRef.current = rects.filter(r => r !== current); // Filter based on reference, not includes
    
    let newSelected = [];
    current.children.reverse().forEach(r => {
        r.x = r.x + current.x;
        r.y = r.y + current.y;

        if(r.subType === 'Animate')
        {
          if(r.mPath)
          {
            r.mPath.x = r.x - r.offsetViewX;
            r.mPath.y = r.y - r.offsetViewY;
          }
          if(r.mObj)
          {
            r.mObj.x +=current.x;
            r.mObj.y +=current.y;
          }
        }
  
        rectsRef.current.push(r);      
        newSelected.push(r);
    });

    // Make sure selectedRef.current is updated with the children
    selectedRef.current = newSelected;
    
    // Update the layout for the current page
    pagesRef.current.pages[pageNum - 1].layout = rectsRef.current; 
  }
  

  

  function rotatePoint(px, py, cx, cy, angle) {
    const rad = (angle * Math.PI) / 180;
    const dx = px - cx;
    const dy = py - cy;
    
    const rotatedX = cx + dx * Math.cos(rad) - dy * Math.sin(rad);
    const rotatedY = cy + dx * Math.sin(rad) + dy * Math.cos(rad);
    
    return { x: rotatedX, y: rotatedY };
  }
  
  function checkPoint(p,acc)
  {

    if(p.x < acc.minX)
    {
      acc.minX = p.x;
    }
    if(p.x > acc.maxX)
    {
      acc.maxX = p.x;
    }
    if(p.y < acc.minY)
    {
      acc.minY = p.y;
    }
    if(p.y > acc.maxY)
    {
      acc.maxY = p.y
    }
    

    return acc;

  }

  function rotateRect(rect,acc) {

    let x=rect.x;
    let y=rect.y;
    let w=rect.w;
    let h=rect.h;

    
    if (rect.subType === 'SvgRect' || rect.subType === 'SvgSquare')
    {
      
      if(rect.allowStroke)    
      {
        let num = parseInt(rect.strokeWidth);
        x = rect.x - num/2;
        y = rect.y - num/2;
        w = rect.w + num;
        h = rect.h + num;
      }
    }
    

    let lC = rotatePoint(x,y, x+w/2, y+h/2, -rect.rotation);
    let rC = rotatePoint(x+w,y, x+w/2, y+h/2, -rect.rotation);
    let bR = rotatePoint(x+w,y+h, x+w/2, y+h/2, -rect.rotation);
    let bL = rotatePoint(x,y+h, x+w/2, y+h/2, -rect.rotation);

    acc = checkPoint(lC, acc);
    acc = checkPoint(rC,acc);
    acc = checkPoint(bR,acc);
    acc = checkPoint(bL,acc);
    return acc;

  }
  
  
  function groupTogether()
  {
    const selected = selectedRef.current;
    const rects = rectsRef.current;

    if (selected.length === 0) return;


    let acc = {minX:99999, minY:99999, maxX:-99999, maxY:-99999}

    selected.forEach(r=>{
      if(!r.locked)
      {
        acc = rotateRect(r,acc);
      }
    })

    const minX = acc.minX;
    const minY = acc.minY;
    const maxX = acc.maxX;
    const maxY = acc.maxY;
 
    /*
      // 1️⃣ Compute bounding box for group origin
    const minX = Math.min(...selected.map(r => r.x));
    const minY = Math.min(...selected.map(r => r.y));
      
    const maxX = Math.max(...selected.map(r => r.x+r.w));
    const maxY = Math.max(...selected.map(r => r.y+r.h));
    */
      
    let newRect = createNewRect(minX, minY,"Group");
    
    let pad = 30;
    newRect.x = minX-pad;
    newRect.y = minY-pad;
    newRect.w = maxX-newRect.x+pad;
    newRect.h = maxY-newRect.y+pad;

    // 3️⃣ Remove selected from top-level rects
    rectsRef.current = rects.filter(r => {
              const notPartOfGroup = !selected.includes(r) || r.locked;
              return notPartOfGroup;
    });

    // 4️⃣ Convert selected into local coords + add as children    
    selected.forEach(r => {
      if(r.locked === true)
      {
        return;
      }

      r.x = r.x - newRect.x; //minX;
      r.y = r.y - newRect.y; // minY;

      /*
      if(r.type === 'Group')
      {
        r.x += pad;
        r.y += pad;
      }
      */

      if(r.subType === 'Animate')
      {
        if(r.mPath)
        {
          
          r.mPath.x = r.x - r.offsetViewX;
          r.mPath.y = r.y - r.offsetViewY;
        }
        if(r.mObj)
        {
          r.mObj.x = r.x - r.offsetViewX; // (r.mObj.x - newRect.x)  // minX;
          r.mObj.y = r.y - r.offsetViewY; // (r.mObj.y - newRect.y)  // minY;
        }

      }

      /*
      if(r.type === "Group")
      {
        r.rotation = 0;
      }
      */
      newRect.children.push(r);      
    });
    
    if(newRect.children.length > 0)
    {
        // Sort the children array by zIndex (ascending order)
        newRect.children.sort((a, b) => a.zIndex - b.zIndex);

        // 5️⃣ Add new group into scene
        rectsRef.current.push(newRect);
        labelZ();

        // 6️⃣ Optional: update selection to new group
        selectedRef.current = [newRect];
        pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
    }
      
  }

    
  function alignTop() {
    const rects = selectedRef.current;
    if (rects.length === 0) return;
    
    let topY = Math.min(...rects.map(r => r.y));

    const {width, height, snapToGrid} = plineRef.current;                  

    if(snapToGrid)
    {
      topY = snap(topY,height);
    }

    rects.forEach(rect => {
        rect.y = topY;
        
        if(rect.subType === 'Animate')
        {          

              
          if(rect.mPath != undefined &&
            rect.mPath != null)
            {
              rect.mPath.y = rect.y - rect.offsetViewY;
        
            }
          if(rect.mObj != undefined &&
              rect.mObj != null)
           {
             rect.mObj.y = rect.y - rect.offsetViewY;
           }
        }  
    });
  }

  function alignBottom() {
    const rects = selectedRef.current;
    if (rects.length === 0) return;
  
    let bottomY = Math.max(...rects.map(r => r.y + r.h));

    const {width, height, snapToGrid} = plineRef.current;                  

    if(snapToGrid)
    {
      bottomY = snap(bottomY,height);
    }

    rects.forEach(rect => {
      rect.y = bottomY - rect.h;

      if(rect.subType === 'Animate')
      {         
        if(rect.mPath != undefined &&
          rect.mPath != null)
          {
            rect.mPath.y = rect.y - rect.offsetViewY;

          }
        if(rect.mObj != undefined &&
            rect.mObj != null)
         {
           rect.mObj.y = rect.y - rect.offsetViewY;
         }

      }
    });
  }

  function alignLeft() {
    const rects = selectedRef.current;
    if (rects.length === 0) return;
  
    let leftX = Math.min(...rects.map(r => r.x));

    const {width, height, snapToGrid} = plineRef.current;                  

    if(snapToGrid)
    {
      leftX = snap(leftX,height);
    }

    rects.forEach(rect => {
      rect.x = leftX;

      if(rect.subType === 'Animate')
      {          
        if(rect.mPath != undefined &&
          rect.mPath != null)
          {
            rect.mPath.x = rect.x - rect.offsetViewX;

          }

        if(rect.mObj != undefined &&
            rect.mObj != null)
         {
           rect.mObj.x = rect.x - rect.offsetViewX;
         }

      }

    });
  }

  function alignRight() {
    const rects = selectedRef.current;
    if (rects.length === 0) return;
  
    let rightX = Math.max(...rects.map(r => r.x + r.w));

    const {width, height, snapToGrid} = plineRef.current;                  

    if(snapToGrid)
    {
      rightX = snap(rightX,height);
    }

    
    rects.forEach(rect => {
      rect.x = rightX - rect.w;

      if(rect.subType === 'Animate')
      {

        
        if(rect.mPath != undefined &&
          rect.mPath != null)
          {
            rect.mPath.x = rect.x - rect.offsetViewX;
          }

        if(rect.mObj != undefined &&
            rect.mObj != null)
         {
           rect.mObj.x = rect.x - rect.offsetViewX;
         }

      }
    });
  }
  
  
  
  function isColliding(r1, r2) {
    return !(
      r1.x + r1.w <= r2.x ||
      r1.x >= r2.x + r2.w ||
      r1.y + r1.h <= r2.y ||
      r1.y >= r2.y + r2.h
    );
  }
 
  function expandRight() {
    const rect = selectedRef.current[0];
    if (!rect) return;
  
    const others = rectsRef.current.filter(r => r !== rect);
    const pageWidth = pageSizeRef.current.width;

    const collidingOthers = others.filter(other => isColliding(rect, other));
    if (collidingOthers.length > 0) {
      console.log('Colliding with:', collidingOthers);
    }


    let count = 0;
    while (rect.x + rect.w < pageWidth) {
      rect.w += 1;

      if(others.some(other =>
        isColliding(rect, other) &&
        !collidingOthers.includes(other))
      )
      {
        rect.w -= 1;
        break;
      }
      if(count > 1000000)
      {
        console.log("ERROR: expandRight");
        break;
      }

    }
  }
  
    
  function expandLeft() {
    const rect = selectedRef.current[0];
    if (!rect) return;
  
    const others = rectsRef.current.filter(r => r !== rect);

    const collidingOthers = others.filter(other => isColliding(rect, other));
        if (collidingOthers.length > 0) {
          console.log('Colliding with:', collidingOthers);
        }

    let count = 0;
    while (rect.x > 0) {
      rect.x -= 1;
      rect.w += 1;

      if(others.some(other =>
        isColliding(rect, other) &&
        !collidingOthers.includes(other))
      )
      {
        rect.x += 1;
        rect.w -= 1;
        break;
      }

      if(count > 1000000)
      {
        console.log("ERROR: expandLeft");
        break;
      }

    }
  }
  
  function expandUp() {
    const rect = selectedRef.current[0];
    if (!rect) return;
  
    const others = rectsRef.current.filter(r => r !== rect);

    const collidingOthers = others.filter(other => isColliding(rect, other));
    if (collidingOthers.length > 0) {
      console.log('Colliding with:', collidingOthers);
    }


    let count = 0;
    while (rect.y > 0) {
      rect.y -= 1;
      rect.h += 1;

      if(others.some(other =>
        isColliding(rect, other) &&
        !collidingOthers.includes(other))
      )
      {
        
        rect.y += 1;
        rect.h -= 1;
        break;
      }
      if(count > 1000000)
      {
        console.log("ERROR: expandUp");
        break;
      }

    }
  }
  
  function expandDown() {
    const rect = selectedRef.current[0];
    if (!rect) return;
    let count = 0;
  
    const others = rectsRef.current.filter(r => r !== rect);
    const pageHeight = pageSizeRef.current.height;
  
    const collidingOthers = others.filter(other => isColliding(rect, other));
    if (collidingOthers.length > 0) {
      console.log('Colliding with:', collidingOthers);
    }

    while (rect.y + rect.h < pageHeight) {
      rect.h += 1;

      if(others.some(other =>
          isColliding(rect, other) &&
          !collidingOthers.includes(other))
        )
      {
        rect.h -= 1;
        break;
      }

      if(count > 1000000)
      {
        console.log("ERROR: expandDown");
        break;
      }
    }
  }


  const createPLineRect = (x,y,w,h,localPath, fillColor, strokeColor, strokeWidth) =>{
    const newRect = { x, y, w, h, id: '', 
                    type:"Prect", imageName:"", description: '', zIndex:-1, stroke:1, 
                    path:localPath, rPath:localPath, rotation:0, bound:null,
                    fillColor:fillColor, strokeColor:strokeColor, strokeWidth:strokeWidth,
                     children:[],
                     clickable: false,
                     useLocal: true,
                     aiSvg: "",
                     localLink: "",
                     externalLink:"",
                     fillColor:"#000000", 
                     strokeColor: "#000000", 
                     strokeWidth: 1,
                     allowStroke: true,
                     allowFill: false,
                     fillMode:"solid",
                     gradientColor1: "#000000",
                     gradientColor2: "#FFFFFF",
                     gradientAngle: 0,                       
                     imagePanX:0,
                     imagePanY:0,
                     imageZoom: 1,
                     iX:0,
                     iY:0,
                     iW:0,
                     iH:0,
                     offsetViewX:0,
                     offsetViewY:0,
                     hasParent:false,
                     locked:false,
                     parentAlpha: 1,
                     fillAlpha: 1,
                     bAlpha: 1,
                     prevEffects: null,
                     shadowColor:"#000000",
                     shadowBlur:0,
                     regularBlur:0,
                     shadowOffsetX:0,
                     shadowOffsetY:0,
                     isDropShadow:false,
                     isBlur:false,
                     isGlow:false,
                     cornerTL:0,
                     cornerTR:0,
                     cornerBR:0,
                     cornerBL:0,
                     tl: {x:0,y:0},
                     tr: {x:0, y:0},
                     bl: {x:0, y:0},
                     br: {x:0, y:0},
                     mPath:undefined,
                     mObj:undefined, 
                     animationSettings:{}
};

    let identifier = rectsRef.current.length;

    let n = "null"
    if(filenameRef.current != '')
    {
      n = `${filenameRef.current}_${pageNum}_`;

    }
    let unique = n + identifier;

    let found = rectsRef.current.find(rect => rect.id === unique);

    while (found && identifier < 10000)
    {
      identifier++;
      unique = n + identifier;
      found = rectsRef.current.find(rect => rect.id === unique);

    }

    newRect.id = unique;

    return newRect;

  }

  const createBezierRect = (x,y,w,h,localPath, localControlPath, fillColor, strokeColor, strokeWidth) =>{
    const newRect = { x, y, w, h, id: '', 
                    type:"Bezier", imageName:"", description: '', zIndex:-1, stroke:1, 
                    path:localPath, control: localControlPath, rPath:localPath, rotation:0, bound:null,
                    fillColor:fillColor, strokeColor:strokeColor, strokeWidth:strokeWidth,
                    children:[],
                    clickable: false,
                    useLocal: true,
                    aiSvg:"",
                    localLink: "",
                    externalLink:"",
                    fillColor:"#000000", 
                    strokeColor: "#000000", 
                    strokeWidth: 1,
                    allowStroke: true,
                    allowFill: false,
                    fillMode:"solid",
                    gradientColor1: "#000000",
                    gradientColor2: "#FFFFFF",
                    gradientAngle: 0,                       
                    imagePanX:0,
                    imagePanY:0,
                    imageZoom: 1,
                    iX:0,
                    iY:0,
                    iW:0,
                    iH:0,
                    offsetViewX:0,
                    offsetViewY:0,
                    hasParent:false,
                    locked:false,
                    parentAlpha:1,
                    fillAlpha:1,
                    bAlpha:1,
                    prevEffects:null,
                    shadowColor:"#000000",
                    shadowBlur:0,
                    regularBlur:0,
                    shadowOffsetX:0,
                    shadowOffsetY:0,
                    isDropShadow:false,
                    isBlur:false,
                    isGlow:false,
                    cornerTL:0,
                    cornerTR:0,
                    cornerBR:0,
                    cornerBL:0,
                    tl: {x:0,y:0},
                    tr: {x:0, y:0},
                    bl: {x:0, y:0},
                    br: {x:0, y:0},
                    mPath:undefined,
                    mObj:undefined, 
                    animationSettings:{}
};

    let identifier = rectsRef.current.length;

    let n = "null"
    if(filenameRef.current != '')
    {
      n = `${filenameRef.current}_${pageNum}_`;

    }
    let unique = n + identifier;

    let found = rectsRef.current.find(rect => rect.id === unique);

    while (found && identifier < 10000)
    {
      identifier++;
      unique = n + identifier;
      found = rectsRef.current.find(rect => rect.id === unique);

    }

    newRect.id = unique;

    return newRect;

  }

  function doFinalizeBezier()
  {
    try
    {

      setOperation('select');
      const {x,y,w,h,localPath, localControlPath} = calculateCurveBoundingBox(pointsRef, controlRef);


      const newRect = createBezierRect(x, y,w,h,localPath, localControlPath, "none", plineRef.current.strokeColor, plineRef.current.stroke);
      historyStack.push(pageNum,rectsRef.current);
      rectsRef.current.push(newRect);
      pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
      
      undoPlineStack.clear();
      pointsRef.current = [];  
      lineBeginRef.current= true;

      undoControlStack.clear();
      controlRef.current = [];
      segmentsRef.current=[];
      labelZ();

      onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      draw();

    }
    catch(e)
    {
      console.log("ERROR: ", e);
    }
  }

  function doFinalizePolyline()
  {
    try
    {
      setOperation('select');
      const {x,y,w,h,localPath} = calculateBoundingBox(pointsRef)

      const newRect = createPLineRect(x, y,w,h,localPath, "none", plineRef.current.strokeColor, plineRef.current.stroke);

      if(operationRef.current === "freehand")
      {
        console.log("FreeHand");
        newRect.subType = "FreeHand";

      }
      historyStack.push(pageNum,rectsRef.current);
      rectsRef.current.push(newRect);
      pagesRef.current.pages[pageNum - 1].layout = rectsRef.current;
      
      undoPlineStack.clear();
      pointsRef.current = [];  
      lineBeginRef.current= true;
      segmentsRef.current=[];
      labelZ();
      onRectSelect([],null, false, segmentsRef.current, aSegmentRef.current, pointsRef.current);
      draw();
    }
    catch(err)
    {
      console.log("ERROR", err);
    }
  }
  

  /*
  function drawCurvePath(ctx, rect) {
    let path = rect.path;
    let controlPath = rect.control;  // Assuming rect contains control points as well
    
    // let {rPath, rControls} = rotateCurvePath(rect.x,rect.y,rect.w,rect.h,rect.rotation, path, controlPath);
    
    // let svgPath = rPathToCurveSvg(path, controlPath);
    let svgPath = rPathToCurveSvgMap(path, controlPath);
    rect.rPath = svgPath;    
    let bound = svgPathBoundingBoxMap(svgPath)
    rect.bound = bound;
  
    ctx.save();
    applyEffects(ctx,rect);

    let {w,h} = rect;
    let sw=w;
    let sh=h;

    
    if(rect.isDropShadow)
    {
      sw = w - Math.abs(rect.shadowOffsetX) - rect.shadowBlur;
      sh = h - Math.abs(rect.shadowOffsetY) - rect.shadowBlur;
    }
    if(rect.isBlur)
    {
      sw = sw - rect.regularBlur;
      sh = sh - rect.regularBlur;
    }

    let tX = (w-sw) / 2;
    let tY = (h-sh) / 2;


    let rZoomX = sw/(w+tX);
    let rZoomY = sh/(h+tY);

    let np = resize(path,rZoomX, rZoomY);
    let rc = resizeC(controlPath,rZoomX, rZoomY);
    
    translateBezier(np, rc, tX, tY);

    let {rPath, rControls} = rotateCurvePath(rect.x,rect.y,rect.w,rect.h,rect.rotation, np, rc);
    // let svgPath = rPathToCurveSvg(path, controlPath);
    svgPath = rPathToCurveSvgMap(np, rc);
    rect.rPath = svgPath;    
        
    // Iterate over each line in the path (each line is a cubic bezier segment)
    for (let i = 0; i < rPath.length; i++) {
      const line = rPath[i];
      const controls = rControls[i] ? rControls[i].controls : [];
  
      ctx.save(); 
      // Skip lines with no points
      if (!line.points || line.points.length < 2) 
      {
        ctx.restore();
        continue;
      }

      ctx.beginPath();

      let gAlpha = getOpacity(rect);

      ctx.globalAlpha = line.alpha*gAlpha;

      // Set the stroke style for the line
      ctx.strokeStyle = line.strokeColor || '#000'; // Default to black if no color is provided
      ctx.lineWidth = line.stroke || 1; // Default line width if not provided
        
      // Loop through the points to draw the Bézier curve segments
      for (let p = 0; p < line.points.length - 1; p++) {
        const p0 = line.points[p];
        const p3 = line.points[p + 1];
  
        // Get control points (make sure controls array has enough points)
        const control = controls[p];  // Get control point for the current segment
        const p1 = control ? { x: control.x2, y: control.y2 } : p0;  // Outgoing control for p0
        const p2 = controls[p + 1] ? { x: controls[p + 1].x1, y: controls[p + 1].y1 } : p3;  // Incoming control for p3
  
        // Calculate the points on screen with the offset from rect.x and rect.y
        const { x: xP0, y: yP0 } = pageToScreenN(p0.x + rect.x, p0.y + rect.y, viewPortRef);
        const { x: xP1, y: yP1 } = pageToScreenN(p1.x + rect.x, p1.y + rect.y, viewPortRef);
        const { x: xP2, y: yP2 } = pageToScreenN(p2.x + rect.x, p2.y + rect.y, viewPortRef);
        const { x: xP3, y: yP3 } = pageToScreenN(p3.x + rect.x, p3.y + rect.y, viewPortRef);
  
        // Start the path at the first point
        if (p === 0 ) {
          ctx.moveTo(xP0, yP0);  // Ensure that we start the path at the first point of the first line
        }
  
        // Draw cubic bezier curve
        ctx.bezierCurveTo(xP1, yP1, xP2, yP2, xP3, yP3);
  
      }


      if(line.allowStroke)
      {
        ctx.strokeStyle = line.strokeColor;
        ctx.lineWidth = line.strokeWidth;
        ctx.stroke(); // Don't forget to stroke each path
      }

      if (line.allowFill) {


        if (line.fillMode === "gradient") {

          const angle = (line.gradientAngle || 0) * Math.PI / 180;
          
          const dx = Math.cos(angle);
          const dy = Math.sin(angle);
        
          // ✅ Use actual bounds
          const bx = rect.x;
          const by = rect.y;

          const bounds = getLineBounds(line.points);
          const bw = bounds.width;
          const bh = bounds.height;
        
          const cx = bx + bounds.x+bw / 2;
          const cy = by + bounds.y+bh / 2;
        
          const halfW = bw / 2;
          const halfH = bh / 2;
        
          const x0 = cx - dx * halfW;
          const y0 = cy - dy * halfH;
          const x1 = cx + dx * halfW;
          const y1 = cy + dy * halfH;
        
          // ⚠️ Convert to screen coords (VERY important)
          const p0 = pageToScreenN(x0, y0, viewPortRef);
          const p1 = pageToScreenN(x1, y1, viewPortRef);
        
          const grad = ctx.createLinearGradient(p0.x, p0.y, p1.x, p1.y);
        
          grad.addColorStop(0, line.gradientColor1 || "#ffffff");
          grad.addColorStop(1, line.gradientColor2 || "#000000");
        
          ctx.fillStyle = grad;
        
         } else {
          ctx.fillStyle = line.fillColor;
        }
      
        ctx.fill();
      }
    
    
      ctx.restore();

      // ctx.stroke(); // Finalize the stroke for the entire path
      // ctx.restore(); // Restore context state after each line
    }

    ctx.restore();
    
  }
    
  */
  /*
  function drawPath(ctx, rect)
  {    
      // console.log("lpath: ", JSON.stringify(lPath));
      let lPath = rect.path;

       let rPath = rotatePath(rect.x,rect.y,rect.w,rect.h,rect.rotation,lPath);
      // let svgPath = rPathToSvg(lPath);
      let svgPath = rPathToSvgMap(lPath);
      rect.rPath = svgPath;
      let bound = getBoundingBoxFromPoints(rPath);
      rect.bound = bound;
      // rect.bound.x = rect.x + bound.x;
      // rect.bound.y = rect.y + bound.y;

      ctx.save();
      applyEffects(ctx,rect);
  
      let {w,h} = rect;
      let sw=w;
      let sh=h;
  
      
      if(rect.isDropShadow)
      {
        sw = w - Math.abs(rect.shadowOffsetX) - rect.shadowBlur;
        sh = h - Math.abs(rect.shadowOffsetY) - rect.shadowBlur;
      }
      if(rect.isBlur)
      {
        sw = sw - rect.regularBlur;
        sh = sh - rect.regularBlur;
      }
  
      let tX = (w-sw) / 2;
      let tY = (h-sh) / 2;
  
  
      let rZoomX = sw/(w+tX);
      let rZoomY = sh/(h+tY);
  
      let np = resize(lPath,rZoomX, rZoomY);
            
      translatePRect(np, tX, tY);
  
      rPath=rotatePath(rect.x,rect.y,rect.w,rect.h,rect.rotation, np);
      // let svgPath = rPathToCurveSvg(path, controlPath);
      svgPath = rPathToSvgMap(np);
      rect.rPath = svgPath;    
        

      for (const line of rPath) 
      {
        ctx.save(); 
        ctx.beginPath();
        let gAlpha = getOpacity(rect);
        ctx.globalAlpha = line.alpha * gAlpha;

        for (let p = 0; p<line.points.length; p++)
        {            
            let xP = line.points[p].x + rect.x;
            let yP = line.points[p].y + rect.y;

            // console.log("XP: ",xP);
            // console.log("YP: ", yP);
            const { x:xS, y:yS } = pageToScreenN(xP, yP,viewPortRef);
                                
            if(p == 0)
            {                   
              ctx.moveTo(xS, yS);  
            }
            else
            {
              ctx.lineTo(xS,yS);
            }            
                     
        }  

        if(line.allowStroke)
        {
          ctx.strokeStyle = line.strokeColor;
          ctx.lineWidth = line.strokeWidth;
          ctx.stroke(); // Don't forget to stroke each path
        }

        if (line.allowFill) {

          if (line.fillMode === "gradient") {

            const angle = (line.gradientAngle || 0) * Math.PI / 180;
          
            const dx = Math.cos(angle);
            const dy = Math.sin(angle);
          
            // ✅ Use actual bounds
            const bx = rect.x;
            const by = rect.y;

            const bounds = getLineBounds(line.points);
            const bw = bounds.width;
            const bh = bounds.height;
          
            const cx = bx + bounds.x+bw / 2;
            const cy = by + bounds.y+bh / 2;
          
            const halfW = bw / 2;
            const halfH = bh / 2;
          
            const x0 = cx - dx * halfW;
            const y0 = cy - dy * halfH;
            const x1 = cx + dx * halfW;
            const y1 = cy + dy * halfH;
          
            // ⚠️ Convert to screen coords (VERY important)
            const p0 = pageToScreenN(x0, y0, viewPortRef);
            const p1 = pageToScreenN(x1, y1, viewPortRef);
          
            const grad = ctx.createLinearGradient(p0.x, p0.y, p1.x, p1.y);
          
            grad.addColorStop(0, line.gradientColor1 || "#ffffff");
            grad.addColorStop(1, line.gradientColor2 || "#000000");
          
            ctx.fillStyle = grad;
          }
          else {
              ctx.fillStyle = line.fillColor;
          }
        
          ctx.fill();
        }
      

        ctx.restore(); // Restore context state after each line
      }

    ctx.restore();      
  }
  */

  function calculateBoundingBox(pointsRef) {
    const lines = pointsRef?.current;
  
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
  
    // First pass: calculate bounds
    for (const line of lines) {  
      for (const point of line.points) {
        if (!point || typeof point.x !== "number" || typeof point.y !== "number") {
          console.warn("Invalid point object:", point);
          continue;
        }
  
        minX = Math.min(minX, point.x);
        minY = Math.min(minY, point.y);
        maxX = Math.max(maxX, point.x);
        maxY = Math.max(maxY, point.y);

      }
    }


    let shift = 10;

    let width = (maxX+2*shift) - minX;
    let height = (maxY+2*shift) - minY;
  

    if(width < 10)
    {
      width = 10;
    }

    if(height < 10)
    {
      height = 10;
    }
    
    /*
    const localPath = lines.map(line => 
      Array.isArray(line.points)
        ? {
            ...line,  // Keep all the metadata (stroke, color, etc.)
            points: line.points
              .filter(p => p && typeof p.x === 'number' && typeof p.y === 'number')
              .map(p => ({ x: p.x - (minX-shift), y: p.y - (minY-shift) }))
          }
        : line  // Keep the line as is if it doesn't have points
    );
    */

    const localPath = lines.map(line => 
      Array.isArray(line.points)
        ? (() => {
            const cleanedPoints = line.points
              .filter(p => p && typeof p.x === 'number' && typeof p.y === 'number')
              .map(p => ({
                x: p.x - (minX - shift),
                y: p.y - (minY - shift)
              }));

            const isClosed =
              cleanedPoints.length > 1 &&
              cleanedPoints[0].x === cleanedPoints[cleanedPoints.length - 1].x &&
              cleanedPoints[0].y === cleanedPoints[cleanedPoints.length - 1].y;

            const finalPoints =
              line.allowFill === true && cleanedPoints.length > 0 && !isClosed
                ? [...cleanedPoints, { ...cleanedPoints[0] }]
                : cleanedPoints;
    
    
            return {
              ...line,
              points: finalPoints
            };
          })()
        : line
    );

  
    return { x: minX, y: minY, w: width, h: height, localPath };
  }
        
  return (
    <canvas
      ref={canvasRef}
      width={1024}
      height={768}
      style={{ border: '1px solid #000', background: '#fff' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    />
  );
});

export default Canvas;

